// ============================================
// aiToolConfig — 비-프롬프트 구조 데이터 (DB 미이관, 프론트 전용)
// TOOL_DEFINITIONS(OpenAI 도구 스키마) / DIMENSION_SCHEMA / INTENTION_DIMENSIONS / getIntentionDimensions
// aiPrompts.js에서 분리 (프롬프트 텍스트 DB 이관 + 파일 폐기 준비, increment 1)
// ============================================

import { resolvePrompt } from './promptStore.js';

export const TOOL_DEFINITIONS = [
  // ── 1. 데이터 조회 ──

  {
    type: "function",
    function: {
      name: "get_intentions",
      description: "Retrieve available intentions (APIs) for a category. MUST be called before call_api to check intention list.",
      parameters: {
        type: "object",
        properties: {
          category: {
            type: "string",
            enum: ["MEMBER", "MEMBER_DETAIL", "MEMBERSHIP", "SCHEDULE", "SALES", "PRODUCT", "COMMUNICATION", "INFRA", "WORKOUT"],
          }
        },
        required: ["category"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "call_api",
      description: "Call backend API with intention key from get_intentions. Result auto-stored in buffer for downstream filter/sort/compute/derive. Calculate dates in YYYY-MM-DD. Include memberId in params for member-detail intentions.",
      parameters: {
        type: "object",
        properties: {
          intention: { type: "string", description: "Exact intention key from get_intentions. Copy exactly — any abbreviation/modification/omission will fail." },
          startDate: { type: "string", description: "Start date (YYYY-MM-DD). Calculate from today." },
          endDate: { type: "string", description: "End date (YYYY-MM-DD). Calculate from today." },
          params: { type: "object", description: "Parameters required by the intention. CHECK_MEMBER_SEARCH MUST include {name} or {phone}. DETAIL intentions MUST include {memberId} from prior SEARCH result.", additionalProperties: true },
          slot: { type: "string", description: "Buffer slot name. For comparing multiple periods/conditions, use different names. Default: 'default' (overwrites existing data)." }
        },
        required: ["intention"]
      }
    }
  },

  // ── 2. 데이터 가공 ──

  {
    type: "function",
    function: {
      name: "filter",
      description: "Filter buffer data — keep matching items, remove rest. MUST use for subset queries (N 이상/이하/초과/미만, ~만, ~인 회원). sort only reorders; do NOT use for filtering. Pass actual availableFields key and structured condition object. Same-field re-filter replaces existing condition; different-field filters accumulate.",
      parameters: {
        type: "object",
        properties: {
          field: { type: "string", description: "Actual field name (availableFields key or derived field). Omit when reset:true." },
          compute: { type: "string", enum: ["age", "birthday", "dday"], description: "Derived computation. age: birthDay→age, birthday: birthDay→month, dday: endDate→remaining days." },
          reset: { type: "boolean", description: "true: reset all filters, restore original data." },
          condition: {
            type: "object",
            description: "Filter condition",
            properties: {
              type: { type: "string", enum: ["exact", "range", "date_range", "birthday_month", "numeric_gte", "numeric_lte"] },
              value: { description: "Filter value. Number for threshold, string for field reference (e.g. \"_halfCapacity\"). Array for exact multi-match." },
              min: { type: "number", description: "range min" },
              max: { type: "number", description: "range max" },
              month: { type: "integer", description: "birthday_month: 1-12" },
              startDate: { type: "string", description: "date_range start (YYYY-MM-DD)" },
              endDate: { type: "string", description: "date_range end (YYYY-MM-DD)" },
            },
            required: ["type"]
          }
        },
      }
    }
  },
  {
    type: "function",
    function: {
      name: "sort",
      description: "Sort buffer data (no removal). Use for ordering: ~순, 높은/낮은 순, 최근순. For threshold conditions, call filter first. With compute, sort by derived value (age, dday).",
      parameters: {
        type: "object",
        properties: {
          field: { type: "string", description: "Sort field (availableFields key)" },
          compute: { type: "string", enum: ["age", "dday"], description: "Derived sort. age: birthDay→age, dday: endDate→remaining days." },
          direction: { type: "string", enum: ["asc", "desc"] }
        },
        required: ["field"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "compute",
      description: "Aggregate buffer data. count: total (field optional). sum/average/min/max: numeric field. group: value distribution.",
      parameters: {
        type: "object",
        properties: {
          operation: { type: "string", enum: ["count", "sum", "average", "min", "max", "group"] },
          field: { type: "string", description: "Aggregation field (omit for count)" },
          compute: { type: "string", enum: ["age"], description: "Derived aggregation. group + compute:age = age distribution." }
        },
        required: ["operation"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "aggregate",
      description: "Group multi-row data by field. 1 row per group + auto _count (frequency), _lastDate, _firstDate fields. For frequency-based conditions, aggregate first then filter on _count.",
      parameters: {
        type: "object",
        properties: {
          field: { type: "string", description: "Grouping field. e.g. name (per-member), lessonName (per-class), coachName (per-coach)." }
        },
        required: ["field"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "derive",
      description: "Compute new derived field per item from existing numeric fields. Available for downstream filter/sort/customize_display. Both field and value MUST exist in availableFields. If required field missing, do NOT derive — inform user. NEVER substitute arbitrary value (1, 0).",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "User-facing display name (e.g. 실결제액, 노쇼횟수). NO _ prefix or camelCase." },
          field: { type: "string", description: "Source field (first operand)" },
          operation: { type: "string", enum: ["divide", "multiply", "add", "subtract"] },
          value: { description: "Second operand. Number (constant) or field name (string). Field name → per-item value." }
        },
        required: ["name", "field", "operation", "value"]
      }
    }
  },

  // ── 3. 출력/액션 ──

  {
    type: "function",
    function: {
      name: "show_display",
      description: "Render buffer data as a table. Call last, after all processing (filter/sort/derive). Only call when showing a table — for stats/guides/questions, use text response. For 'TOP N/상위 N/하위 N/N명만', pass N to max_count.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "Top table message. Factual summary only — NO suggestions/offers. Numbers MUST use placeholders: {{SUM:field}}, {{AVG:field}}, {{COUNT}}, {{COUNT_NONZERO:field}}, {{MAX:field}}, {{MIN:field}}, {{SUMMARY:key}}. Raw numbers trigger auto-retry. Do NOT fabricate meta fields absent from data." },
          outro: { type: "string", description: "Bottom table message. Factual summary only — NO suggestions/offers. Same placeholder rules as intro." },
          hint: { type: "string", description: "Follow-up suggestions (system discards — never shown to user). Put all offers/next-step ideas here, not in intro/outro." },
          columns: { type: "array", items: { type: "string" }, description: "Columns to show. Omission recommended — code auto-selects based on question context. Specify only when user explicitly requests certain columns." },
          columnLabels: { type: "object", description: "Per-column display labels. MUST provide for merged/comparison data. Short, simple Korean (no jargon). e.g. {\"이전_attendedCount\":\"이전 출석\",\"_change_attendedCount\":\"출석 차이\",\"_changeRate_attendedCount\":\"출석 변동(%)\"}." },
          max_count: { type: "number", description: "Max items to show. Pass when user specifies count (TOP 10/상위 5/탑텐). Default: 150." }
        }
      }
    }
  },
  {
    type: "function",
    function: {
      name: "recommend_sms",
      description: `Generate SMS templates from loaded member data. Load target members via call_api first. Code generates text via FAST_MODEL — LLM must NOT write SMS text directly.
mode:
- bulk (default): Same text to all targets. Use this for most cases ("문자 보내줘/추천해줘", "다시 써줘", "톤 바꿔줘", "좀 더 친근하게").
- individual: Per-member customized text. Use when (a) user explicitly requests individual sending (따로/개별/각각/한명씩) OR (b) user requests regeneration scoped to specific row(s) by index (Korean: "N번 다시 써줘/N번만 다시/N번 회원 다시/N번째 다시/N번 톤 바꿔/위에서 N번 다시").
targetIndex:
- 1-based row index in the previously displayed table. Pass ONLY when user references specific row(s) by number (e.g. "1번 다시 써줘" → targetIndex=1, "3번 톤 바꿔줘" → targetIndex=3). When targetIndex is provided, mode MUST be individual — recipients are narrowed to just that row, NOT the full list.
- Do NOT use targetIndex for "다시 써줘"/"톤 바꿔줘" without a number — that is full-set rewrite (bulk).
targetNames:
- Array of member names when the user targets SPECIFIC named members rather than the whole list (Korean: names listed like "김서현, 이옥주에게 문자", "이 사람들한테만") OR narrows a count that references previously named targets (Korean: "2명한테만" after naming 2 members — pass those same names). Recipients AND the displayed table are narrowed to exactly these members; mode stays bulk (same text) unless per-member text is requested.
- Use targetNames when the user gives NAMES; use targetIndex only for numeric row references ("N번"). NEVER fall back to the full member list when the user named a subset — that risks sending SMS to unintended people.
- REUSE CURRENT BUFFER: when a member list is already loaded (e.g. after "명단 보여줘"), call recommend_sms with targetNames DIRECTLY on the current buffer. Do NOT call MEMBER_SEARCH or any *_DETAIL intention to re-fetch the named members for SMS — DETAIL / search-detail buffers drop phone numbers, so recommend_sms then fails with "no phone numbers". The current member-list buffer already holds the named members with their phones.
IMPORTANT: When mode/targetIndex/targetNames are omitted or unclear, ALWAYS use bulk with no narrowing. bulk is default regardless of target count.`,
      parameters: {
        type: "object",
        properties: {
          mode: {
            type: "string",
            enum: ["bulk", "individual"],
          },
          tone: {
            type: "string",
            description: "User-requested tone. e.g. '존댓말', '캐주얼', '격식체'",
          },
          targetDescription: {
            type: "string",
            description: "SMS target member description (Korean). e.g. '홀딩', '만료 예정', '이번 달 생일', '장기 미출석'",
          },
          targetIndex: {
            type: "number",
            description: "1-based row index of the SPECIFIC row in the previously displayed table to regenerate SMS for. Pass ONLY for row-scoped regen (Korean: 'N번 다시 써줘/N번만 다시/N번 톤 바꿔'). Omit for full-set rewrite/tone change. When set, mode MUST be 'individual'.",
          },
          targetNames: {
            type: "array",
            items: { type: "string" },
            description: "Member names to narrow SMS recipients to, when the user targets specific named members (Korean: '김서현, 이옥주에게', or '2명한테만' referencing previously named members). Recipients and the displayed table are narrowed to these members. Omit for full-set send. Use this (NOT targetIndex) when the user gives names rather than row numbers.",
          },
          contentGuide: {
            type: "string",
            description: "SMS BODY content/purpose the user EXPLICITLY dictated — WHAT the message should say (Korean themes, e.g. '회원권 만료 안내', '재등록 방문 요청', '응원 메시지'). DISTINCT axis from targetDescription (WHO receives) and tone (style).\nPOPULATE ONLY when the user's words contain an explicit content/purpose directive — e.g. '~담아서/담아', '~안내하고', '~유도하는', '~응원하는', '내용은 ~', '~하는 내용으로'. Example: '재등록 유도하는 문자' → '재등록 유도'; '만료 안내랑 응원 담아서' → '만료 안내, 응원'.\nOMIT ENTIRELY for a BARE SMS request with no stated content (Korean: '문자 보내줘/추천해줘/써줘', '이 회원들한테 문자'). Do NOT infer or auto-fill contentGuide from the target segment (do NOT set '만료 안내' just because the target is '만료 예정 회원') — leave it unset so the default body generator handles phrasing.\nBODY THEMES ONLY — never put targeting/selection meta (제외/빼고/마지막 N명/상위 N명) here; that belongs to targetNames/targetIndex.",
          }
        }
      }
    }
  },
  {
    type: "function",
    function: {
      name: "customize_display",
      description: `Adjust table display columns. Pass availableFields key or virtual field name (age, remainingDays, derived fields).
- "이름만 보여줘" → only:["name"]
- "나이 추가" → include:["age"]
- "날짜 빼줘" → exclude:["viewEndDate","viewStartDate"]
- Pass column names only, NOT filter values (60대/남성/만료).`,
      parameters: {
        type: "object",
        properties: {
          only: { type: "array", items: { type: "string" }, description: "'~만 보여줘' — show only these, remove rest" },
          include: { type: "array", items: { type: "string" }, description: "'추가/넣어' — add to existing columns" },
          exclude: { type: "array", items: { type: "string" }, description: "'빼/제거' — remove from existing columns" }
        }
      }
    }
  },

  // ── 4. 매출 캘린더 ──

  {
    type: "function",
    function: {
      name: "show_sales_view",
      description: "SALES category data ONLY. Renders weekly sales calendar. Do NOT use for MEMBER, SCHEDULE, MEMBERSHIP, or other categories — use show_display instead. Load SALES data via call_api first. Use paymentFilter param for payment method filtering — do NOT use the filter tool.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "Top message" },
          startDate: { type: "string", description: "Start date (YYYY-MM-DD)" },
          endDate: { type: "string", description: "End date (YYYY-MM-DD)" },
          paymentFilter: { type: "string", enum: ["all", "card", "cash", "point", "linkoPay", "mixed"], description: "Payment method filter." },
          showSettlement: { type: "boolean", description: "Whether to show LinkoPay settlement amount on calendar." },
        },
        required: ["startDate", "endDate"]
      }
    }
  },

  // ── 5. 매출 차트 ──

  {
    type: "function",
    function: {
      name: "show_revenue_chart",
      description: "SALES category data ONLY. Renders monthly revenue bar+line chart (current year vs previous year). Do NOT use for MEMBER, SCHEDULE, MEMBERSHIP, or other categories. Load 2 years of SALES data via call_api first. '월별 매출 순위/매출 랭킹/많은 순'(금액 정렬)은 show_period_sales 사용 — 본 도구는 '추이/변화/작년 대비'.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "Top message" },
          year: { type: "number", description: "Base year (YYYY). Default: current year." }
        },
        required: []
      }
    }
  },

  // ── 5-1. 유효회원 증가 추이 차트 ──

  {
    type: "function",
    function: {
      name: "show_member_trend",
      // [VIZ-SOURCE-COMPLETENESS] buffer source intention 메타 — 누락 시 executeTool dispatch가 자동 보강.
      requiredSources: ["CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST"],
      description: "MEMBER category. 월별 유효회원 수 증가 추이를 막대+점선 차트로 시각화 (무료체험 포함=막대 / 무료체험 제외=점선, 결제액 기준). call_api 선행 필수 — call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) → buffer.items에 회원별 usages 적재. 본 도구는 buffer.items 기반으로 월별 유효회원(usage 기간이 월말 포함)을 산정하고 유료 usage 여부로 2계열 분리. '유효회원 증가 추이', '유효회원 추이', '월별 유효회원', '활성회원 추이', '회원 수 추이' 등의 요청에 사용. show_member_status(신규/재등록/이탈 분류 목록)와 구분 — '추이/증가/월별 유효회원 수'는 본 도구.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          startDate: { type: "string", description: "추이 시작일 (YYYY-MM-DD). 생략 시 buffer 기간 또는 최근 12개월." },
          endDate: { type: "string", description: "추이 종료일 (YYYY-MM-DD). startDate와 함께 사용." }
        },
        required: []
      }
    }
  },

  // ── 5-1b. 회원 증감 추이 차트 (신규/재등록/이탈) ──

  {
    type: "function",
    function: {
      name: "show_member_flow_trend",
      // [VIZ-SOURCE-COMPLETENESS] buffer source intention 메타 — 누락 시 executeTool dispatch가 자동 보강.
      requiredSources: ["CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST"],
      description: "MEMBER category. 월별 신규/재등록/이탈 증감 추이를 발산 막대 차트로 시각화 (위=신규+재등록 막대 / 아래=이탈 막대). call_api 선행 필수 — call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) → buffer.items에 회원별 usages 적재. 본 도구는 usage 순서로 첫 이용권=신규 / 이후 이용권=재등록 / 마지막 이용권 종료(만료)=이탈을 월별 집계. '신규 재등록 이탈 추이', '회원 증감 추이', '월별 신규/이탈', '신규 이탈 변화', '회원 유입 이탈 추이' 등의 요청에 사용. show_member_trend(유효회원 수 스톡 추이)와 구분 — '신규/재등록/이탈 증감'은 본 도구, '유효회원 수 추이'는 show_member_trend. show_member_status(특정 유형 회원 목록)와도 구분 — '월별 추이/증감'은 본 도구.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          startDate: { type: "string", description: "추이 시작일 (YYYY-MM-DD). 생략 시 buffer 기간 또는 최근 12개월." },
          endDate: { type: "string", description: "추이 종료일 (YYYY-MM-DD). startDate와 함께 사용." }
        },
        required: []
      }
    }
  },

  // ── 5-2. LTV (고객 생애 가치) ──

  {
    type: "function",
    function: {
      name: "show_ltv",
      // [VIZ-SOURCE-COMPLETENESS] 본 viz 도구가 buffer에서 읽는 source intention 메타 (도메인 무관 선언).
      //   executeTool dispatch 진입 시 누락 source를 코드가 call_api로 자동 보강(auto-prefetch). LTV 키워드 하드코딩 X — 메타 기반 범용.
      requiredSources: ["CHECK_SALES_LIST_BY+YEAR", "CHECK_MEMBERSHIP+DURATION_LIST_BY+PERIOD", "CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST"],
      description: "고객 생애 가치(LTV)를 시각화. 월 평균 매출, 유효 회원 수, 평균 지속 기간을 기반으로 LTV를 계산하고, 실시간 조절 슬라이더와 함께 표시. call_api 선행 필수 — call_api(CHECK_SALES_LIST_BY+YEAR, year) + call_api(CHECK_MEMBERSHIP+DURATION_LIST_BY+PERIOD, period) + call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) (유효 회원 산정용) → slot 분리 + merge_slots 권장. 본 도구는 buffer.items 기반으로 priceAvg(YTD 합계/누적월수) + durationAvg(duration<1은 1로 보정) + 유효회원수 계산. 'LTV', '고객 생애 가치', '회원 1명이 남기는 총 매출', 'lifetime value', '고객 가치' 등의 요청에 사용.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          month: { type: "string", description: "산정 기준 월 (YYYY-MM). 기본값=이번달. '이번달 LTV', '지난달 LTV', '3월 LTV' 등 월 단위 질문에 사용. 예: '2026-05'." },
          startDate: { type: "string", description: "명시적 산정 시작일 (YYYY-MM-DD). 분기/주간 등 월 단위가 아닌 기간 LTV에 사용. month 파라미터를 무시함." },
          endDate: { type: "string", description: "명시적 산정 종료일 (YYYY-MM-DD). startDate와 함께 사용." }
        },
        required: []
      }
    }
  },

  // ── 6. 출석률 차트 ──

  {
    type: "function",
    function: {
      name: "show_attendance_chart",
      // [VIZ-SOURCE-COMPLETENESS] buffer source intention 메타 — 누락 시 executeTool dispatch가 자동 보강.
      requiredSources: ["CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD", "CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST"],
      description: "출석률/출석 현황을 Area 차트로 시각화. call_api 선행 필수 — daily 모드: call_api(CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD, period) + call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) (유효회원 산정용) / monthly 모드: 추가로 작년 동기간 1번 더 (총 3 endpoint). slot 분리 + merge_slots 권장. 본 도구는 buffer.items 사용하여 차트 데이터 가공만 수행. '출석률 보여줘', '출석 현황 차트', '출석 추이' 등의 요청에 사용.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트. CRITICAL: viewMode='monthly'의 헤더 수치(평균 출석률/평균 출석 인원)는 연간(1~12월) 평균값임. 사용자가 '지난달/이번달/3월' 등 특정 기간을 물었는데 monthly로 응답하면 그 헤더 값을 마치 해당 기간 값처럼 잘못 전달하게 되므로 절대 금지." },
          viewMode: {
            type: "string",
            enum: ["daily", "monthly"],
            description: "차트 뷰 모드. **MUST FOLLOW STRICTLY**: 사용자가 명시적으로 '올해/이번 년도/연간/1년치/12개월/연도별 추이'를 요청한 경우에만 'monthly'. 그 외 모든 경우는 무조건 'daily'. 'daily' 적용 케이스: '이번달', '이달', '지난달', '전달', '저번달', '3월/4월' 등 특정 월, '최근 한달', '지난 30일', '이번주', '지난주', '주별', 그리고 기간 언급이 모호하거나 없을 때. monthly 차트의 헤더 수치는 연간 평균이므로 특정 월/기간 질문에 monthly로 응답하면 안 됨. 생략 시 'daily'."
          },
          isCountOncePerDay: {
            type: "boolean",
            description: "하루에 여러 수업 출석 시 중복 제외 여부. true=1일 1회만 집계(중복 제외), false=모든 출석 건수 집계(중복 포함). 생략 시 true(중복 제외)"
          },
          month: {
            type: "string",
            description: "daily 모드에서 조회할 월 ('YYYY-MM'). '이번달'=현재 월, '지난달'=전월, '3월'=해당 연도 3월. 생략 시 이번달. startDate/endDate를 명시한 경우 무시됨."
          },
          startDate: {
            type: "string",
            description: "daily 모드에서 명시적 기간 지정 시 시작일 ('YYYY-MM-DD'). '최근 7일' / '이번주' 등 월 단위가 아닌 기간에만 사용. daily 모드 최대 360일까지 (초과 시 종료일 기준 최근 360일로 자동 클램프). monthly 모드는 무시됨."
          },
          endDate: {
            type: "string",
            description: "daily 모드에서 명시적 기간 지정 시 종료일 ('YYYY-MM-DD'). startDate와 함께 사용."
          }
        },
        required: []
      }
    }
  },

  // ── 7. 회원 상태 전용 UI ──

  {
    type: "function",
    function: {
      name: "show_member_status",
      // [VIZ-SOURCE-COMPLETENESS] buffer source intention 메타 — 누락 시 executeTool dispatch가 자동 보강.
      requiredSources: ["CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST"],
      description: "신규/재등록/이탈 회원 목록을 전용 UI로 시각화. call_api 선행 필수 — call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) → buffer.items에 전체 회원 + usages 적재. 본 도구는 buffer.items 기반으로 산정기간 overlap 계산 + memberType별 분류 (new: 산정기간 내 첫 이용권 시작 / reregistered: 산정기간 내 비-첫 이용권 시작 / churned: 산정기간 내 마지막 이용권 종료 오늘 이전). 기본 산정기간 이번달.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          memberType: {
            type: "string",
            enum: ["new", "reregistered", "churned"],
            description: "회원 유형. 신규 = 'new', 재등록 = 'reregistered', 이탈 = 'churned' (산정기간 내 마지막 이용권 종료). 단순 '만료회원' 목록 조회는 본 도구가 아니라 CHECK_MEMBER+EXPIRED_LIST 호출 후 show_display로 표시 — show_member_status는 신규/재등록/이탈 분류 전용."
          },
          month: { type: "string", description: "조회 월 (YYYY-MM). 기본값=이번달. '이번달', '지난달', '3월' 등 월 단위 질문에 사용. 예: '2026-05'." },
          startDate: { type: "string", description: "명시적 시작일 (YYYY-MM-DD). '최근 N일', '이번주', '특정 기간' 등 월 단위가 아닌 산정기간에 사용. month 파라미터를 무시함." },
          endDate: { type: "string", description: "명시적 종료일 (YYYY-MM-DD). startDate와 함께 사용." }
        },
        required: ["memberType"]
      }
    }
  },

  // ── 8. 수업별 수강 인원 (시간대별) ──

  {
    type: "function",
    function: {
      name: "show_class_attendance",
      description: "특정 기간의 수업별/시간대별 수강 인원을 리스트로 시각화. call_api 선행 필수 — call_api(CHECK_RESERVE+CLASS+SLOT_LIST_BY+PERIOD, period). 본 도구는 buffer.items 기반으로 reserveCount 내림차순 정렬 + 시간 표시(HH:MM-HH:MM AM/PM) + 강사명/수업명/수강 인원 뱃지 렌더링. '수업별 수강 인원', '시간대별 출석', '인기 수업', '강사별 수강 인원' 등의 요청에 사용. show_attendance_chart(추이 차트) / show_attendance_ranking(회원 랭킹) 와 혼동 금지.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          month: { type: "string", description: "조회 월 (YYYY-MM). 기본값=이번달. '이번달', '지난달', '3월' 등 월 단위 질문에 사용. 예: '2026-05'." },
          startDate: { type: "string", description: "명시적 시작일 (YYYY-MM-DD). '최근 7일', '이번주', '4/1~4/15' 등 월 단위가 아닌 기간에만 사용. month 파라미터를 무시함." },
          endDate: { type: "string", description: "명시적 종료일 (YYYY-MM-DD). startDate와 함께 사용." }
        },
        required: []
      }
    }
  },

  // ── 9. 회원별 출석 랭킹 ──

  {
    type: "function",
    function: {
      name: "show_attendance_ranking",
      // [VIZ-SOURCE-COMPLETENESS] buffer source intention 메타 — 누락 시 executeTool dispatch가 자동 보강.
      requiredSources: ["CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD", "CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST"],
      description: "특정 기간의 회원별 출석 횟수 랭킹을 표 형태로 시각화. call_api 선행 필수 — call_api(CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD, period) + call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) (회원 메타 보강용 — 0회 회원 포함, 회원권 이력) → slot 분리 + merge_slots 권장. 본 도구는 buffer.items 기반으로 회원별 reserveAttendances 집계 + 정렬. 순위/이름/성별/생년월일/전화번호/출석횟수 컬럼 + 정렬 토글 + 행 클릭 시 회원 상세 페이지 이동. '출석 순위', '출석 랭킹', '출석 많은 회원', '제일 잘 나오는 회원' 등의 요청에 사용. show_display(범용 표) / show_attendance_chart(추이 차트) 와 혼동 금지 — 회원(개인) 대상 '순위/랭킹/제일 많이' 키워드는 무조건 show_attendance_ranking. EXCLUDED — 회원권(상품) 축 출석 순위('출석률 높은 회원권 순위' 등 ranking OF membership products)는 본 도구 대상 아님: get_intentions(MEMBERSHIP) → call_api(CHECK_MEMBERSHIP+TOP+ATTENDANCE_STATISTICS_BY+PERIOD) → show_display 사용.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          month: { type: "string", description: "조회 월 (YYYY-MM). 기본값=이번달. '이번달', '지난달', '3월' 등 월 단위 질문에 사용. 예: '2026-05'." },
          startDate: { type: "string", description: "명시적 시작일 (YYYY-MM-DD). '최근 N일', '이번주', '특정 날짜~날짜' 등 월 단위가 아닌 기간에만 사용. month 파라미터를 무시함." },
          endDate: { type: "string", description: "명시적 종료일 (YYYY-MM-DD). startDate와 함께 사용." },
          isCountOncePerDay: { type: "boolean", description: "하루 여러 출석 시 1회만 집계 여부 (기본 true). false 지정 시 모든 출석 건수 집계." }
        },
        required: []
      }
    }
  },

  // ── 10. 연령별 이용 분석 차트 ──

  {
    type: "function",
    function: {
      name: "show_engagement_chart",
      // [VIZ-SOURCE-COMPLETENESS] buffer source intention 메타 — 누락 시 executeTool dispatch가 자동 보강.
      requiredSources: ["CHECK_MEMBER+ENGAGEMENT_LIST_BY+PERIOD", "CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST"],
      description: "연령별 이용자 분석을 프로그레스 바 + 회원권 선호도 테이블 형태로 시각화. call_api 선행 필수 — call_api(CHECK_MEMBER+ENGAGEMENT_LIST_BY+PERIOD, period) + call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) (회원 메타 보강용) → slot 분리 + merge_slots 권장. 본 도구는 buffer.items 기반으로 birthDay 기반 연령대 분류 + membershipId별 집계 + 고유 회원수(uniqueMemberCount) 산정. '연령별 이용자', '연령대별 분석', '어떤 회원권이 잘 팔려', '인기 회원권', '연령별 잘 팔리는 회원권', '어떤 회원권 많이 끊어', 'N0대 회원권 선호', '연령대별 회원권' 등의 요청에 사용. 특정 연령대(20대/30대 등) + 회원권 선호 질문도 본 도구 대상. 단, '회원권 랭킹/선호도/재등록률/매출 기여'는 show_membership_preference 사용. 기간 미지정 시 이번달.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          startDate: { type: "string", description: "조회 시작일 (YYYY-MM-DD). 생략 시 이번달 1일." },
          endDate: { type: "string", description: "조회 종료일 (YYYY-MM-DD). 생략 시 이번달 말일." }
        },
        required: []
      }
    }
  },

  {
    type: "function",
    function: {
      name: "show_membership_preference",
      // [VIZ-SOURCE-COMPLETENESS] buffer source intention 메타 — 누락 시 executeTool dispatch가 자동 보강.
      requiredSources: ["CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST", "CHECK_SALES_LIST_BY+YEAR"],
      description: "회원권 선호도를 회원권별 랭킹(가입 인원) + 연령대 스택 바 + 상단 카드(최다 선택/매출 기여/재등록률) 형태로 시각화. call_api 선행 필수 — call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST) + call_api(CHECK_SALES_LIST_BY+YEAR) (매출 기여 카드용) → slot 분리 권장. 본 도구는 산정기간 내 등록(usage.startDate 기준) 회원을 회원권별로 집계 + 회원권별 연령 분포(세그먼트) + itemName 매칭 매출 + 재등록률 산출. '회원권 선호도', '어떤 회원권이 인기', '회원권 랭킹', '많이 끊는 회원권', '회원권별 가입 인원', '재등록률 높은 회원권', '매출 많은 회원권' 등의 요청에 사용. show_engagement_chart(연령대별 이용/참여도 분석)와 구분 — '선호도/랭킹/재등록률/매출 기여' 키워드는 무조건 show_membership_preference. 기간 미지정 시 이번달.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          startDate: { type: "string", description: "조회 시작일 (YYYY-MM-DD). 생략 시 이번달 1일." },
          endDate: { type: "string", description: "조회 종료일 (YYYY-MM-DD). 생략 시 이번달 말일." }
        },
        required: []
      }
    }
  },

  {
    type: "function",
    function: {
      name: "show_period_sales",
      // [VIZ-SOURCE-COMPLETENESS] buffer source intention 메타 — 누락 시 executeTool dispatch가 자동 보강.
      requiredSources: ["CHECK_SALES_LIST_BY+YEAR"],
      description: "기간 내 월별 매출을 금액순 막대 랭킹으로 시각화 (월별 매출 순위). call_api 선행 필수 — call_api(CHECK_SALES_LIST_BY+YEAR). 본 도구는 approvalDate 기준 월별 매출 집계 후 금액 내림차순 정렬 + 기간 매출 합계 산출. '월별 매출 순위', '매출 순위', '매출 랭킹', '어느 달 매출이 제일 높아', '기간 매출', '월별 매출 많은 순' 등의 요청에 사용. show_revenue_chart(연간 12개월 올해 vs 작년 추이)와 구분 — '순위/랭킹/많은 순'은 show_period_sales, '추이/변화/작년 대비'는 show_revenue_chart. 기간 미지정 시 올해.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          startDate: { type: "string", description: "조회 시작일 (YYYY-MM-DD). 생략 시 올해 1월 1일." },
          endDate: { type: "string", description: "조회 종료일 (YYYY-MM-DD). 생략 시 올해 12월 31일." }
        },
        required: []
      }
    }
  },

  {
    type: "function",
    function: {
      name: "show_point_mange",
      description: "포인트 관리 현황을 요약 카드(누적 발행/누적 사용/현재 보유) + 보유·발행내역·사용내역 탭 테이블로 시각화. call_api 선행 불필요 — 본 도구가 point/statistics를 직접 호출해 기간 내 포인트 집계·회원별 보유 랭킹을 산출. '포인트 관리', '포인트 현황', '포인트 통계', '포인트 발행/사용 내역', '포인트 발행 순위', '포인트 많이 보유한 회원', '포인트 랭킹' 등의 요청에 사용. 회원권 선호도(show_membership_preference)·매출(show_revenue_chart/show_period_sales)과 구분 — '포인트'가 핵심이면 본 도구. 기간 미지정 시 올해.",
      parameters: {
        type: "object",
        properties: {
          intro: { type: "string", description: "상단 안내 텍스트" },
          startDate: { type: "string", description: "조회 시작일 (YYYY-MM-DD). 생략 시 올해 1월 1일." },
          endDate: { type: "string", description: "조회 종료일 (YYYY-MM-DD). 생략 시 오늘." }
        },
        required: []
      }
    }
  },

  // ── 6. 멀티슬롯 ──

  {
    type: "function",
    function: {
      name: "merge_slots",
      description: "Merge buffer slots by matchKey. For period comparison/trend analysis. Result stored in new slot for downstream filter/sort/show_display. Merging 2 slots auto-derives _change_{field} and _changeRate_{field}.",
      parameters: {
        type: "object",
        properties: {
          slots: {
            type: "array",
            items: {
              type: "object",
              properties: {
                slotName: { type: "string", description: "Slot name to merge" },
                label: { type: "string", description: "Field prefix on merge (e.g. '1월', '최근', '이전')" }
              },
              required: ["slotName", "label"]
            },
            description: "Slots to merge (2+, chronological order)"
          },
          matchKey: {
            type: "string",
            description: "Match field. Default: auto-detect (memberId > userId). Specify directly when auto-detect fails (e.g. lessonName)."
          },
          mode: {
            type: "string",
            enum: ["inner", "outer", "left", "intersect", "except"],
            description: "inner: records in both + field merge (default). outer: all records (null when missing). left: first slot baseline. intersect: keep first slot data, filter to items present in second slot (no field add/derive). except: keep first slot rows that are NOT present in remaining slots (set difference / absence — \"X인데 Y 없는\")."
          },
          outputSlot: {
            type: "string",
            description: "Output slot name (default: _merged). Use to preserve previous merge while re-merging (e.g. _merged_2)."
          }
        },
        required: ["slots"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "switch_slot",
      description: "Switch active buffer slot. After switch, filter/sort/show_display operates on this slot. Use before merge_slots to process individual slots (aggregate/filter).",
      parameters: {
        type: "object",
        properties: {
          slot: { type: "string", description: "Slot name to switch to" }
        },
        required: ["slot"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "get_guide",
      description: "Retrieve platform usage/service info/business facts/brand info for ANY question ABOUT the LINKO/링크존 platform itself. Covers: (a) HOWTO (방법/어떻게/하는 법/사용법), (b) PLATFORM FACTS (수수료/요금/가격/연락처/고객센터/문의/전화/상담/주소/URL/링크/접속/사이트/홈페이지/관리자 페이지/약관/정책/지원 브라우저), (c) PRODUCT IDENTITY (링크존/링코/링코페이/관리자 모드/키패드 — \"X가 뭐야/어디야/어떻게 써\"), (d) BRAND RECOMMENDATION (추천 프로그램/회원관리 프로그램). EXCLUDED — this center's OWN data is NOT a guide question: the center's 회원권/이용권 종류·목록·가격 (product catalog — \"우리/저희 이용권 종류\" 등), member/sales/schedule/attendance data, and this center's name/phone/address → use get_intentions + call_api instead (e.g., \"우리 이용권 종류\" → get_intentions(MEMBERSHIP) → CHECK_MEMBERSHIP+CATALOG_LIST). get_guide explains HOW TO USE the platform, never what THIS center sells or its data. Do NOT combine with data queries (get_intentions/call_api). However, follow-up questions about loaded data (기준/조건/범위/날짜) are NOT guide questions — use finish(). NEVER refuse a platform-fact question as \"외부 정보/범위 밖\" — call get_guide first; the cache covers these. Calling without slug returns slug list → pick best slug and call again.",
      parameters: {
        type: "object",
        properties: {
          query: { type: "string", description: "User question or key keyword" },
          slug: { type: "string", description: "Guide page slug. Pick from slug list returned by first call." }
        },
        required: ["query"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "finish",
      description: "Must call to deliver response. response = factual summary ONLY (what was found, counts, metrics). hint = follow-up suggestions (system discards hint — never shown to user). Do NOT repeat intro content. CRITICAL: If buffer has _change_*/_changeRate_* fields, MUST use {{CHANGE:fieldName}} / {{CHANGE_RATE:fieldName}} placeholders for change/rate numbers — raw numbers trigger auto-retry (finish invalidated).",
      strict: true,
      parameters: {
        type: "object",
        properties: {
          response: {
            type: "string",
            description: "Factual data summary for user. Report what was found (counts, key metrics, names). NO follow-up suggestions, NO offers ('if you want'/'원하면'), NO next-step proposals. If buffer has _change_*/_changeRate_*, MUST use placeholder tokens (e.g. '{{CHANGE:totalSalesRevenue}} ({{CHANGE_RATE:totalSalesRevenue}}) 늘었어요'). Do NOT write raw numbers (e.g. '5,080,000원이 늘었어요')."
          },
          hint: {
            type: "string",
            description: "Follow-up suggestions or next-step ideas (e.g. 'filter by gender', 'send SMS', 'compare periods'). System discards this — never shown to user. Put ALL suggestions here instead of response."
          }
        },
        required: ["response", "hint"],
        additionalProperties: false
      }
    }
  },
];


// ============================================
// 6-axis intention dimensions (omo refactor B-frontend, 2026-05-18)
// ============================================
// Backend ai_intention.dimensions JSON column 으로 이전 예정. 본 const = LLM-readable dimension catalog.
// 사용처: RL-8 dimension lookup, B4-1 modifier preservation (할인 회원 재등록률 etc. modifier dropping 차단).

export const DIMENSION_SCHEMA = Object.freeze({
  version: 1,
  createdAt: '2026-05-18',
  description: '6-axis dimension catalog for intentions. Maps each intentionKey to (category, action, subject, modifier[], aggregation, period). Replaces ~50% of categoryRules keyword→intention text rules with structural matching.',
  axes: {
    category: {
      type: 'enum',
      values: ['GENERAL', 'MEMBER', 'MEMBER_DETAIL', 'MEMBERSHIP', 'SCHEDULE',
               'SALES', 'PRODUCT', 'COMMUNICATION', 'INFRA', 'WORKOUT'],
    },
    action: {
      type: 'enum',
      values: ['LIST', 'DETAIL', 'STATS', 'SEARCH', 'STYLE', 'RECOMMEND', 'FALLBACK'],
    },
    subject: {
      type: 'string',
      note: 'category-dependent. 권장 list는 subjectsByCategory 참조. 신규 intention 추가 시 자유 확장 가능',
      subjectsByCategory: {
        GENERAL: ['STYLE', 'RECOMMEND', 'FALLBACK'],
        MEMBER: ['PROFILE', 'ALIVE', 'EXPIRED', 'HOLDING', 'EXPIRING', 'OUTSTANDING',
                 'NEW', 'ALL', 'POINT_LIST', 'RE_REGISTRATION_RATE', 'CHURN_RETENTION_RATE',
                 'ATTENDANCE_TENURE_TREND'],
        MEMBER_DETAIL: ['PROFILE', 'MEMBERSHIP', 'LESSON_PARTICIPATION', 'PRODUCT', 'LOCKER',
                        'POINT', 'MEMBERSHIP_HOLDING', 'MEMBERSHIP_EXTEND', 'REFUND', 'CARD', 'ATTENDANCE'],
        MEMBERSHIP: ['APPLICATION_LIST', 'CATALOG_LIST', 'SUBSCRIPTION_CANCELLATION',
                     'RE_REGISTRATION_RATE', 'TERM_RATIO', 'DISCOUNT_RETENTION',
                     'TOP_RE_REGISTRATION', 'TOP_ATTENDANCE', 'TOP_SALES'],
        SCHEDULE: ['LESSON_LIST', 'RESERVATION_STATUS_LIST', 'RESERVATION_STATUS_DETAIL',
                   'SETTING_DETAIL', 'CATALOG_LIST', 'ATTENDANCE_RATE', 'ATTENDANCE_LIST',
                   'RESERVE_ATTENDANCE_STATS'],
        SALES: ['YEAR', 'TOTAL', 'MONTHLY_TREND', 'AVERAGE_REVENUE', 'PAYMENT_RATIO', 'LINKOPAY_LIST'],
        PRODUCT: ['CATALOG_LIST', 'SALES_LIST', 'SALES_STATS'],
        COMMUNICATION: ['NOTICE_LIST', 'TOP_COMMENTED_NOTICE', 'MESSAGE_LIST', 'BIZ_LIST'],
        INFRA: ['LOCKER_LIST', 'LOCKER_DETAIL', 'SYSTEM_SETTING', 'PARTNER_GYM_LIST', 'COACH_LIST'],
        WORKOUT: ['WOD_LIST', 'PARTICIPANT_LIST', 'PARTNER_GYM_LIST', 'PARTNER_GYM_WOD_LIST'],
      },
    },
    modifier: {
      type: 'array',
      values: ['TOP', 'DISCOUNT', 'NEW', 'EXPIRING', 'EXPIRED', 'HOLDING', 'ALIVE',
               'OUTSTANDING', 'RE_REGISTRATION', 'CHURN', 'RETENTION', 'AVERAGE',
               'MONTHLY', 'YEARLY', 'TENURE', 'SUBSCRIPTION', 'LINKOPAY'],
      note: '복수 modifier 가능. B4-1 핵심 — modifier dropping (예: DISCOUNT 누락) 시 lookup 실패 강제',
    },
    aggregation: {
      type: 'enum',
      values: ['NONE', 'COUNT', 'SUM', 'AVERAGE', 'RATE', 'RATIO', 'CHANGE', 'RANKING', 'TREND'],
    },
    period: {
      type: 'enum',
      values: ['NONE', 'SINGLE', 'RANGE', 'MULTI', 'COMPARISON'],
      note: 'MULTI/COMPARISON은 frontend multi-slot 합성 (intention 차원 아님). 현재 intention catalog에는 NONE/RANGE만 사용',
    },
  },
  required: ['category', 'action', 'aggregation', 'period'],
  optional: ['subject', 'modifier'],
});

export const INTENTION_DIMENSIONS = Object.freeze({
  // GENERAL (3)
  'STYLE':     { category:'GENERAL', action:'STYLE',     subject:'STYLE',     modifier:[], aggregation:'NONE', period:'NONE' },
  'RECOMMEND': { category:'GENERAL', action:'RECOMMEND', subject:'RECOMMEND', modifier:[], aggregation:'NONE', period:'NONE' },
  'FALLBACK':  { category:'GENERAL', action:'FALLBACK',  subject:'FALLBACK',  modifier:[], aggregation:'NONE', period:'NONE' },

  // MEMBER (12)
  'CHECK_MEMBER_SEARCH':                                            { category:'MEMBER', action:'SEARCH', subject:'PROFILE',                  modifier:[],              aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+ALIVE_LIST':                                        { category:'MEMBER', action:'LIST',   subject:'ALIVE',                    modifier:[],              aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+EXPIRED_LIST':                                      { category:'MEMBER', action:'LIST',   subject:'EXPIRED',                  modifier:[],              aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+HOLDING_LIST':                                      { category:'MEMBER', action:'LIST',   subject:'HOLDING',                  modifier:[],              aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD':                           { category:'MEMBER', action:'LIST',   subject:'EXPIRING',                 modifier:[],              aggregation:'NONE', period:'RANGE' },
  'CHECK_MEMBER+OUTSTANDING_LIST':                                  { category:'MEMBER', action:'LIST',   subject:'OUTSTANDING',              modifier:[],              aggregation:'NONE', period:'NONE' },
  'CHECK_NEW+MEMBER_LIST_BY+PERIOD':                                { category:'MEMBER', action:'LIST',   subject:'NEW',                      modifier:['NEW'],         aggregation:'NONE', period:'RANGE' },
  'CHECK_MEMBER+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD':         { category:'MEMBER', action:'STATS',  subject:'RE_REGISTRATION_RATE',     modifier:[],              aggregation:'RATE', period:'RANGE' },
  'CHECK_MEMBER+CHURN+RETENTION+RATE_STATISTICS_BY+PERIOD':         { category:'MEMBER', action:'STATS',  subject:'CHURN_RETENTION_RATE',     modifier:[],              aggregation:'RATE', period:'RANGE' },
  'CHECK_MEMBER+POINT_LIST':                                        { category:'MEMBER', action:'LIST',   subject:'POINT_LIST',               modifier:[],              aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+ALL_LIST':                                          { category:'MEMBER', action:'LIST',   subject:'ALL',                      modifier:[],              aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+ATTENDANCE+TENURE+TREND_STATISTICS_BY+PERIOD':      { category:'MEMBER', action:'STATS',  subject:'ATTENDANCE_TENURE_TREND',  modifier:['TENURE'],      aggregation:'TREND', period:'RANGE' },

  // MEMBER_DETAIL (11) — 모두 priorEndpoint=CHECK_MEMBER_SEARCH
  'CHECK_MEMBER+PROFILE_DETAIL':                  { category:'MEMBER_DETAIL', action:'DETAIL', subject:'PROFILE',              modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+MEMBERSHIP_DETAIL':               { category:'MEMBER_DETAIL', action:'DETAIL', subject:'MEMBERSHIP',           modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+LESSON+PARTICIPATION_DETAIL':     { category:'MEMBER_DETAIL', action:'DETAIL', subject:'LESSON_PARTICIPATION', modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+PRODUCT_DETAIL':                  { category:'MEMBER_DETAIL', action:'DETAIL', subject:'PRODUCT',              modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+LOCKER_DETAIL':                   { category:'MEMBER_DETAIL', action:'DETAIL', subject:'LOCKER',               modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+POINT_DETAIL':                    { category:'MEMBER_DETAIL', action:'DETAIL', subject:'POINT',                modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+MEMBERSHIP+HOLDING_DETAIL':       { category:'MEMBER_DETAIL', action:'DETAIL', subject:'MEMBERSHIP_HOLDING',   modifier:['HOLDING'], aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+MEMBERSHIP+EXTEND_DETAIL':        { category:'MEMBER_DETAIL', action:'DETAIL', subject:'MEMBERSHIP_EXTEND',    modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+REFUND_DETAIL':                   { category:'MEMBER_DETAIL', action:'DETAIL', subject:'REFUND',               modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+CARD_DETAIL':                     { category:'MEMBER_DETAIL', action:'DETAIL', subject:'CARD',                 modifier:[],          aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+ATTENDANCE_DETAIL':               { category:'MEMBER_DETAIL', action:'DETAIL', subject:'ATTENDANCE',           modifier:[],          aggregation:'NONE', period:'NONE' },

  // MEMBERSHIP (10)
  'CHECK_MEMBERSHIP+APPLICATION_LIST':                                   { category:'MEMBERSHIP', action:'LIST',  subject:'APPLICATION_LIST',       modifier:['NEW'],                       aggregation:'NONE',    period:'NONE' },
  'CHECK_MEMBERSHIP+PURCHASE_LIST_BY+PERIOD':                            { category:'MEMBERSHIP', action:'LIST',  subject:'PURCHASE_HISTORY',       modifier:[],                            aggregation:'NONE',    period:'RANGE' },
  'CHECK_MEMBERSHIP+SUBSCRIPTION+CANCELLATION_LIST_BY+PERIOD':           { category:'MEMBERSHIP', action:'LIST',  subject:'SUBSCRIPTION_CANCELLATION', modifier:['SUBSCRIPTION'],          aggregation:'NONE',    period:'RANGE' },
  'CHECK_MEMBERSHIP+CATALOG_LIST':                                       { category:'MEMBERSHIP', action:'LIST',  subject:'CATALOG_LIST',           modifier:[],                            aggregation:'NONE',    period:'NONE' },
  'CHECK_MEMBERSHIP+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD':          { category:'MEMBERSHIP', action:'STATS', subject:'RE_REGISTRATION_RATE',   modifier:[],                            aggregation:'RATE',    period:'RANGE' },
  'CHECK_MEMBERSHIP+TERM+RATIO_STATISTICS_BY+PERIOD':                    { category:'MEMBERSHIP', action:'STATS', subject:'TERM_RATIO',             modifier:[],                            aggregation:'RATIO',   period:'RANGE' },
  'CHECK_MEMBERSHIP+DISCOUNT+RETENTION_STATISTICS':                      { category:'MEMBERSHIP', action:'STATS', subject:'RE_REGISTRATION_RATE',   modifier:['DISCOUNT'],                  aggregation:'RATE',    period:'RANGE' },
  'CHECK_MEMBERSHIP+TOP+RE+REGISTRATION_STATISTICS_BY+PERIOD':           { category:'MEMBERSHIP', action:'STATS', subject:'RE_REGISTRATION_RATE',   modifier:['TOP'],                       aggregation:'RANKING', period:'RANGE' },
  'CHECK_MEMBERSHIP+TOP+ATTENDANCE_STATISTICS_BY+PERIOD':                { category:'MEMBERSHIP', action:'STATS', subject:'ATTENDANCE_RATE',        modifier:['TOP'],                       aggregation:'RANKING', period:'RANGE' },
  'CHECK_MEMBERSHIP+TOP+SALES_STATISTICS_BY+PERIOD':                     { category:'MEMBERSHIP', action:'STATS', subject:'SALES_STATS',            modifier:['TOP'],                       aggregation:'RANKING', period:'RANGE' },

  // SCHEDULE (8)
  'CHECK_SCHEDULE+LESSON_LIST_BY+PERIOD':              { category:'SCHEDULE', action:'LIST',   subject:'LESSON_LIST',                modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_SCHEDULE+RESERVATION+STATUS_LIST_BY+PERIOD':  { category:'SCHEDULE', action:'LIST',   subject:'RESERVATION_STATUS_LIST',    modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_SCHEDULE+RESERVATION+STATUS_DETAIL_BY+PERIOD':{ category:'SCHEDULE', action:'DETAIL', subject:'RESERVATION_STATUS_DETAIL',  modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_SCHEDULE+SETTING_DETAIL_BY+PERIOD':           { category:'SCHEDULE', action:'DETAIL', subject:'SETTING_DETAIL',             modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_SCHEDULE+CATALOG_LIST':                       { category:'SCHEDULE', action:'LIST',   subject:'CATALOG_LIST',               modifier:[], aggregation:'NONE', period:'NONE' },
  'CHECK_MEMBER+ATTENDANCE+RATE_STATISTICS_BY+PERIOD': { category:'SCHEDULE', action:'STATS',  subject:'ATTENDANCE_RATE',            modifier:[], aggregation:'RATE', period:'RANGE' },
  'CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD':            { category:'SCHEDULE', action:'LIST',   subject:'ATTENDANCE_LIST',            modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_RESERVE+ATTENDANCE_STATISTICS':               { category:'SCHEDULE', action:'STATS',  subject:'RESERVE_ATTENDANCE_STATS',   modifier:[], aggregation:'RATE', period:'RANGE' },

  // SALES (6)
  'CHECK_SALES+YEAR_STATISTICS':                              { category:'SALES', action:'STATS', subject:'YEAR',            modifier:['YEARLY'],   aggregation:'SUM',     period:'RANGE' },
  'CHECK_SALES_STATISTICS_BY+PERIOD':                         { category:'SALES', action:'STATS', subject:'TOTAL',           modifier:[],           aggregation:'SUM',     period:'RANGE' },
  'CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD':           { category:'SALES', action:'STATS', subject:'MONTHLY_TREND',   modifier:['MONTHLY'],  aggregation:'TREND',   period:'RANGE' },
  'CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD':         { category:'SALES', action:'STATS', subject:'AVERAGE_REVENUE', modifier:['AVERAGE'],  aggregation:'AVERAGE', period:'RANGE' },
  'CHECK_SALES+PAYMENT+RATIO_STATISTICS_BY+PERIOD':           { category:'SALES', action:'STATS', subject:'PAYMENT_RATIO',   modifier:[],           aggregation:'RATIO',   period:'RANGE' },
  'CHECK_SALES+LINKOPAY_LIST_BY+PERIOD':                      { category:'SALES', action:'LIST',  subject:'LINKOPAY_LIST',   modifier:['LINKOPAY'], aggregation:'NONE',    period:'RANGE' },

  // PRODUCT (3)
  'CHECK_PRODUCT+CATALOG_LIST':           { category:'PRODUCT', action:'LIST',  subject:'CATALOG_LIST', modifier:[], aggregation:'NONE', period:'NONE'  },
  'CHECK_PRODUCT+SALES_LIST_BY+PERIOD':   { category:'PRODUCT', action:'LIST',  subject:'SALES_LIST',   modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_PRODUCT+SALES_STATISTICS':       { category:'PRODUCT', action:'STATS', subject:'SALES_STATS',  modifier:[], aggregation:'SUM',  period:'RANGE' },

  // COMMUNICATION (4)
  'CHECK_NOTICE_LIST_BY+PERIOD':              { category:'COMMUNICATION', action:'LIST',  subject:'NOTICE_LIST',          modifier:[],      aggregation:'NONE',    period:'RANGE' },
  'CHECK_NOTICE+TOP+COMMENTED_STATISTICS':    { category:'COMMUNICATION', action:'STATS', subject:'TOP_COMMENTED_NOTICE', modifier:['TOP'], aggregation:'RANKING', period:'RANGE' },
  'CHECK_MESSAGE_LIST_BY+PERIOD':             { category:'COMMUNICATION', action:'LIST',  subject:'MESSAGE_LIST',         modifier:[],      aggregation:'NONE',    period:'RANGE' },
  'CHECK_BIZ_LIST_BY+PERIOD':                 { category:'COMMUNICATION', action:'LIST',  subject:'BIZ_LIST',             modifier:[],      aggregation:'NONE',    period:'RANGE' },

  // INFRA (5)
  'CHECK_LOCKER_LIST':            { category:'INFRA', action:'LIST',   subject:'LOCKER_LIST',      modifier:[], aggregation:'NONE', period:'NONE' },
  'CHECK_LOCKER_DETAIL':          { category:'INFRA', action:'DETAIL', subject:'LOCKER_DETAIL',    modifier:[], aggregation:'NONE', period:'NONE' },
  'CHECK_SYSTEM+SETTING_DETAIL':  { category:'INFRA', action:'DETAIL', subject:'SYSTEM_SETTING',   modifier:[], aggregation:'NONE', period:'NONE' },
  'CHECK_PARTNER+GYM_LIST':       { category:'INFRA', action:'LIST',   subject:'PARTNER_GYM_LIST', modifier:[], aggregation:'NONE', period:'NONE' },
  'CHECK_COACH_LIST':             { category:'INFRA', action:'LIST',   subject:'COACH_LIST',       modifier:[], aggregation:'NONE', period:'NONE' },

  // WORKOUT (4)
  'CHECK_WORKOUT+WOD_LIST_BY+PERIOD':             { category:'WORKOUT', action:'LIST', subject:'WOD_LIST',              modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_WORKOUT+PARTICIPANT_LIST_BY+PERIOD':     { category:'WORKOUT', action:'LIST', subject:'PARTICIPANT_LIST',      modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_WORKOUT+PARTNER+GYM_LIST_BY+PERIOD':     { category:'WORKOUT', action:'LIST', subject:'PARTNER_GYM_LIST',      modifier:[], aggregation:'NONE', period:'RANGE' },
  'CHECK_WORKOUT+PARTNER+GYM+WOD_LIST':           { category:'WORKOUT', action:'LIST', subject:'PARTNER_GYM_WOD_LIST',  modifier:[], aggregation:'NONE', period:'NONE' },

  // VISUALIZATION 6 — 자체 fetch architecture B 일원화 (2026-05-19)
  'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST':           { category:'MEMBER',     action:'LIST', subject:'MEMBERSHIP_HISTORY',        modifier:[],         aggregation:'NONE', period:'NONE' },
  'CHECK_RESERVE+CLASS+SLOT_LIST_BY+PERIOD':        { category:'SCHEDULE',   action:'LIST', subject:'CLASS_SLOT',                modifier:[],         aggregation:'NONE', period:'RANGE' },
  'CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD': { category:'SCHEDULE',   action:'LIST', subject:'MONTHLY_ATTENDANCE',        modifier:['MONTHLY'],aggregation:'NONE', period:'RANGE' },
  'CHECK_SALES_LIST_BY+YEAR':                       { category:'SALES',      action:'LIST', subject:'SALES_BY_YEAR',             modifier:['YEARLY'], aggregation:'NONE', period:'RANGE' },
  'CHECK_MEMBERSHIP+DURATION_LIST_BY+PERIOD':       { category:'MEMBERSHIP', action:'LIST', subject:'MEMBERSHIP_DURATION',       modifier:[],         aggregation:'NONE', period:'RANGE' },
  'CHECK_MEMBER+ENGAGEMENT_LIST_BY+PERIOD':         { category:'MEMBER',     action:'LIST', subject:'MEMBER_ENGAGEMENT',         modifier:[],         aggregation:'NONE', period:'RANGE' },
});

/**
 * @param {string} intentionKey
 * @returns {object|null} 6-axis dimensions or null
 */
export function getIntentionDimensions(intentionKey) {
  if (!intentionKey || typeof intentionKey !== 'string') return null;
  return INTENTION_DIMENSIONS[intentionKey] || null;
}


// ============================================
// 인텐션 로컬 데이터 — DB(ai_intention) 스냅샷
// ============================================
// 빠른 iteration용. 편집 후 syncIntentionsToDb.mjs로 DB 적재.
// helper: getLocalIntentionsByCategory(category) — aiGuide.js

// Visualization 도구 중앙 레지스트리 (override 가드/메타 검증 공통 참조)
export const VISUALIZATION_TOOLS = [
  'show_revenue_chart',
  'show_member_trend',
  'show_member_flow_trend',
  'show_sales_view',
  'show_attendance_chart',
  'show_attendance_ranking',
  'show_engagement_chart',
  'show_ltv',
  'show_member_status',
  'show_membership_preference',
  'show_period_sales',
  'show_point_mange',
];


// ============================================
// aiPrompts.js → 이전 (파일 폐기): 비-DB meta/구조 데이터
// DEFAULT_DISPLAY_OVERRIDES / CALL_API_GUIDES / AVAILABLE_FIELDS_OVERRIDES / buildComparisonNextGuide / LOCAL_INTENTIONS_DATA
// ============================================
export const DEFAULT_DISPLAY_OVERRIDES = {
  'CHECK_SALES+YEAR_STATISTICS': 'show_revenue_chart',
  'CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD': 'show_revenue_chart',
};

// ============================================
// 인텐션별 call_api 가이드 — DB 덤프 (로컬 테스트용, 배포 시 DB 우선)
// ============================================
export const CALL_API_GUIDES = {
  'CHECK_MEMBER_SEARCH': `[NEXT STEP] Verify the searched member. If duplicates (동명이인), show list for selection. If 1 match, proceed to next detail intention.`,
  'CHECK_MEMBER+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD': `[NEXT STEP] Data includes BOTH re-registered (reRegistration=true) AND new (reRegistration=false) PURCHASERS in the period. reRegistrationRate = share of purchasers who already had purchase history BEFORE the period (buyer composition) — it is NOT the renewal rate after expiry. RE-ROUTE: if the user actually asked '만료 후 재등록률/갱신율' (renewal rate after expiry), this intention is the wrong source — call call_api(CHECK_MEMBER+CHURN+RETENTION+RATE_STATISTICS_BY+PERIOD) and answer with its renewalRate instead. When user asks for re-registered member LIST (Korean: 재등록 회원) → filter(field:"reRegistration", condition:{type:"exact",value:true}) FIRST, then show_display. When user asks for re-registration RATE among purchasers → announce reRegistrationRate from summary as text, then show_display (all items, no filter needed).`,
  'CHECK_MEMBER+CHURN+RETENTION+RATE_STATISTICS_BY+PERIOD': `[NEXT STEP] Aggregate summary statistics (no per-member list) — answer in text, show_display not needed. METRIC SELECTION (MANDATORY): headline ONLY the metric the user asked. '만료 후 재등록률/갱신율/재등록 비율' → renewalRate. '이탈률/유지율' → churnRate/retentionRate. churnRate≠renewalRate — denominators differ (renewalRate: % of expiry EVENTS renewed within 30 days, denominator = matureExpiredEvents = events past the 30-day observation window only; churn/retention: member-pool based = totalMemberPool). PHRASING: renewalRate is per-EVENT — say '만료 건의 X%' / '만료 건 기준 X%', NEVER '만료 회원의 X%'. ZERO DENOMINATOR: if matureExpiredEvents=0, do NOT cite renewalRate 0% — say no expiry has completed the 30-day observation yet and report progress using pendingObservationEvents(N)/pendingRenewedSoFar(M) (예: '아직 판정 가능한 만료 건이 없어. 진행 중 N건 중 M건은 벌써 재등록했어'). PENDING CAVEAT: when citing renewalRate while pendingObservationEvents>0, append one short line that N recent expiries are still within their 30-day window. FOLLOW-UP '재등록까지 평균 며칠/얼마나 걸려' → avgDaysToRenew (avg days, renewals within 0~30d only; earlyRenewalCount = renewals started before expiry, excluded from the average).`,
  'CHECK_MEMBER+ATTENDANCE+TENURE+TREND_STATISTICS_BY+PERIOD': `[NEXT STEP] Attendance rate trend by months-since-registration. Announce peakRetentionMonth (가장 꾸준히 나오는 시점) and the drop-off onset in PLAIN everyday Korean the gym owner understands. NEVER expose raw English identifiers OR internal jargon — FORBIDDEN phrasings: "절대 70% 기준", "매장 평균 -15%p", "relative/absolute". Map each metric to plain language: dropOffStartMonthAbsolute → "N개월 차부터 출석률이 70% 아래로 떨어져"; dropOffStartMonthRelative → "N개월 차부터 매장 평균보다 눈에 띄게 낮아져". If both point to the SAME month, state it ONCE ("N개월 차부터 출석률이 떨어지기 시작해") — do NOT redundantly cite two separate bases for the same month. If they differ, mention both but ONLY with those plain phrasings. Use show_display for tenure detail. Use items.tenureLabel ("가입 첫 달", "N개월 차") as table labels.
[DROPOFF FACT-CHECK — MANDATORY] Before mentioning a drop-off month, verify the value in summary:
  • dropOffStartMonthRelative === null (or key absent) → say "출석률이 매장 평균보다 크게 낮아지는 구간은 없었어요" (plain wording, no jargon) — DO NOT invent a month.
  • dropOffStartMonthAbsolute === null (or key absent) → say "출석률이 70% 아래로 떨어지는 구간은 없었어요" (plain wording, no jargon) — DO NOT invent a month.
  • dropOffStartMonthRelative or dropOffStartMonthAbsolute === peakRetentionMonth → IMPOSSIBLE pattern, treat as null (drop-off must occur AFTER peak).
  • If the value is a number > peakRetentionMonth → cite that month explicitly ("X개월 차부터 떨어지기 시작해요").
[FORBIDDEN] Citing the peak month as drop-off month (e.g., "8개월 차에 꾸준한데 8개월 차부터 떨어져요") — self-contradiction. Citing 0개월 / 0달 when peak is later — self-contradiction. Citing only threshold percentage without a month when key is non-null — incomplete.`,
  'CHECK_MEMBER+PROFILE_DETAIL': `[NEXT STEP] Single member profile. Summarize as text. show_display not needed.
[OUTRO RULE — TABLE-DUPLICATION BLOCKER] If show_display IS used (table form), outro/finish() text MUST NOT re-enumerate the same profile fields the table already shows — neither as a bullet/dash list nor as a comma-separated sentence (예: "이름은 X, 성별은 여자, 전화번호는 ..., 프로필 사진도 등록돼 있어"). This duplicates information already visible in the table row and creates user-visible redundancy. When the table covers all requested fields, outro should be either: (a) omitted entirely, (b) a single brief acknowledgement (예: "회원 상세 확인했어"), or (c) ONE additional fact not present in the table (예: 회원 메모 내용 발췌). NEVER list profile fields (이름/성별/전화번호/생년월일/닉네임/프로필 사진/소속 지점) in outro when the same column appears in show_display.`,
  'CHECK_MEMBER+MEMBERSHIP_DETAIL': `[NEXT STEP] Member's membership history. status: 1=active, 2=expired, 3=holding, 4=refunded. ALWAYS call show_display regardless of record count (single record + multi record both use show_display — table format is the preferred presentation for membership info). NEVER answer with text-only finish() when items exist.\n[STATUS VALIDATION] Backend may return status=1 for memberships whose endDate has already passed — the system auto-corrects these to status=2 (expired). Trust the status value in the data as-is after API response. NEVER label an item as "active" or "valid" based on status alone — always verify endDate >= today before calling it active.\n[SHOW_DISPLAY RULE] Do NOT pass columns argument — omit it entirely to let auto-selection include memberShipName + startDate + endDate + status. If you explicitly set columns, you MUST include memberShipName — without it user cannot distinguish rows (e.g. two "12회권" vs "1개월권"). Omitting memberShipName is a critical info-loss bug.\n[OUTRO RULE — RAW VALUE LEAK BLOCKER] In show_display intro/outro and finish() text, NEVER enumerate raw field values as a bullet/dash list mirroring the table content (예: "- 회원권명: 1개월권\\n- 시작일: ...\\n- 만료일: ..."). The table already shows that data — duplicating it as text is redundant AND surfaces raw enum codes / sentinels the table renders as labels (status=1/2 vs "활성"/"만료"; totalMax=-1 vs "무제한"; courseTakeCount=0 vs "-"). Outro may state ONE high-level fact ONLY: total active membership count, OR currently active membership's name + 남은일수 (D-N). NEVER mention internal fields: 결제 식별값, 결제 식별값 묶음, 거래번호, 구독 회원권 여부, 구독 상태, 구독 삭제 여부, 예상 회원권 여부, 환불 카드/현금 금액, 판매 카드/미수금 금액, 적립 포인트, 홀딩 가능 횟수/홀딩 횟수 — these are backend internals, not user-facing membership info. If no active membership, state "현재 활성 회원권은 없어요" with no further field dump.`,
  'CHECK_MEMBER+LESSON+PARTICIPATION_DETAIL': `[NEXT STEP] Member's per-lesson participation. Separate overall summary from per-lesson detail. Use show_display for multiple lessons.`,
  'CHECK_MEMBER+ATTENDANCE_DETAIL': `[NEXT STEP] Member's per-date reservation/attendance records, already sorted by classDate DESC (records[0] = most recent reservation). status: attended=출석, absent=결석 (absent = reserved but did not attend — still counts as a reservation); membershipName = 예약 회원권명. For per-lesson aggregation/statistics use CHECK_MEMBER+LESSON+PARTICIPATION_DETAIL.
[SINGLE-TIMEPOINT — ANSWER ONE DATE, NO TABLE] If the user asked WHEN the member last reserved/visited/attended (Korean: 마지막 예약/마지막 방문/마지막 출석/최근 예약/마지막으로 언제 왔어/언제 마지막으로 예약했어 — a single date is expected, NOT a history table), do NOT call show_display. Call finish() answering ONLY with the single most-recent reservation date = records[0].classDate (Korean reference: {name} 회원의 마지막 예약은 {classDate}이야.). State ONLY the date — do NOT append lesson name or attendance status: the buffer holds SEPARATE fields lessonName(수업명, e.g. 크로스핏) and membershipName(예약 회원권명, e.g. 1년시즌권) plus a status(attended/absent) that are easy to mislabel, so omit them unless the user explicitly asks. Dumping the full per-date table for a single-timepoint "언제" question is an INTENT-DRIFT bug — the user wants one date, not a list.
[LIST REQUEST — SHOW TABLE] Only when the user explicitly asked to SEE the records (Korean: 일자별 출석 기록/출석 내역 보여줘/예약 내역 전부 보여줘) call show_display with the full per-date table.`,
  'CHECK_MEMBER+PRODUCT_DETAIL': `[NEXT STEP] Member's product purchase history. Text or show_display.`,
  'CHECK_MEMBER+LOCKER_DETAIL': `[NEXT STEP] Member's locker assignment. Distinguish current vs past as text.`,
  'CHECK_MEMBER+POINT_DETAIL': `[NEXT STEP] Member's point history. summary.totalPoint = current balance. Announce balance first; show_display for detail if needed.`,
  'CHECK_MEMBER+MEMBERSHIP+HOLDING_DETAIL': `[NEXT STEP] Member's holding (pause) history.
[CALL FLOW] When buffer items array is non-empty, call show_display (table is the natural presentation for per-holding rows). When items is empty/null, answer with finish() text only (no show_display) — empty answer MUST include the queried member's name (from prior CHECK_MEMBER_SEARCH buffer) so the user can confirm which person was queried; never emit a bare "데이터 없음" sentence without the member name context.
[SHOW_DISPLAY RULE] Omit the columns argument entirely — auto-selection picks the available buffer item fields. If you explicitly set columns, choose ONLY from the keys actually present in buffer.items[0] (typically: memberShipName / startDate / endDate / reason / holdingDays). NEVER request fields that exist only in available_fields metadata but not in buffer item rows (e.g. relational fields like memberName from CHECK_MEMBER_SEARCH, or derived/summary-only fields) — these trigger SHOW_DISPLAY COLUMN GUARD retry and waste iterations. The safest path is to omit columns entirely.
[INTRO/OUTRO RULE] intro uses {{COUNT}} placeholder (resolved to displayCount) — never raw "{{SUMMARY:totalCount}}" or a hard-coded number computed by the LLM. intro MUST mention the queried member's name explicitly (from prior CHECK_MEMBER_SEARCH buffer); bare "총 {{COUNT}}건이야" (no member name) is INCOMPLETE in a per-member detail context where the user expects member-anchored framing. Outro is optional — when present it must NOT re-enumerate rows the table already shows (no bullet/dash list mirroring table content) and must NOT introduce metrics not visible in the buffer (no invented totals).`,
  'CHECK_MEMBER+MEMBERSHIP+EXTEND_DETAIL': `[NEXT STEP] Member's membership extension history.
[CALL FLOW] When buffer items array is non-empty, call show_display (table is the natural presentation for per-extension rows). When items is empty/null, answer with finish() text only (no show_display) — empty answer MUST include the queried member's name (from prior CHECK_MEMBER_SEARCH buffer) so the user can confirm which person was queried; never emit a bare "데이터 없음" sentence without the member name context.
[SHOW_DISPLAY RULE] Omit the columns argument entirely — auto-selection picks the available buffer item fields. If you explicitly set columns, choose ONLY from the keys actually present in buffer.items[0] (typically: memberShipName / startDate / endDate / reason). NEVER request fields that exist only in available_fields metadata but not in buffer item rows (e.g. derived/summary-only fields like extendDays, or relational fields like memberName from CHECK_MEMBER_SEARCH) — these trigger SHOW_DISPLAY COLUMN GUARD retry and waste iterations. The safest path is to omit columns entirely.
[INTRO/OUTRO RULE] intro uses {{COUNT}} placeholder (resolved to displayCount) — never raw "{{SUMMARY:totalCount}}" or a hard-coded number computed by the LLM. intro MUST mention the queried member's name explicitly (from prior CHECK_MEMBER_SEARCH buffer); bare "총 {{COUNT}}건이야" (no member name) is INCOMPLETE in a per-member detail context where the user expects member-anchored framing. Outro is optional — when present it must NOT re-enumerate rows the table already shows (no bullet/dash list mirroring table content) and must NOT introduce metrics not visible in the buffer (no invented totals).`,
  'CHECK_MEMBER+REFUND_DETAIL': `[NEXT STEP] Member's refund history. summary.totalRefundPrice = total refund amount. Announce summary as text.`,
  'CHECK_MEMBER+CARD_DETAIL': `[NEXT STEP] Member's card registration status. Text answer. show_display not needed.`,
  'CHECK_MEMBERSHIP+APPLICATION_LIST': `[NEXT STEP] Membership signup application list. approval: 0=pending, 1=approved, 2=rejected.
[DATA SHAPE — MIXED STATUS] API returns ALL applications regardless of approval status (mixed: pending + approved + rejected coexist). DO NOT assume rows are pending-only. Inspect items[].approval distribution before show_display — if no row has approval=0 you cannot honestly say "승인 대기 중".
[DEFAULT PATH — NO STATUS KEYWORD IN USER INPUT] If user input is generic (Korean: "신규 신청 목록/가입 신청 목록/신청자 목록/신청 명단/신청 회원" without 대기/승인/거절/반려/미승인 status qualifier), do NOT filter. Call show_display directly on all items. intro MUST be neutral: "신규 신청 목록 {{COUNT}}건이야" / "가입 신청 목록 {{COUNT}}건이야" / "신청자 {{COUNT}}명이야" — strictly no status word (대기/승인/거절/반려/미승인) in intro or outro.
[STATUS PATH — USER INPUT CONTAINS STATUS KEYWORD] Only when user input explicitly contains a status keyword, apply filter BEFORE show_display:
  - "승인 대기 / 대기 회원 / 대기 명단 / 대기 중인 / 승인 안 된 / 미승인" → filter(field:"approval", condition:{type:"exact", value:0})
  - "승인된 / 승인 완료 / 가입 승인 (NOT just \"승인 대기\")" → filter(field:"approval", condition:{type:"exact", value:1})
  - "거절된 / 반려된 / 거절 명단" → filter(field:"approval", condition:{type:"exact", value:2})
  After filter:
  - items.length=0 → finish with honest text matching the requested status AND include total application count + other-status distribution context so user understands why filter returned 0 (e.g. "전체 신청 N건 중 승인 대기는 없어. 모두 승인 완료(M건)/거절(K건) 상태야" — derive M/K from filter tool message "실제 데이터 값: [...]" hint and pre-filter buffer count). DO NOT call show_display, DO NOT show all rows as substitute. Mandatory: cite the pre-filter total count from the call_api result message ("N건 조회 완료") so the user does not assume the API returned nothing.
  - items.length>0 → show_display + intro: "승인 대기 신청 {{COUNT}}건이야" / "거절된 신청 {{COUNT}}건이야" using the filtered count.
[INTRO RULE — STATUS HONESTY] Critical rule: if you did NOT call filter on approval, intro/outro MUST NOT contain status words (대기/승인 대기/대기 중/거절/반려/미승인). Putting "대기" in intro while displaying rows with approval=2(거절) creates visible self-contradiction the user immediately catches.
[STATUS COLUMN MANDATORY] show_display MUST include the approval column so the user can verify each row's status. NEVER omit approval.`,
  'CHECK_MEMBERSHIP+CATALOG_LIST': `[NEXT STEP] Center membership catalog (회원권 product types). This intention returns ONE row per membership product (memberShipName/totalPrice/memberShipDuration/totalCount) — it is reference/lookup data, NOT a per-member dataset. [WRONG-FIT FOR DISTRIBUTION QUERIES] If the user's question is a per-member distribution (Korean: 회원권별 인원/회원권별 분포/회원권별 회원 수/회원권 분포/회원권 구성/회원권별 통계), this intention is the WRONG source — there is NO per-member count here, so compute(group, viewMembershipName) on these rows yields "종류별 1 1 1 ..." which does NOT answer the user. IMMEDIATELY abandon this intention: call get_intentions(MEMBER) → call_api(CHECK_MEMBER+ALIVE_LIST) → compute(operation:"group", field:"viewMembershipName") → show_display. The same rule applies for any "회원권별 + 인원/명/회원/분포/비율" axis — route to a MEMBER list intention, not the catalog.`,
  'CHECK_MEMBERSHIP+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD': `[NEXT STEP] Per-product re-registration rate. summary.totalReRegistrationRate = overall. Announce summary as text.`,
  'CHECK_MEMBERSHIP+TERM+RATIO_STATISTICS_BY+PERIOD': `[NEXT STEP] Long-term vs short-term ratio. summary has longTermRate/shortTermRate + revenue. Announce summary as text — the headline answer is the long/short RATIO (longTermRate vs shortTermRate), NOT the row count.\n[INTRO RULE] intro/outro MUST NOT use generic "총 N건" / "N건이야" pointing to items count or group count (e.g. items.length=14 회원권 종류 수). Such phrasing collides with summary.totalPurchaseCount (실제 구매 건수) and confuses the user about what N counts. Instead, lead with the ratio percentages (e.g. "단기 {{SUMMARY:shortTermRate}}%, 장기 {{SUMMARY:longTermRate}}%") and optionally append "(총 구매 {{SUMMARY:totalPurchaseCount}}건 기준)" — never bare "총 N건".\n[NO_COMPUTE_GROUP] DO NOT call compute(operation='group', field='durationType') after call_api. The raw items are 회원권 종류별 rows (e.g. 14 종류), and group(durationType) produces "단기: 14" which is 종류 수 — NOT the purchase count the user asked about (summary.totalPurchaseCount). Skip compute entirely; call show_display directly with raw item columns + ratio placeholder in intro.\n[COMPARISON MODE] For multi-period term ratio comparison (Korean: N월 N월 기간별 비율 비교/회원권 비교/회원권 추이), call this intention once per period with slot names. matchKey="memberShipId". show_display MUST include ALL core metric columns per period: memberShipName + each period's purchaseCount + purchaseRate + totalRevenue (e.g. columns:["memberShipName","1월_purchaseCount","2월_purchaseCount","3월_purchaseCount","1월_purchaseRate","2월_purchaseRate","3월_purchaseRate","1월_totalRevenue","2월_totalRevenue","3월_totalRevenue"]). NEVER omit totalRevenue columns — revenue is essential context for ratio analysis. NEVER omit any of the requested periods from display columns.`,
  'CHECK_MEMBERSHIP+DISCOUNT+RETENTION_STATISTICS': `[NEXT STEP] Discount vs non-discount re-registration comparison. summary has comparison numbers (discountReRegistrationRate, nonDiscountReRegistrationRate, rateDifference, discountPurchaseCount, discountReRegistrationCount, nonDiscountPurchaseCount, nonDiscountReRegistrationCount, totalPurchaseCount, totalReRegistrationCount, totalReRegistrationRate).
[INTRO RULE — HEADLINE IS THE COMPARISON, NOT THE AGGREGATE] The user asked specifically about 할인 회원 재등록률 (discount). intro/outro headline MUST lead with the comparison numbers ({{SUMMARY:discountReRegistrationRate}}% vs {{SUMMARY:nonDiscountReRegistrationRate}}%). FORBIDDEN: leading with {{SUMMARY:totalReRegistrationRate}} as the headline ("전체 재등록률은 N%") — that aggregates discount+non-discount and answers a different question than what the user asked. totalReRegistrationRate is an optional context value (cite only as supplementary, never as the lead).
[OUTRO] Optionally append rate gap with {{SUMMARY:rateDifference}}%p and/or small-sample caveat if discountPurchaseCount is very small (1-2 cases).`,
  'CHECK_MEMBERSHIP+TOP+RE+REGISTRATION_STATISTICS_BY+PERIOD': `[NEXT STEP] Top re-registration rate membership ranking. Ranking pre-sorted.`,
  'CHECK_MEMBERSHIP+TOP+ATTENDANCE_STATISTICS_BY+PERIOD': `[NEXT STEP] Top attendance rate membership ranking. Ranking pre-sorted.`,
  'CHECK_MEMBERSHIP+TOP+SALES_STATISTICS_BY+PERIOD': `[NEXT STEP] Per-membership sales ranking from CHECK_MEMBERSHIP+TOP+SALES. summary has totalSalesCount + totalSalesRevenue. Backend pre-sorts by salesCount (rank=1 = most UNITS sold). For "얼마야" questions, answer with averagePrice.
[REVENUE CONTRIBUTION INTENT — 매출 기여/제일 매출 많은] When the user asks 매출 기여/기여도/수익 기여 or 제일 매출 많은/매출 높은/매출 1위, the contribution measure is salesRevenue (총 매출, refund-aware). Call sort(field:"salesRevenue") desc BEFORE show_display so the revenue leader is at top (backend salesCount order may differ). Lead the intro with the DATA PERIOD followed by total revenue — "{{SUMMARY:startDate}} ~ {{SUMMARY:endDate}} 기준" then {{SUMMARY:totalSalesRevenue}}원 (총 매출), NOT a 건수 count. The period MUST use summary.startDate/endDate (the actual applied window the backend returned), NEVER the call_api date params you passed (they can differ from the applied default).
[SHOW_DISPLAY COLUMNS — FIXED] Display ONLY these top-sales fields: columns ["memberShipName","salesCount","salesRevenue","salesRate"] with columnLabels {"memberShipName":"회원권","salesCount":"판매 건수","salesRevenue":"총 매출","salesRate":"판매 점유율"}. Do NOT pull catalog fields (price/validity/categoryName/memberShipDuration) from any other buffer — catalog 가격 is irrelevant to sales contribution and sorting by it is wrong.
[salesRate LABEL — STRICT] salesRate = 판매 점유율 (salesCount / totalSalesCount = a COUNT-based share), NOT a revenue share. Its columnLabel MUST be exactly "판매 점유율". NEVER label salesRate with any 매출/기여/수익 word (FORBIDDEN: 매출 기여율 / 기여율 / 매출 점유율 / 매출 비중) — salesRate 68.3 means 68.3% of transaction COUNT, not revenue (1개월권 revenue share is 48.2%). No revenue-share field exists; do NOT fabricate a 기여율 column. The revenue contribution is conveyed by the 총 매출 (salesRevenue) column.
[COUNT INTENT — 많이 팔린/인기/판매 순위] For most-sold / popularity intent (Korean: 많이 팔린/인기 회원권/판매 순위/판매량), keep the backend salesCount order; salesCount is the measure.
[LEAST-SOLD INTENT — 적게 팔린/안 팔리는/하위 — MANDATORY SEQUENCE] When the user asks for the LEAST/fewest-sold membership (Korean: 적게 팔린/적게 팔리는/안 팔리는/덜 팔리는/판매 하위/제일 안 나가는/가장 안 팔리는): (1) After call_api, MUST call sort(field:"salesCount", direction:"asc") BEFORE show_display — the backend ranking is DESC (most-sold first), so without this the bottom is hidden. (2) Word show_display.intro as '판매 하위/적게 팔린 회원권' framing, NOT '인기/판매 순위'. NEVER present the DESC top order for a least-sold question — that is the opposite of what was asked.`,
  'CHECK_SCHEDULE+SETTING_DETAIL_BY+PERIOD': `[NEXT STEP] Class operation settings. Summarize settings as text.`,
  'CHECK_SCHEDULE+CATALOG_LIST': `[NEXT STEP] Center class catalog. [CONTEXT CHECK] If user named a specific lesson (필라테스/요가/HIIT etc.), this catalog is for existence-check only — if items.lessonName has NO match, immediately finish("해당 수업은 등록되어 있지 않아요"). Do NOT call show_display (user wants specific lesson data, not the catalog itself). [CATALOG DISPLAY — DEFAULT] If user requested the catalog itself (Korean: "수업 종류/수업 종류 알려줘/어떤 수업 있어/무슨 수업 있어/수업 목록/수업 카탈로그/운영 수업/등록된 수업/클래스 종류/레슨 종류"), MUST call show_display with columns [lessonName, type, maxBookedPeople, teacherNames] and intro "센터에서 운영 중인 수업 종류예요. 총 {{COUNT}}종류예요". NEVER finish with text-only when items[] has rows for a bare catalog request — the catalog is a tabular response. NEVER fall back to "해당 데이터가 없어요" — CATALOG_LIST is period-independent and reflects registered class types; if items=0, it means the center has NOT registered any class types yet, in which case finish("등록된 수업 종류가 없어요").`,
  'CHECK_MEMBER+ATTENDANCE+RATE_STATISTICS_BY+PERIOD': `[NEXT STEP] summary has overall attendance KPI.\n[METRIC DISAMBIGUATION — THREE DISTINCT METRICS]\n  1. summary.activeRate (Korean: "전체 출석률(%)") = ratio of members who attended at least once out of ALL members. e.g. activeRate=34 means 34% of all members attended. This is the DEFAULT "출석률" metric.\n  2. summary.avgAttendanceRate (Korean: "예약 출석률 평균(%)") = average of per-member (attended/reserved) ratios. Often 100% when no absences occur — misleading as a headline KPI.\n  3. members[].attendanceRate (Korean: "예약 출석률(%)") = individual per-member reserved-to-attended ratio. Use for per-member filtering/ranking only.\n[DEFAULT METRIC] When user says "출석률" without "예약" qualifier, ALWAYS use summary.activeRate. avgAttendanceRate is only correct when user explicitly says "예약 출석률".\n[FORBIDDEN — avgAttendanceRate AS HEADLINE] NEVER use {{SUMMARY:avgAttendanceRate}} in intro/outro/finish as the headline "출석률". avgAttendanceRate is often 100% (no absences among those who reserved) and misleads users. ALWAYS use {{SUMMARY:activeRate}} for "출석률" headline.\n[FINISH NOW — KPI INTENT] If user input is KPI intent (Korean: "출석률 알려줘/보여줘/얼마야", no 명단/목록/리스트/회원들 keyword), call finish IMMEDIATELY with response using {{SUMMARY:activeRate}} (e.g. Korean: "이번 달 전체 출석률은 {{SUMMARY:activeRate}}%야. 전체 회원 {{SUMMARY:totalMembers}}명 중 출석한 회원은 {{SUMMARY:attendedMembers}}명이야"). Do NOT call show_display — the user wants the KPI, not the member list.\n[SHOW_DISPLAY ONLY IF LIST REQUESTED] Only call show_display when user explicitly asks for a member list (Korean: 명단/목록/리스트/회원들). In that case, intro MUST use {{SUMMARY:activeRate}} as headline KPI, NOT {{SUMMARY:avgAttendanceRate}}.\n[DO NOT COMPUTE RATE] Do NOT compute attendance rate from totalAttendedCount/totalReservations. Use the appropriate summary scalar directly.\n[EXCEPTION — COUNT QUERY] If user asked for "출석 횟수" (attendance COUNT, Korean: 횟수/회), NOT "출석률" (rate, Korean: 률/%), use compute(operation:"average"/"sum", field:"attendedCount") on items. Korean: 횟수 = attendedCount, 률 = attendanceRate.\n[DATA CAUTION] summary scalars are aggregates. For individual filtering, use members[].attendedCount/attendanceRate.\n[COMPARISON MODE] For multi-period attendance rate comparison (Korean: N월 N월 출석률 비교/추이), call this intention for each period with slot names. merge_slots with matchKey="userId", mode:"outer". show_display MUST include per-member rows: name + per-period attendanceRate (e.g. "1월_attendanceRate","2월_attendanceRate","3월_attendanceRate"). NEVER reduce to summary-only.`,
  'CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD': `[ATTENDANCE FILTER RULE]\nFor COUNT-based filtering (출석 N회 이상/이하), ALWAYS use attendedCount. NEVER use weeklyFrequency.\nFor RATE-based filtering (출석률 N% 이상/이하), use attendanceRate with numeric_gte/numeric_lte. e.g. filter({ field: "attendanceRate", condition: { type: "numeric_lte", value: 50 } }).\nFor "주 N회 이하/이상", convert to total: N × numberOfWeeks (numberOfWeeks = round((endDate - startDate) / 7)).\n[SINGLE CATEGORY — NO CROSS-MERGE] For "주 N회 이상/이하 + 나온/출석한 + 회원" pattern, this intention alone provides per-member attendedCount + weeklyFrequency. DO NOT call ALIVE_LIST / MEMBERSHIP_LIST / cross-merge. Single call_api + filter → show_display is sufficient.\n[REQUIRED COLUMNS for "주 N회" queries] show_display MUST include weeklyFrequency column (주당 출석 빈도) so the user can verify the frequency criterion. Default columns: [name, phone, attendedCount, weeklyFrequency, lastVisit].\n[FILTER BY INTENT]\n- ATTENDED (나온/출석한/다닌): range(min:1) to exclude zero.\n- NOT ATTENDED (안 나온/안 한/결석/미출석): exact:0.\n- RATE THRESHOLD (출석률 N% 이하/이상): filter attendanceRate with numeric_lte/numeric_gte.\nweeklyFrequency is DISPLAY ONLY.\n[AGGREGATE QUERY — MANDATORY ORDERING] When user asks for aggregate statistics (Korean: 평균/총/최대/최소 + 출석 횟수), you MUST call compute BEFORE show_display. Wrong order → wrong answer.\n  Step sequence (DO NOT REORDER):\n  1) call_api loads data (already done)\n  2) compute(operation:"average", field:"attendedCount") — CALL THIS NEXT, before anything else\n  3) show_display (optional, only if user wants member list)\n  FORBIDDEN: calling show_display or finish before compute. If you skip compute, you will use summary.avgWeeklyFrequency (weekly frequency) instead of avg(attendedCount) (total count average) — WRONG metric.\n  "평균 출석 횟수" → compute(operation:"average", field:"attendedCount") → finish with "평균 출석 횟수는 {{SUMMARY:avgAttendedCount}}회"\n  "총 출석 횟수" → compute(operation:"sum", field:"attendedCount")\n  "최대 출석" → compute(operation:"max", field:"attendedCount")\n  NEVER use summary.avgWeeklyFrequency as "평균 출석 횟수" — avgWeeklyFrequency is WEEKLY frequency, NOT total attendance count average.\n[COMPARISON MODE] For multi-period attendance comparison (Korean: N월 N월 출석 비교/출석 추이), use this intention with multiple slots. matchKey="userId". show_display MUST include per-member rows with columns: name + each period's attendedCount (e.g. "1월_attendedCount","2월_attendedCount","3월_attendedCount"). NEVER summarize to aggregate-only.`,
  'CHECK_RESERVE+ATTENDANCE_STATISTICS': `[REQUIRED PARAM — DO NOT OMIT] groupBy: "coach" | "lesson" | "day" | "time". Set explicitly based on user's question.
[KEYWORD → groupBy] 코치별/강사별/코치 성과/누가 가장 많이 → "coach". 수업별/레슨별/클래스별/정원 대비 → "lesson". 요일별/일별/무슨 요일 → "day". 시간대별/시간별/오전·오후·저녁/몇 시 출석 → "time".
[SELF-CHECK] (1) Does user's question mention grouping axis (코치/수업/요일/시간대)? (2) If yes, set groupBy. (3) If no, default to "day". NEVER call without groupBy.
[COHORT REDIRECT] For lifecycle keywords (꾸준히/라이프사이클/가입 후 N개월/이탈 시점/신규 회원 출석 변화), use MEMBER category's CHECK_MEMBER+ATTENDANCE+TENURE+TREND_STATISTICS_BY+PERIOD instead.
[FIELD SEMANTICS — DISTINGUISH CAREFULLY] totalSessions = number of class sessions held (수업 개설/진행 횟수, lesson-side). totalReservations = total booking transactions across all sessions (예약 건수). attendedCount = total attendance count across all sessions (출석/참여 횟수, member-side). [METRIC FILTER FIELD — STRICT] When user filters by Korean "참여 횟수/출석 횟수/수강 횟수" with numeric threshold (N회 이상/이하) → filter field = attendedCount. When user filters by Korean "예약 건수/예약 횟수" → filter field = totalReservations. When user filters by Korean "수업 진행 횟수/수업이 열린 횟수/수업 개설 횟수" → filter field = totalSessions. CRITICAL — "참여 횟수" DOES NOT MEAN totalSessions: in natural Korean, "참여" (participation) refers to a person attending the class, mapping to attendedCount (출석한 횟수). "수업이 N회 진행됐다" maps to totalSessions. Filtering attendedCount when user said "참여 횟수" gives 출석한 사람이 모인 횟수가 N회 이상인 수업, which is the user's actual intent. Filtering totalSessions instead returns "이 수업이 N회 이상 열렸나" which is a different question. [INTRO MUST CITE FILTER METRIC] After filter, intro/outro MUST explicitly state which metric was applied so the user can verify the axis (e.g. "출석 횟수 3회 이상인 수업 N건이에요" / "예약 건수 N건 이상인 수업 N건이에요" / "개설된 수업 횟수 N회 이상인 수업 N건이에요"). NEVER echo the user's original ambiguous phrasing ("수업별 참여 횟수 3회 이상") as the intro because that does NOT disclose which metric was filtered. [SHOW_DISPLAY COLUMNS] When filtering attendedCount, columns MUST include attendedCount as a primary column (label: "출석 횟수"). When filtering totalReservations, columns MUST include totalReservations (label: "예약 건수"). When filtering totalSessions, columns MUST include totalSessions (label: "수업 진행 횟수"). The filtered metric MUST be visible in the table so the user can verify the threshold check. Do NOT confuse with RESERVATION+STATUS_LIST's reserveCount — that field means per-session slot capacity (how many people CAN book one session), NOT per-member or per-class attendance count.
[LESSON EFFICIENCY / DISCONTINUE — MANDATORY SEQUENCE] When user asks about lesson efficiency or discontinuation candidates (Korean: 없애야 될 수업/폐강/수업 효율/정원 대비/비효율 수업/수업 정리): (1) After call_api, MUST call sort(field:"utilizationRate", order:"asc") BEFORE show_display. (2) show_display MUST include these columns: lessonName, teacherNames, totalSessions, avgAttendedPerSession, defaultMaxBookedPeople, utilizationRate. utilizationRate is MANDATORY — omitting it makes the answer meaningless because utilizationRate IS the "이유" (reason). Low utilizationRate = lesson fills very little of its capacity = discontinuation candidate. (3) Do NOT show only lessonName+totalSessions+totalReservations — that lacks the efficiency metric the user asked for.
[COMPARISON MODE] For multi-period coach/lesson comparison (Korean: N월 N월 코치별/수업별 출석 비교), call this intention once per period with the SAME groupBy param + slot names. matchKey varies by groupBy: coach → "coachName", lesson → "lessonName", day → "dayOfWeek", time → "timeSlot". show_display MUST show per-entity rows with each period's metric (e.g. "1월_totalReserveCount","2월_totalReserveCount","3월_totalReserveCount").
[DISPLAY COLUMNS — MANDATORY IDENTIFIER] show_display columns MUST always include the groupBy identifier field: lesson → lessonName, coach → teacherNames, day → dayOfWeek, time → timeSlot. Without the identifier, rows are just numbers — user cannot tell which entity each row belongs to. Minimum columns: identifier + totalSessions + totalReservations + attendedCount + attendanceRate + utilizationRate.
[COACH AXIS — HELD vs ATTENDED SESSIONS] When groupBy=coach, the table MUST include BOTH totalSessions (label "총 수업 횟수" = sessions held regardless of attendance marking) AND attendedSessions (label "출석 확인 수업 수" = distinct sessions with at least one recorded 출석/지각). These two diverge when a coach does not mark attendance (예약만 사용하는 경우) — attendedSessions can be 0 even though sessions were held. Intro MUST clarify both so the user knows which is the real coached count, e.g. Korean: "총 수업 횟수는 출석 처리와 무관하게 진행된 수업 수, 출석 확인 수업 수는 실제 출석이 기록된 수업 수예요". NEVER present totalLessons ("담당 수업 종류 수" = distinct assigned lesson TYPES, may include lessons with zero sessions this period) as the number of sessions a coach taught.
[COMPOUND QUERY — 코치 + 수업 IN SAME INPUT] Compound query trigger is NARROW — applies ONLY when input has an explicit conjunction "와/의/그/해당" linking coach-keyword + lesson-keyword (e.g. "코치와 해당 수업", "어떤 코치의 어떤 수업", "평균 수강 인원이 가장 높은 코치와 해당 수업", "N명 코치의 인기 수업") OR the interrogative pattern "누가 어떤 수업". In these cases ONLY, use groupBy:"lesson" ONLY (single call) — lesson rows already include teacherNames per row, so a single lesson-grouped display answers both dimensions. [NOT COMPOUND — DO NOT MISCLASSIFY] Korean "X별" suffix (별 = "per/by") indicates the PRIMARY grouping axis is X, and any other noun in the phrase is the metric being measured per-X. Patterns: "코치별 수업 성과/코치별 수업/코치별 출석/강사별 클래스 통계" → coach-axis grouping (groupBy="coach") — the user wants ONE row per coach with their lesson statistics aggregated. "수업별 코치/수업별 출석/수업별 통계" → lesson-axis grouping (groupBy="lesson") — ONE row per lesson with coach info per row. The "별" suffix wins: groupBy = whatever precedes 별. Containing BOTH 코치 AND 수업 keywords is NOT sufficient for compound — REQUIRED is explicit conjunction or interrogative+어떤 as listed above. NEVER call BOTH groupBy:"coach" AND groupBy:"lesson" in the same turn — that produces TWO show_display calls and the second one overwrites the first, dropping either lessonName or teacherNames from the visible answer. After show_display on lesson slot, IMMEDIATELY call finish — do NOT re-call call_api with a different groupBy as a "double-check"; one display per turn is the rule.
[POST-DISPLAY TERMINATION] After show_display succeeds (returns count > 0), call finish IMMEDIATELY. Do NOT call_api again with a different groupBy value to "verify" or "cross-check" — that overwrites the current display and confuses the user. One successful show_display per turn is the rule; the only exception is multi-period COMPARISON MODE (slots per period) which explicitly requires multiple call_api before a SINGLE final show_display.
[RATE COLUMN SEMANTICS — OUTRO DISCLOSURE RULE] Per-row rate columns (attendanceRate "예약 출석률(%)" / utilizationRate "정원 대비 출석률(%)") share the suffix "출석률(%)" but use DIFFERENT denominators — without disclosure the user cannot tell what each ratio means. Apply the appropriate outro pattern:
  • When BOTH attendanceRate AND utilizationRate appear in show_display columns → outro MUST include a brief formula disclosure right after listing the table content. Recommended template: "예약 출석률은 예약 대비 출석 비율(출석/예약), 정원 대비 출석률은 정원 대비 출석 비율(출석/정원)이야." This single sentence resolves the otherwise-opaque dual-rate ambiguity (especially when attendanceRate=100% across all rows because absentCount=0).
  • When ONLY attendanceRate appears → no formula disclosure required (the label "예약 출석률" is self-explanatory). But if all rows show 100% AND the user did not explicitly ask for "예약 출석률", outro SHOULD briefly note "예약 출석률은 예약 대비 출석 비율이라 결석이 없으면 100%로 동일하게 나와" to prevent the user from concluding the column is broken.
  • When ONLY utilizationRate appears (efficiency / discontinuation queries) → outro MUST cite the formula in context: "정원 대비 출석률은 정원(회차×기본정원) 대비 실제 출석 비율이야". Without this, the user sees percentages without knowing what 100% means (=정원 가득 채운 상태).
  • Coach axis (groupBy="coach") returns root scalar attendedReservationRatio per coach — same disclosure rule applies when both per-coach attendedReservationRatio and a capacity-derived rate appear together.
  • The disclosure sentence is metadata, NOT a number — do NOT wrap field values in placeholders here (the formula text itself is fixed). Cite ranked entities and their per-row rate values separately using items[i].<field>.
  • NEVER skip disclosure when both rate columns are visible — the user has no way to recover the formula from the table alone, and the dual rate often produces apparent contradictions (100% 예약 출석률 vs 28.3% 정원 대비 출석률 for the same row).`,
  'CHECK_SCHEDULE+RESERVATION+STATUS_LIST_BY+PERIOD': `[FIELD SEMANTICS] reserveCount = per-session slot capacity (how many people CAN book that session), NOT per-member attendance count. maxBookedPeople = maximum booking slots. This intention returns per-session individual records (each row = one reservation entry across all classes), there is NO attendedCount field per row. For aggregate "수업별 참여 횟수" (per-class participation count), use CHECK_RESERVE+ATTENDANCE_STATISTICS(groupBy:"lesson") instead — it provides totalSessions/attendedCount/totalReservations per lesson. Do NOT filter reserveCount as if it were attendance count.\n[FORBIDDEN ROUTE — AGGREGATE ATTENDANCE COUNT] NEVER use this intention to answer aggregate per-member attendance count queries (Korean: 총/평균/합계/최대/최소 + 출석 횟수). compute(count) on these per-session rows yields total reservation entries (not per-member attendance count) and compute(sum) has no attendedCount field to sum. The correct intention is CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD which returns per-member rows with attendedCount field — see Disambiguation rule for "총/평균/합계 출석 횟수".`,
  'CHECK_SALES+YEAR_STATISTICS': `[NEXT STEP] Annual sales statistics. items[] = individual transactions of the year — annual TOTAL revenue = {{SUM:totalPrice}} (refunds are negative rows, so the sum is net-correct). summary.totalMaxPrice is the ALL-TIME RECORD-HIGH MONTHLY revenue (chart crown baseline) — it is NOT the annual total; NEVER announce it as 연간/올해 총매출. Default → show_revenue_chart (monthly bar chart). Announce annual total via {{SUM:totalPrice}} in intro/outro. Use show_display only if user explicitly asks for 표/리스트/목록/list/table.`,
  'CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD': `[NEXT STEP] Monthly sales trend. summary has totalSales + averageMonthlySales. Default → show_revenue_chart (monthly bar chart). Announce {{SUMMARY:totalSales}} / {{SUMMARY:averageMonthlySales}} in intro/outro. Use show_display only if user explicitly asks for 표/리스트/목록/list/table.`,
  'CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD': `[NEXT STEP] Single summary row only — items[] has 1 aggregate row with grossRevenue/customerCount/averageRevenue scalars. NO per-member breakdown is available from any SALES intention. Use for KPI questions (객단가/평균 매출/1인당 매출이 얼마야). Announce {{SUMMARY:averageRevenue}} (1인당 평균 매출 = 객단가) as the headline together with {{SUMMARY:customerCount}}명 / {{SUMMARY:grossRevenue}}원.\n[PER-MEMBER FILTER DISCLOSURE — MANDATORY] If the user's input contains a per-member axis (Korean: 1인당/회원별/회원당/회원 1명당) AND a downstream filter/sort qualifier (Korean: 평균 이상/이하/N원 이상/이하/상위 N명/하위 N명/이상만/이하만), the answer MUST: (1) report the 객단가 + totals from this intention's summary using placeholders, AND (2) explicitly disclose the capability gap in finish() — "회원별 매출 합계 기준으로 '평균 이상만' 추리는 기능은 현재 제공되지 않아요" (or matching language). Do NOT silently drop the "이상만/이하만" filter intent. Do NOT call CHECK_SALES_STATISTICS_BY+PERIOD as a substitute — its raw transactions cannot be locally aggregated into per-member sums (aggregate(name) does not sum totalPrice; representative-row copy yields incorrect per-member figures). Do NOT show_display unless the user explicitly asked for "내역/목록/리스트". finish() is the correct response shape. NEVER attempt to filter/derive on items[] of THIS intention — items[] has only 1 row.`,
  'CHECK_SALES+PAYMENT+RATIO_STATISTICS_BY+PERIOD': `[NEXT STEP] Per-payment-method ratio statistics. Each row = one payment method (e.g. 카드/현금/링코페이) with its aggregated transactionCount + totalAmount + amountRate.\n[FORMULA DEFINITION — CRITICAL] The 총 거래 건수 (total transaction count) shown to the user MUST equal the visible sum of per-method transactionCount in the table (row-sum), NOT the backend summary.totalTransactionCount. Backend summary.totalTransactionCount can include refunded/canceled transactions and may differ from the row-sum (실제 결제 완료 합계). Citing summary 43 while the table sums to 38 produces a visible contradiction the user immediately catches.\n[INTRO PLACEHOLDER — STRICT] For 총 거래 건수/전체 거래 건수/총 결제 건수: MUST use {{SUM:transactionCount}} — this computes the row-sum of items[].transactionCount, deterministically matching the visible table. NEVER use {{SUMMARY:totalTransactionCount}} (backend metric, can mismatch row-sum) and NEVER use {{COUNT}} (row count = payment method type count, e.g. 3 — contradicts table's transactionCount column). For 총 거래 금액/전체 결제 금액: use {{SUM:totalAmount}} (row-sum of totalAmount), NOT {{SUMMARY:totalAmount}}.\n[OUTRO PER-METHOD CITATION — DETERMINISTIC] When citing each payment method in outro (Korean: "카드 N건/현금 N건/링코페이 N건"), MUST cite the EXACT items[i].transactionCount from the displayed table — NEVER use {{COUNT}} (row count), NEVER invent a number. Same for per-method 금액: cite items[i].totalAmount as displayed. Per-method 비율: cite items[i].amountRate.\n[SINGLE METHOD FILTER — Korean: "카드 비율/카드만/현금 비율"] If user asks for one payment method, prefer plain text answer using items[i].amountRate (e.g. "카드 비율은 56.1%야") — NO show_display needed for single-value answer. If show_display IS used with filter(paymentOptionName), the intro/outro 거래 건수 MUST cite items[0].transactionCount (the filtered row's actual count, e.g. 20 for 카드) — NEVER "총 거래 1건" (1 = row count after filter, not the method's transaction count, produces self-contradiction).\n[ANTI-CONTRADICTION] Forbidden patterns: (a) "<method> N건" where N differs from items[i].transactionCount; (b) "<method 금액>원 중 <total 금액>원 비율" mixing method amount with grand total; (c) summary totalTransactionCount applied (43 vs row-sum 38 = visible contradiction); (d) "총 거래 1건" after single-method filter (collides with row's transactionCount=20). If unsure of a value, OMIT the number; never approximate.\nAnalyze ratios as text.`,
  'CHECK_SALES+LINKOPAY_LIST_BY+PERIOD': `[NEXT STEP] LinkoPay payment history. For total aggregation, use compute(operation:"sum", field:"price").\n[REFUND] isRefund is boolean (true/false). Filter refunds: filter(field:"isRefund", condition:{type:"exact", value:true}). Summary already has totalRefundAmount and refundCount — use {{SUMMARY:totalRefundAmount}} for total refund amount. If 0 refund items, report "Korean: 환불 내역이 없습니다" with the summary value.`,
  'CHECK_PRODUCT+CATALOG_LIST': `[NEXT STEP] Product catalog list.`,
  'CHECK_PRODUCT+SALES_STATISTICS': `[NEXT STEP] Per-product sales ranking. summary has totalQuantity + totalRevenue. MUST call show_display with columns [rank, productName, unitPrice, totalQuantity, salesCount, totalRevenue] — NEVER enumerate individual products as text. Use {{COUNT}} for product count and {{SUMMARY:totalRevenue}} for total revenue in intro/outro.`,
  'CHECK_NOTICE+TOP+COMMENTED_STATISTICS': `[NEXT STEP] Top-commented notice ranking.`,
  'CHECK_SYSTEM+SETTING_DETAIL': `[NEXT STEP] Center (체육관/센터/지점) system settings AND center identity info. This intention IS the source for the gym's own name/phone/address: storeName(체육관명), storeTel(체육관 전화), storeAddress(체육관 주소) — plus reservation/holding policy settings.
[ROUTING] For questions about THIS center's name/phone/address/연락처 (Korean: 체육관 이름/우리 지점 전화번호/센터 주소/체육관 정보), use THIS intention — do NOT use get_guide (get_guide's CONTACT returns 링코 SERVICE 고객센터 번호, NOT this center's number). 링코 서비스 자체(고객센터/사용법)만 get_guide.
[ANSWER] Answer the setting item(s) the user asked as text. When the user explicitly asks for a table (Korean: 표로/표로 정리) of center info, show_display with the storeName/storeTel/storeAddress fields is acceptable; otherwise text is enough. Only state fields present in the data — if storeTel/storeAddress is empty, say it's not registered rather than inventing.`,
  'CHECK_COACH_LIST': `[NEXT STEP] Center coach (강사) list.`,
  'CHECK_PARTNER+GYM_LIST': `[NEXT STEP] Partner gym list. Distinguished by attendancePartners and exerciseSharePartners.`,
  'CHECK_WORKOUT+WOD_LIST_BY+PERIOD': `[NEXT STEP] WOD (workout-of-the-day) content by period. The wodContent field contains the full workout program text (section titles + exercises). Always include wodContent in show_display columns so the user sees the actual workout details.\n[EMPTY-RESULT HANDLING] If apiTotalCount=0 (no WOD registered for the requested period), finish DIRECTLY with a clear empty-WOD response (Korean: "오늘 WOD는 등록된 내역이 없어요" / "이번 주 등록된 WOD가 없어요"). The user's ask is WOD program CONTENT (exercises, sequences) — do NOT cascade to CHECK_WORKOUT+PARTICIPANT_LIST_BY+PERIOD as a fallback. Participants are a SEMANTICALLY DIFFERENT entity (people attending classes, not workout program content); drifting the finish to "운동 참가자도 없어" / "와드 참가자도 없어" answers a question the user did NOT ask. The original empty-WOD finding is itself the correct answer — do NOT speculatively load participant data to "fill" the response.\n[COMPARISON MODE] For multi-period WOD comparison (Korean: N월 N월 와드 비교), call this intention once per period with slot names. matchKey="lessonName" (or "title" for per-WOD). show_display MUST show per-WOD rows with each period's data. NEVER summarize to count-only.`,
  'CHECK_WORKOUT+PARTICIPANT_LIST_BY+PERIOD': `[NEXT STEP] Workout participant list. Each row = one participant attending one workout class (NOT a per-participant summary). The participantCount field is workout-level — the total number of participants in THAT class (denormalized across all rows of that class). Summing participantCount across rows of the same participant produces a meaningless number (mixes class size with attendance frequency).\n[METRIC SEMANTICS] For per-participant attendance frequency, use the row count per name (= number of classes that participant attended). After merge_slots(matchKey:"name"), this is auto-aggregated into the "count" field. Per-period column key is "{label}_count" (예: "1월_count","2월_count","3월_count"), label = "참가 횟수".\n[INVOKE GUARD — WOD VS PARTICIPANT DISTINCTION] Call this intention ONLY when the user's input explicitly mentions 참가자/참여자/참가 현황/참가 명단/참여 명단/운동 참가/수업 참가. Do NOT call this intention as a fallback after CHECK_WORKOUT+WOD_LIST_BY+PERIOD returns empty — WOD (workout program content) and PARTICIPANT (people attending classes) are SEMANTICALLY DIFFERENT entities answering DIFFERENT questions. Cascading WOD→PARTICIPANT for empty WOD drifts the response away from the user's original WOD-content ask.\n[COMPARISON MODE] For multi-period participant comparison (Korean: N월 N월 운동 참가자 비교), call this intention once per period with slot names → merge_slots({slots, matchKey:"name", mode:"outer"}) → show_display columns=["name","{label1}_count","{label2}_count",...] with columnLabels={"{label1}_count":"{label1} 참가 횟수", ...}. NEVER put {label}_participantCount in columns — that surfaces summed class-size, not attendance frequency. NEVER collapse to summary-only aggregate.`,
  'CHECK_WORKOUT+PARTNER+GYM_LIST_BY+PERIOD': `[NEXT STEP] Partner gym WOD sharing status.`,
  'CHECK_WORKOUT+PARTNER+GYM+WOD_LIST': `[NEXT STEP] Partner gym shared WOD list. Each row = one WOD from an external partner gym (storeName field identifies the source gym). show_display columns MUST include "storeName" (소속 지점) alongside ["title","lessonName","classDate"] — the user asked about PARTNER gym WOD (제휴점/파트너/공유), so the source gym identity is the primary disambiguator. Omitting storeName collapses multiple partner gyms into anonymous rows, which the user reads as "내 매장 WOD" and is intent-drift even with correct intention. Recommended columns=["storeName","title","lessonName","classDate"], columnLabels={"storeName":"소속 지점","title":"WOD 제목","lessonName":"수업명","classDate":"날짜"}.\n[INVOKE GUARD — PARTNER GYM WOD DISTINCTION] Call this intention (not CHECK_WORKOUT+WOD_LIST_BY+PERIOD) whenever the user's input mentions 제휴/제휴점/공유/파트너 alongside WOD/와드 (e.g. "제휴점 WOD 보여줘", "공유 WOD 알려줘", "파트너 체육관 와드"). The 제휴점 WOD entity is EXTERNAL partner gyms' shared WOD pool, NOT the own gym's per-period WOD program. Cascading own-gym WOD_LIST_BY+PERIOD with a "제휴점 WOD를 정리했어" intro is false-label intent drift — user reads "제휴점" label but data is own-gym, which mismatches the explicit 제휴/공유/파트너 ask.`,
};

// ============================================
// 인텐션별 availableFields 보강 — DB 덤프 (boolean enum 라벨 메타)
// ============================================
// 백엔드 ai_intention.available_fields JSON이 string("유효 여부")만 가질 때
// 클라이언트가 enum 라벨 메타({ label, enums })로 덮어쓰는 fallback dump.
// 운영 DB에 update_boolean_enum_labels.sql 적용 시 자동으로 동일 효과.
// 새 boolean 필드 추가 시 여기에 한 줄 추가하면 즉시 반영 (백엔드 배포 대기 없이).
//
// 도메인 의미 출처: 백엔드 DTO @ApiModelProperty 코멘트
// ============================================
export const AVAILABLE_FIELDS_OVERRIDES = {
  // [모델 A/B 검증 2026-07-02] totalMaxPrice 오라벨 — 라이브 DB available_fields가 "연간 총 매출"로 서빙 →
  //   "올해 총매출?"에 totalMaxPrice(역대 최고 월 매출, 12.5M)를 연총으로 오답. 실제 시맨틱은 StatisticsView/NewDataBar
  //   왕관("역대 최고 매출") 기준선. 연총 = items totalPrice 합({{SUM:totalPrice}}). DB id41 교정 전 라이브 라벨 override.
  'CHECK_SALES+YEAR_STATISTICS': {
    totalMaxPrice: '역대 최고 월 매출(연간 총매출 아님, 차트 기준선)',
  },
  'CHECK_MEMBER+ALL_LIST': {
    isActive: { label: '유효 여부', enums: { true: '유효', false: '만료' } },
    // [운영 5차 rid106] 가입경로 provider (link_coach_id 접두 파생)
    provider: { label: '가입경로', enums: { naver: '네이버', kakao: '카카오', google: '구글', apple: '애플', none: '없음' } },
    // [운영 5차 rid109] 국적 countryIso (KR=한국, 외국인=KR 아닌 값, NULL=미입력)
    countryIso: '국적(ISO 코드, KR=한국 · 값 없으면 미입력)',
  },
  'CHECK_MEMBER+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD': {
    reRegistration: { label: '재등록 여부', enums: { true: '재등록', false: '신규' } },
  },
  // 만료 후 재등록률(renewalRate) 신규 필드 — 백엔드 DB available_fields 반영 전 라이브 라벨 보강
  'CHECK_MEMBER+CHURN+RETENTION+RATE_STATISTICS_BY+PERIOD': {
    renewalRate: { label: '만료 후 재등록률(%) — 만료 건 기준', dimension: 'rate' },
    matureExpiredEvents: { label: '판정 완료 만료 건수(만료 후 30일 경과, 분모)', dimension: 'none' },
    renewedMatureEvents: { label: '판정 완료 건 중 30일 내 재등록 건수(분자)', dimension: 'none' },
    pendingObservationEvents: { label: '판정 대기 만료 건수(만료 후 30일 미경과)', dimension: 'none' },
    pendingRenewedSoFar: { label: '판정 대기 건 중 이미 재등록한 건수', dimension: 'none' },
    avgDaysToRenew: { label: '재등록 평균 소요일(일)', dimension: 'none' },
    earlyRenewalCount: { label: '조기 갱신 건수(만료 전 다음 회원권 시작)', dimension: 'none' },
  },
  'CHECK_SCHEDULE+LESSON_LIST_BY+PERIOD': {
    isCanceled: { label: '취소 여부', enums: { true: '취소됨', false: '정상' } },
  },
  'CHECK_SCHEDULE+SETTING_DETAIL_BY+PERIOD': {
    type: { label: '수업 유형', enums: { Offline: '오프라인', Online: '온라인' } },
    size: { label: '수업 규모', enums: { Group: '그룹', '1:1': '1:1' } },
    isCanceled: { label: '취소 여부', enums: { true: '취소됨', false: '정상' } },
    isHide: { label: '숨김 여부', enums: { true: '숨김', false: '노출' } },
  },
  'CHECK_SCHEDULE+CATALOG_LIST': {
    type: { label: '수업 유형', enums: { Offline: '오프라인', Online: '온라인' } },
    size: { label: '수업 규모', enums: { Group: '그룹', '1:1': '1:1' } },
    isHide: { label: '숨김 여부', enums: { true: '숨김', false: '노출' } },
    isDeleted: { label: '삭제 여부', enums: { true: '삭제됨', false: '존재' } },
  },
  'CHECK_LOCKER_DETAIL': {
    isExit: { label: '퇴장 여부', enums: { true: '퇴장', false: '사용중' } },
  },
  'CHECK_PARTNER+GYM_LIST': {
    storeName: '센터명',
    storeTel: '전화번호',
    storeAddress: '주소',
    openingHour: '운영시간',
    partnerType: '제휴 유형',
  },
  // [운영 5차 rid200] 체육관 기본정보(이름·전화·주소) — 백엔드 GET /api/system/ (aa47369)이 storeName/storeTel/storeAddress 반환하나
  //   DB available_fields={}라 LLM에 미노출 → 라이브 라벨 보강으로 즉시 surface. 백엔드 배포 대기 없이 반영(라이브 AgentSession:4725 + 테스트 nodeAgent 동일 맵 소비).
  'CHECK_SYSTEM+SETTING_DETAIL': {
    storeName: '체육관명',
    storeTel: '체육관 전화',
    storeAddress: '체육관 주소',
  },
  // [운영 5차 rid106] 가입경로(provider) — 백엔드 MemberListDTO(0827501)가 link_coach_id 접두 파생 provider(naver/kakao/google/apple/none) 반환.
  //   DB available_fields 미포함 → 라벨+enum 보강해 LLM이 filter(provider, exact) 가능하도록 surface. ALL_LIST는 위 항목에 병합.
  'CHECK_MEMBER+ALIVE_LIST': {
    provider: { label: '가입경로', enums: { naver: '네이버', kakao: '카카오', google: '구글', apple: '애플', none: '없음' } },
    // [운영 5차 rid109] 국적 countryIso(ISO 3166-1 alpha-2, KR=한국). 외국인=KR 아닌 값. NULL=미입력(플랫폼 ~2%만 입력)이므로 외국인 아님으로 단정 금지.
    countryIso: '국적(ISO 코드, KR=한국 · 값 없으면 미입력)',
  },
  'CHECK_MEMBER+EXPIRED_LIST_BY+PERIOD': {
    provider: { label: '가입경로', enums: { naver: '네이버', kakao: '카카오', google: '구글', apple: '애플', none: '없음' } },
    countryIso: '국적(ISO 코드, KR=한국 · 값 없으면 미입력)',
  },
};

export function buildComparisonNextGuide({ requestedPeriodCount, loadedCompareCount, hasBase }) {
  // hasBase: already in NEXT_MERGE state — caller should use TOOL_GUIDE_COMPARISON_NEXT_MERGE directly
  if (hasBase) return resolvePrompt('TOOL_GUIDE_COMPARISON_NEXT_MERGE', '');
  // N<3 or unknown period count → existing NEXT_BASE (2-period path)
  if (!Number.isFinite(requestedPeriodCount) || requestedPeriodCount < 3) {
    return resolvePrompt('TOOL_GUIDE_COMPARISON_NEXT_BASE', '');
  }
  // N>=3 path — calculate remaining compare slots needed
  const expectedCompareCount = requestedPeriodCount - 1; // 1 base + (N-1) compare
  const remaining = expectedCompareCount - loadedCompareCount;
  if (remaining <= 0) {
    // all compare slots filled — next is base
    return resolvePrompt('TOOL_GUIDE_COMPARISON_NEXT_BASE', '');
  }
  const nextSlotIdx = loadedCompareCount + 1;
  return `[NEXT — MULTI-PERIOD] User requested ${requestedPeriodCount}-period comparison. ${loadedCompareCount}/${expectedCompareCount} compare slots loaded. Next call_api MUST use slot:"compare_${nextSlotIdx}" (NOT "base"). After ALL ${expectedCompareCount} compare slots, load the final/latest period with slot:"base", then merge_slots. Do NOT skip remaining periods or shortcut to merge with fewer slots than requested.`;
}

export const LOCAL_INTENTIONS_DATA = {
  "meta": {
    "sourceTable": [
      "ai_intention",
      "ai_intention_category_rule"
    ],
    "dumpedAt": "2026-05-19",
    "intentionCount": 71,
    "ruleCount": 10,
    "note": "Local snapshot for fast iteration. Sync back to DB via syncIntentionsToDb.mjs after edits."
  },
  "intentions": [
    {
      "id": 1,
      "intention_key": "STYLE",
      "category": "GENERAL",
      "description": "Intention for changing conversation style/tone. Switches AI manager's speaking style such as casual, formal, English, emoji usage. LLM changes tone based on context without data retrieval.",
      "endpoint": null,
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW3",
      "params": null,
      "prior_endpoint": null,
      "available_fields": null,
      "call_api_guide": null
    },
    {
      "id": 2,
      "intention_key": "RECOMMEND",
      "category": "GENERAL",
      "description": "Intention for requesting strategic advice on center operations such as revenue improvement, member retention, marketing strategy. LLM generates creative suggestions based on knowledge without data retrieval.",
      "endpoint": null,
      "service": "zone",
      "creative": true,
      "next_flow": "FLOW3",
      "params": null,
      "prior_endpoint": null,
      "available_fields": null,
      "call_api_guide": null
    },
    {
      "id": 3,
      "intention_key": "FALLBACK",
      "category": "GENERAL",
      "description": "Intention for handling out-of-domain questions unrelated to fitness center management. Politely informs that weather, news, general knowledge queries are outside service scope.",
      "endpoint": null,
      "service": "zone",
      "creative": false,
      "next_flow": "DIRECT",
      "params": null,
      "prior_endpoint": null,
      "available_fields": null,
      "call_api_guide": null
    },
    {
      "id": 4,
      "intention_key": "CHECK_MEMBER_SEARCH",
      "category": "MEMBER",
      "description": "Search members by name or phone number and return candidate list. All MEMBER_DETAIL intentions use this as prior API to obtain memberId. Returns member list (candidates), not a specific member's detail. [PHONE SUFFIX — 운영 rid135] For phone-number lookups including PARTIAL/suffix digits (Korean: '4805로 끝나는 전화번호', '끝자리 0590', '뒷자리로 찾아줘'), put the digit string in the `phone` param — the backend matches phone by suffix (LIKE '%input'). Do NOT load a full member list and filter(phone, exact) — exact match on a partial number returns 0. Do NOT put digits in `name`.",
      "endpoint": "GET /api2/member/search",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "name",
          "type": "string",
          "required": false,
          "condition": "atLeastOne"
        },
        {
          "name": "phone",
          "type": "string",
          "format": "010XXXXXXXX",
          "required": false,
          "condition": "atLeastOne"
        }
      ],
      "prior_endpoint": null,
      "available_fields": {
        "name": "이름",
        "birthDay": "생년월일",
        "phone": "전화번호",
        "profile": "프로필 이미지"
      },
      "call_api_guide": "[NEXT STEP] From the search result, identify the member the user is looking for. If multiple members share the name (동명이인), show the list and ask the user to pick. If exactly 1 match, proceed straight to the next detail call."
    },
    {
      "id": 5,
      "intention_key": "CHECK_MEMBER+ALIVE_LIST",
      "category": "MEMBER",
      "description": "Retrieve ONLY currently-active members (with valid memberships). For ALL members regardless of status (active + expired + inactive — used for full-base age/gender aggregation), use CHECK_MEMBER+ALL_LIST instead. Includes name, gender, phone number, birthday, primary membership name, earliest membership start date (viewStartDate), latest membership end date (viewEndDate), and active status (isActive). Use this when user explicitly asks 'active/현재/유효회원' OR for birthday-person filter (이번 달 생일자 = active members with birthday in current month). For expired members use CHECK_MEMBER+EXPIRED_LIST.",
      "endpoint": "GET /api2/member/list?status=alive",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "status",
          "type": "string",
          "fixed": "alive"
        }
      ],
      "prior_endpoint": null,
      "available_fields": {
        "memberId": "회원 PK(long)",
        "userId": "링코 ID(linkCoachId)",
        "name": "이름",
        "gender": "성별",
        "birthDay": "생년월일",
        "phone": "전화번호",
        "viewMembershipName": "대표 회원권명",
        "viewStartDate": "회원권 시작일",
        "viewEndDate": "회원권 만료일",
        "viewCount": "회원권 잔여 이용 횟수",
        "createdDate": "등록일"
      },
      "call_api_guide": "[NEXT STEP] This is the full active member list.\n[BARE LIST GUARD — DEFAULT NO FILTER] If user's original request is a BARE active-member list query (Korean: 유효회원/활성회원/현재 회원/회원 목록/회원 보여줘/회원 알려줘/회원 명단 — NO additional qualifier), call show_display IMMEDIATELY with the FULL list. DO NOT apply any filter. The implied filters below ONLY trigger when the user's ORIGINAL INPUT contains the EXACT explicit Korean keyword listed.\n[EXPLICIT-KEYWORD-ONLY IMPLIED FILTERS] Apply a filter ONLY if the user's input literally contains one of these keywords:\n- ONLY if input contains Korean: \"생일자\" OR \"생일 회원\" → filter({ field: \"birthDay\", condition: { type: \"birthday_month\", month: (current month) } }). Default to current month if no time specified.\n- ONLY if input contains Korean: \"20대\"/\"30대\"/\"40대\"/\"50대\"/\"N세 이상\"/\"N세 이하\" → filter({ field: \"birthDay\", compute: \"age\", condition: { type: \"range\", min/max } })\n- ONLY if input contains Korean: \"여성\"/\"여자\"/\"남성\"/\"남자\" → filter({ field: \"gender\", condition: { type: \"exact\", value: ... } })\n- ONLY if input contains Korean: \"만료 임박\"/\"만료 예정\"/\"곧 만료\" → filter viewEndDate with date_range\n- ONLY if input contains Korean: \"출석 N회 이상/이하\" → WRONG CATEGORY. This data does NOT have attendedCount. You need SCHEDULE category (CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD). Call get_intentions(SCHEDULE) → call_api.\n[NO INFERENCE] NEVER infer an implied filter from the intention name alone (ALIVE_LIST does NOT mean birthday filter). NEVER assume the user wants the current month subset. NEVER apply any filter the user did not literally ask for. If none of the explicit keywords above are present in the user's original input, the correct action is: show_display with the FULL list. Do NOT ask the user to clarify."
    },
    {
      "id": 6,
      "intention_key": "CHECK_MEMBER+EXPIRED_LIST",
      "category": "MEMBER",
      "description": "Retrieves a list of inactive members whose membership has expired or who hold no active membership. Use for requests like \"churned members\", \"expired members\", \"inactive members\", or any request for a list of people who are no longer active. Includes name, gender, phone number, last membership name, earliest membership start date (viewStartDate), latest membership end date (viewEndDate), and active status (isActive) if user ask \"이번달 이탈 회원 알려줘\", choose this intention.",
      "endpoint": "GET /api2/member/list?status=expired",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "status",
          "type": "string",
          "fixed": "expired"
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "name": "이름",
        "gender": "성별",
        "birthDay": "생년월일",
        "phone": "전화번호",
        "viewMembershipName": "마지막 회원권명",
        "viewStartDate": "회원권 시작일",
        "viewEndDate": "회원권 만료일",
        "createdDate": "등록일"
      },
      "call_api_guide": "[NEXT STEP] This is the expired member list for the requested period. The buffer items ARE the expired members directly (name/gender/birthDay/phone/viewMembershipName/viewStartDate/viewEndDate/createdDate) — no usages array, so show_member_status is NOT applicable here. If user specified a time period (\"이번달\", \"지난달\", \"최근 3개월\", etc.), filter by viewEndDate with date_range condition IMMEDIATELY. Example: \"이번달 이탈 회원\" → filter({ field: \"viewEndDate\", condition: { type: \"date_range\", start: \"2026-04-01\", end: \"2026-04-30\" } }). Apply any other implied filters (age group / gender / expired duration), then use show_display({intro, columns: [\"name\", \"viewMembershipName\", \"viewEndDate\", \"phone\"]}) to render the list. (show_member_status requires CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST buffer with usages array for new/reregistered/churned classification — that is a separate workflow, not applicable to this intention.)"
    },
    {
      "id": 7,
      "intention_key": "CHECK_MEMBER+HOLDING_LIST",
      "category": "MEMBER",
      "description": "Retrieve members with membership on hold/paused/frozen. 이용권 동결, 일시정지, 홀딩 중인 회원. NOT membership application or signup. item_template MUST be: \"- {memberName} | {holdingMembers.startDate} ~ {holdingMembers.endDate} | {holdingMembers.reason}\"",
      "endpoint": "GET /api2/member/holding",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "available_fields": {
        "memberName": "회원명",
        "phone": "전화번호",
        "membershipName": "회원권명",
        "startDate": "홀딩 시작일",
        "endDate": "홀딩 종료일",
        "reason": "홀딩 사유",
        "remainingDays": "홀딩 잔여일"
      },
      "call_api_guide": "[NEXT STEP] This is the holding member list. Check user's original request for implied filters (e.g. holding duration, specific membership). Apply filter if needed, then call show_display. Do NOT ask the user to clarify."
    },
    {
      "id": 8,
      "intention_key": "CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD",
      "category": "MEMBER",
      "description": "Retrieve members whose memberships expire within specified period. Identifies members with upcoming expiry for renewal outreach. Use for month-specific queries (Korean: 3월에 만료되는/5월 만료 — set startDate/endDate to that month range) and sort-by-expiry queries (Korean: 만료일 빠른 순 — sort remainingDays asc after load). Also used when user asks about churn-risk members (Korean: 이탈 위험 회원 — treat as members expiring within 2 weeks). Distinct from CHECK_MEMBERSHIP+CATALOG_LIST which lists membership product types (NOT member status). For already expired members use CHECK_MEMBER+EXPIRED_LIST.",
      "endpoint": "GET /api2/member/expiring",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "today", "end": "+14d" },
      "available_fields": {
        "memberName": "회원명",
        "phone": "전화번호",
        "memberShipName": "회원권명",
        "startDate": "회원권 시작일",
        "endDate": "회원권 만료일",
        "remainingDays": "잔여일"
      },
      "call_api_guide": "[NEXT STEP] This is the list of members expiring within the specified period.\n[DATE RANGE POST-FILTER — MANDATORY] Backend returns ALL expiring members regardless of startDate/endDate params. If user specified a month or date range (Korean: N월에 만료/N월 만료/N월~M월 만료), you MUST apply filter({ field: \"endDate\", condition: { type: \"date_range\", startDate: \"YYYY-MM-01\", endDate: \"YYYY-MM-DD\" } }) IMMEDIATELY after call_api to keep only members whose endDate falls within the requested range. Without this filter, members expiring in other months will be incorrectly included.\nApply any additional filter if user implied (e.g. specific membership, gender). Then call show_display. Do NOT ask the user to clarify."
    },
    {
      "id": 9,
      "intention_key": "CHECK_MEMBER+OUTSTANDING_LIST",
      "category": "MEMBER",
      "description": "Retrieve members with outstanding balances (unpaid amounts). Includes outstanding amount and reason per member. Used for payment follow-up and management.",
      "endpoint": "GET /api2/member/outstanding",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "memberName": "회원명",
        "phone": "전화번호",
        "itemName": "미수금 항목명",
        "notReceivedPrice": "미수금 금액",
        "approvalDate": "결제 승인일"
      },
      "call_api_guide": null
    },
    {
      "id": 10,
      "intention_key": "CHECK_NEW+MEMBER_LIST_BY+PERIOD",
      "category": "MEMBER",
      "description": "Retrieve a list of new members whose first membership usage started within the specified period. A member is classified as new if their earliest member_ship_usage.startDate falls within the period — this matches the statistics page new-member classification. Members who have never registered any membership usage are excluded. Includes member name, phone number, first membership name, and first membership start date.",
      "endpoint": "GET /api2/member/new-registrations",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "memberId": "회원 PK(long)",
        "userId": "링코 ID(linkCoachId)",
        "name": "이름",
        "phone": "전화번호",
        "memberShipName": "등록 회원권명",
        "registrationDate": "등록일"
      },
      "call_api_guide": "[NEXT STEP] This is the new registration list for the requested period. The buffer items ARE the new members directly (memberId/name/phone/memberShipName/registrationDate) — no usages array, so show_member_status is NOT applicable here. Use show_display({intro, columns: [\"name\", \"registrationDate\", \"memberShipName\", \"phone\"]}) to render the list directly. Check user's original request for any implied filters (gender/membership/age range) and apply filter() before show_display if needed. (show_member_status requires CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST buffer with usages array for new/reregistered/churned classification — that is a separate workflow, not applicable to this intention.)"
    },
    {
      "id": 11,
      "intention_key": "CHECK_MEMBER+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD",
      "category": "MEMBER",
      "description": "Re-registration member statistics by period: among PURCHASERS in the period, ratio of returning buyers (members with purchase history before the period) vs new members. Buyer composition statistics — NOT expiry-based. For renewal rate after membership expiry (만료 후 재등록률/갱신율) use CHECK_MEMBER+CHURN+RETENTION+RATE_STATISTICS_BY+PERIOD. For membership-level re-registration rate use CHECK_MEMBERSHIP+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD.",
      "endpoint": "GET /api2/member/re-registration-rate",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "name": "이름",
        "phone": "전화번호",
        "reRegistration": "재등록 여부",
        "memberShipName": "이번 구매 회원권",
        "purchaseDate": "이번 구매일",
        "prevMemberShipName": "이전 회원권",
        "prevPurchaseDate": "이전 구매일",
        "startDate": "조회 시작일",
        "endDate": "조회 종료일",
        "totalPurchaseMembers": "총 구매 회원수",
        "newMembers": "신규 회원수",
        "reRegistrationMembers": "재등록 회원수",
        "reRegistrationRate": "재등록률(%)"
      },
      "call_api_guide": "[NEXT STEP] summary holds overall re-registration rate (reRegistrationRate) and total purchaser count (totalPurchaseMembers). Announce the overall re-registration rate in text first; call show_display if the user needs the per-member list. reRegistration=true means re-registration, false means new."
    },
    {
      "id": 12,
      "intention_key": "CHECK_MEMBER+CHURN+RETENTION+RATE_STATISTICS_BY+PERIOD",
      "category": "MEMBER",
      "primaryRateKey": "renewalRate",
      "description": "Member renewal rate after expiry (만료 후 재등록률/갱신율) + churn/retention rate statistics by period. renewalRate = ratio of expired membership EVENTS renewed within 30 days (per-expiry; denominator counts only events past the 30-day observation window). Also returns churnRate (이탈률) / retentionRate (유지율) for member lifecycle analysis. Note: if requesting a list of churned/expired members rather than rates or statistics, use CHECK_MEMBER+EXPIRED_LIST instead.",
      "endpoint": "GET /api2/member/churn-retention-rate",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "activeMembersAtStart": {
          "label": "기간 시작 활성 회원수",
          "dimension": "people"
        },
        "activeMembersAtEnd": {
          "label": "기간 종료 활성 회원수",
          "dimension": "people"
        },
        "expiredMembers": {
          "label": "기간 내 만료 회원수",
          "dimension": "people"
        },
        "churnedMembers": {
          "label": "이탈 회원수",
          "dimension": "people"
        },
        "renewedMembers": {
          "label": "갱신 회원수",
          "dimension": "people"
        },
        "newMembers": {
          "label": "신규 회원수",
          "dimension": "people"
        },
        "churnRate": {
          "label": "이탈률(%)",
          "dimension": "rate"
        },
        "retentionRate": {
          "label": "유지률(%)",
          "dimension": "rate"
        },
        "renewalRate": {
          "label": "만료 후 재등록률(%) — 만료 건 기준",
          "dimension": "rate"
        },
        "matureExpiredEvents": {
          "label": "판정 완료 만료 건수(만료 후 30일 경과, 분모)",
          "dimension": "none"
        },
        "renewedMatureEvents": {
          "label": "판정 완료 건 중 30일 내 재등록 건수(분자)",
          "dimension": "none"
        },
        "pendingObservationEvents": {
          "label": "판정 대기 만료 건수(만료 후 30일 미경과)",
          "dimension": "none"
        },
        "pendingRenewedSoFar": {
          "label": "판정 대기 건 중 이미 재등록한 건수",
          "dimension": "none"
        },
        "avgDaysToRenew": {
          "label": "재등록 평균 소요일(일)",
          "dimension": "none"
        },
        "earlyRenewalCount": {
          "label": "조기 갱신 건수(만료 전 다음 회원권 시작)",
          "dimension": "none"
        },
        "startDate": {
          "label": "조회 시작일",
          "dimension": "none"
        },
        "endDate": {
          "label": "조회 종료일",
          "dimension": "none"
        },
        "totalMemberPool": {
          "label": "전체 회원 모수",
          "dimension": "people"
        }
      },
      "call_api_guide": "[NEXT STEP] Aggregate summary statistics (no per-member list) — answer in text, show_display not needed. METRIC SELECTION (MANDATORY): headline ONLY the metric the user asked. '만료 후 재등록률/갱신율/재등록 비율' → renewalRate. '이탈률/유지율' → churnRate/retentionRate. churnRate≠renewalRate — denominators differ (renewalRate: % of expiry EVENTS renewed within 30 days, denominator = matureExpiredEvents = events past the 30-day observation window only; churn/retention: member-pool based = totalMemberPool). PHRASING: renewalRate is per-EVENT — say '만료 건의 X%' / '만료 건 기준 X%', NEVER '만료 회원의 X%'. ZERO DENOMINATOR: if matureExpiredEvents=0, do NOT cite renewalRate 0% — say no expiry has completed the 30-day observation yet and report progress using pendingObservationEvents(N)/pendingRenewedSoFar(M) (예: '아직 판정 가능한 만료 건이 없어. 진행 중 N건 중 M건은 벌써 재등록했어'). PENDING CAVEAT: when citing renewalRate while pendingObservationEvents>0, append one short line that N recent expiries are still within their 30-day window. FOLLOW-UP '재등록까지 평균 며칠/얼마나 걸려' → avgDaysToRenew (avg days, renewals within 0~30d only; earlyRenewalCount = renewals started before expiry, excluded from the average)."
    },
    {
      "id": 13,
      "intention_key": "CHECK_MEMBER+PROFILE_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's full profile. Returns name, phone, birth date, gender, join date, profile photo, memo. Requires prior CHECK_MEMBER_SEARCH. Default MEMBER_DETAIL fallback — when user mentions a specific member name but context is unclear (no specific info type like 회원권/출석/포인트 mentioned), pick this as the most comprehensive option.",
      "endpoint": "GET /api2/member/detail/{member_id}",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "name": "이름",
        "gender": "성별",
        "phone": "전화번호",
        "birthDay": "생년월일",
        "nickName": "닉네임",
        "memberDetail": "회원 메모"
      },
      "call_api_guide": "[NEXT STEP] Profile info of one member. Summarize in text. No need to call show_display."
    },
    {
      "id": 14,
      "intention_key": "CHECK_MEMBER+MEMBERSHIP_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's membership details. Backend may return two response forms depending on the member's primary record: (A) 회원권 본체 with fields [memberShipName, categoryName, startDate, endDate, totalPrice, status, courseTakeCount, totalMax, validity, approvalDate]; (B) 회원권 일시정지(holding) records with fields [holdingId, usageId, startDate, endDate, reason, plusTotalMax]. Inspect items[0] keys before selecting show_display columns. If form (B), refer to the data as '회원권 일시정지 내역' (NOT '회원권 내역'). Requires prior member search.",
      "endpoint": "GET /api2/member/detail/membership/{member_id}",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "memberShipName": "회원권명",
        "categoryName": "카테고리",
        "startDate": "시작일",
        "endDate": "만료일",
        "totalPrice": "총 금액",
        "status": { "label": "상태", "enums": { "1": "활성", "2": "만료", "3": "홀딩", "4": "환불" } },
        "courseTakeCount": "수강 횟수",
        "totalMax": { "label": "총 횟수", "enums": { "-1": "무제한" } },
        "validity": "유효기간(일)",
        "approvalDate": "승인일",
        "holdingId": "일시정지 ID",
        "usageId": "회원권 사용 ID",
        "reason": "일시정지 사유",
        "plusTotalMax": "추가 횟수"
      },
      "call_api_guide": "[NEXT STEP] Member's membership detail. Two possible forms — inspect items[0] keys FIRST before responding. (A) 회원권 본체 — items[0] has memberShipName/categoryName/totalPrice/status. Refer to data as '회원권 내역' or '회원권 정보'. status enum: 1=valid/2=expired/3=holding/4=refunded. show_display acceptable for multiple entries. (B) 회원권 일시정지(holding) — items[0] has holdingId or reason or plusTotalMax (회원권 본체 fields 부재). MUST label as '회원권 일시정지 내역' in EVERY user-facing text — show_display intro, outro, finish text. NEVER label form (B) data as '회원권 내역', '회원권 정보', '회원권 상세', or '회원권 기록' — those generic labels imply form (A) membership records and mislead users into thinking they see active membership data when they actually see holding/pause records. Example for form (B): WRONG: '정지원 회원권 내역 16건' / '회원권 정보입니다' / CORRECT: '정지원 회원의 회원권 일시정지 내역 16건' / '회원권 일시정지 내역을 확인했어요'. show_display columns for form (B) must be [startDate, endDate, reason, plusTotalMax] only (do NOT include form A columns).\n[STATUS VALIDATION] Backend may return status=1 for memberships whose endDate has already passed — the system auto-corrects these to status=2 (expired). Trust the status value in the data as-is. NEVER label an item as active/valid based on status alone — always verify endDate >= today.\n[SHOW_DISPLAY RULE] For form (A), do NOT pass columns argument — omit it to let auto-selection include memberShipName + startDate + endDate + status. If you explicitly set columns, MUST include memberShipName — without it user cannot distinguish rows. Omitting memberShipName is a critical info-loss bug."
    },
    {
      "id": 15,
      "intention_key": "CHECK_MEMBER+LESSON+PARTICIPATION_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's class participation history. Specify member name. Returns attended classes, dates, rates. Use for '회원 수강 이력'. NOT coach/instructor statistics.",
      "endpoint": "GET /api2/member/{memberId}/participation",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "lessonName": "수업명",
        "scheduleTime": "수업 시간",
        "reservationCount": "예약 횟수",
        "attendedCount": "출석 횟수",
        "absentCount": "결석 횟수",
        "attendanceRate": "출석률(%)",
        "lastVisit": "마지막 방문일"
      },
      "call_api_guide": "[NEXT STEP] Member's per-lesson participation history. Separate the overall attendance summary (summary) from per-lesson detail in your answer. If there are multiple lessons, show them via show_display."
    },
    {
      "id": 16,
      "intention_key": "CHECK_MEMBER+PRODUCT_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's product purchase history. Returns product names, prices, purchase dates, quantities. For overall product sales statistics use PRODUCT category.",
      "endpoint": "GET /api2/member/detail/product/{member_id}",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "itemName": "상품명",
        "totalPrice": "총 금액",
        "paymentOptionName": "결제수단",
        "paymentPrice": "결제금액"
      },
      "call_api_guide": "[NEXT STEP] Member's product purchase history. Answer in text or via show_display."
    },
    {
      "id": 17,
      "intention_key": "CHECK_MEMBER+LOCKER_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's locker info by member name/phone. Use when member name is specified (e.g. '김링크 락커'). NOT by locker number.",
      "endpoint": "GET /api2/locker/{member_id}",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "lockerName": "락커명",
        "state": "상태",
        "startDate": "시작일",
        "endDate": "종료일",
        "scheduledEndDate": "종료 예정일",
        "cardPrice": "카드 결제액",
        "cashPrice": "현금 결제액"
      },
      "call_api_guide": "[NEXT STEP] Member's locker assignment info. Distinguish current locker vs past history in text."
    },
    {
      "id": 18,
      "intention_key": "CHECK_MEMBER+POINT_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's point balance and earn/spend history. Use when member name is specified (e.g. '김링크 포인트'). NOT store-wide ranking.",
      "endpoint": "GET /api2/member/detail/point/{member_id}",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "reason": "사유",
        "approvalDate": "날짜",
        "point": "포인트"
      },
      "call_api_guide": "[NEXT STEP] Member's point history. summary.totalPoint is the current balance. Announce the balance first, then call show_display if the detailed history is needed."
    },
    {
      "id": 19,
      "intention_key": "CHECK_MEMBER+MEMBERSHIP+HOLDING_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's membership hold (pause) history. Returns hold start/end dates, reason, membership name. For all holding members list use CHECK_MEMBER+HOLDING_LIST.",
      "endpoint": "GET /api2/member/{member_id}/holding",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "memberShipName": "회원권명",
        "startDate": "홀딩 시작일",
        "endDate": "홀딩 종료일",
        "holdingDays": "홀딩 일수",
        "reason": "사유"
      },
      "call_api_guide": "[NEXT STEP] Member's holding (temporary pause) history. Summarize in text."
    },
    {
      "id": 20,
      "intention_key": "CHECK_MEMBER+MEMBERSHIP+EXTEND_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's membership extension history. Returns extension dates, extended days, reason, membership name.",
      "endpoint": "GET /api2/member/{member_id}/extends",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "memberShipName": "회원권명",
        "startDate": "연장 시작일",
        "endDate": "연장 종료일",
        "extendDays": "연장 일수",
        "reason": "사유"
      },
      "call_api_guide": "[NEXT STEP] Member's membership extension history. Summarize in text."
    },
    {
      "id": 21,
      "intention_key": "CHECK_MEMBER+REFUND_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's refund history. Returns refund amount, date, reason, membership name.",
      "endpoint": "GET /api2/member/{member_id}/refund",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "itemName": "항목명",
        "refundPrice": "환불 금액",
        "reason": "사유",
        "applicationDate": "신청일",
        "approvalDate": "승인일",
        "status": "상태"
      },
      "call_api_guide": "[NEXT STEP] Member's refund history. summary.totalRefundPrice is the total refund amount. Summarize in text."
    },
    {
      "id": 22,
      "intention_key": "CHECK_MEMBER+CARD_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's registered payment card info. Returns card issuer, partial card number, registration date.",
      "endpoint": "GET /api2/member/{member_id}/card",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "memberName": "회원명",
        "hasCard": "카드 등록 여부",
        "registeredDate": "등록일"
      },
      "call_api_guide": "[NEXT STEP] Member's card registration status. Answer in text. No need to call show_display."
    },
    {
      "id": 23,
      "intention_key": "CHECK_MEMBER+POINT_LIST",
      "category": "MEMBER",
      "description": "Retrieve store-wide member point balance ranking/list. All/active/expired members' point balances sorted by amount. Use for '포인트 순위', '전체 회원 포인트 목록'. NOT one specific member.",
      "endpoint": "GET /api2/point/list/{division}",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "division",
          "type": "string",
          "required": true
        }
      ],
      "prior_endpoint": null,
      "available_fields": {
        "name": "이름",
        "phone": "전화번호",
        "point": "포인트 잔액"
      },
      "call_api_guide": null
    },
    {
      "id": 24,
      "intention_key": "CHECK_MEMBERSHIP+APPLICATION_LIST",
      "category": "MEMBERSHIP",
      "description": "Retrieve members pending NEW membership signup approval. 신규 가입 신청 대기, 승인 전 회원. NOT holding/pause/freeze. NOT 만료 예정/만료 임박 — for that use CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD.",
      "endpoint": "GET /api2/member/waiting",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "userName": "회원명",
        "phone": "전화번호",
        "itemName": "신청 회원권",
        "gender": "성별",
        "birthDay": "생년월일",
        "approval": "승인 상태",
        "createdDate": "신청일"
      },
      "call_api_guide": "[NEXT STEP] Membership signup application list. approval: 0=pending, 1=approved, 2=rejected.\n[DATA SHAPE — MIXED STATUS] API returns ALL applications regardless of approval status (mixed: pending + approved + rejected coexist). DO NOT assume rows are pending-only. Inspect items[].approval distribution before show_display — if no row has approval=0 you cannot honestly say \"승인 대기 중\".\n[DEFAULT PATH — NO STATUS KEYWORD IN USER INPUT] If user input is generic (Korean: \"신규 신청 목록/가입 신청 목록/신청자 목록/신청 명단/신청 회원\" without 대기/승인/거절/반려/미승인 status qualifier), do NOT filter. Call show_display directly on all items. intro MUST be neutral: \"신규 신청 목록 {{COUNT}}건이야\" / \"가입 신청 목록 {{COUNT}}건이야\" / \"신청자 {{COUNT}}명이야\" — strictly no status word (대기/승인/거절/반려/미승인) in intro or outro.\n[STATUS PATH — USER INPUT CONTAINS STATUS KEYWORD] Only when user input explicitly contains a status keyword, apply filter BEFORE show_display:\n  - \"승인 대기 / 대기 회원 / 대기 명단 / 대기 중인 / 승인 안 된 / 미승인\" → filter(field:\"approval\", condition:{type:\"exact\", value:0})\n  - \"승인된 / 승인 완료 / 가입 승인 (NOT just \\\"승인 대기\\\")\" → filter(field:\"approval\", condition:{type:\"exact\", value:1})\n  - \"거절된 / 반려된 / 거절 명단\" → filter(field:\"approval\", condition:{type:\"exact\", value:2})\n  After filter:\n  - items.length=0 → finish with honest text matching the requested status AND include total application count + other-status distribution context so user understands why filter returned 0 (e.g. \"전체 신청 N건 중 승인 대기는 없어. 모두 승인 완료(M건)/거절(K건) 상태야\" — derive M/K from filter tool message \"실제 데이터 값: [...]\" hint and pre-filter buffer count). DO NOT call show_display, DO NOT show all rows as substitute. Mandatory: cite the pre-filter total count from the call_api result message (\"N건 조회 완료\") so the user does not assume the API returned nothing.\n  - items.length>0 → show_display + intro: \"승인 대기 신청 {{COUNT}}건이야\" / \"거절된 신청 {{COUNT}}건이야\" using the filtered count.\n[INTRO RULE — STATUS HONESTY] Critical rule: if you did NOT call filter on approval, intro/outro MUST NOT contain status words (대기/승인 대기/대기 중/거절/반려/미승인). Putting \"대기\" in intro while displaying rows with approval=2(거절) creates visible self-contradiction the user immediately catches.\n[STATUS COLUMN MANDATORY] show_display MUST include the approval column so the user can verify each row's status. NEVER omit approval."
    },
    {
      "id": 25,
      "intention_key": "CHECK_MEMBERSHIP+SUBSCRIPTION+CANCELLATION_LIST_BY+PERIOD",
      "category": "MEMBERSHIP",
      "description": "Retrieve members who canceled subscriptions (auto-payment) within specified period. Returns member names, cancellation dates, reasons, membership names. Used for membership lifecycle management.",
      "endpoint": "GET /api2/member/subscription-canceled",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "totalCount": "해지 회원 수",
        "startDate": "조회 시작일",
        "endDate": "조회 종료일",
        "memberName": "회원명",
        "phone": "전화번호",
        "members.canceledSubscriptions.membershipName": "회원권명",
        "members.canceledSubscriptions.terminationReason": "해지 사유",
        "members.canceledSubscriptions.canceledDate": "해지일"
      },
      "call_api_guide": null
    },
    {
      "id": 26,
      "intention_key": "CHECK_MEMBERSHIP+CATALOG_LIST",
      "category": "MEMBERSHIP",
      "description": "Retrieve all membership types (catalog) registered at the center. Returns membership names, durations, prices, types (period-based/count-based). This is a PRODUCT catalog (membership product definitions, ONE row per membership type), NOT a member list. For member-level pre-expire status (Korean: 만료 예정/만료되는/만료일/N월 만료) use CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD instead. For per-member distribution by membership (Korean: 회원권별 인원/회원권별 분포/회원권별 회원 수/회원권 분포/회원권 구성/회원권별 통계) DO NOT use this intention — the catalog has no per-member count dimension; route to CHECK_MEMBER+ALIVE_LIST + compute(group, viewMembershipName) instead.",
      "endpoint": "GET /api2/membership/catalog",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "categoryName": "카테고리",
        "memberShipName": "회원권명",
        "totalPrice": "가격",
        "memberShipDuration": "기간(일)",
        "totalCount": "총 횟수"
      },
      "call_api_guide": "[NEXT STEP] Center membership catalog (product list). Reference/lookup data."
    },
    {
      "id": 27,
      "intention_key": "CHECK_MEMBERSHIP+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD",
      "category": "MEMBERSHIP",
      "description": "Retrieve membership-level re-registration rate statistics by period (BASE intention for per-membership rate breakdown). Returns ALL membership products with reRegistrationRate + newCount + reRegistrationCount + totalRevenue + averageRevenuePerMember columns per row, plus summary.totalReRegistrationRate as overall headline. DEFAULT MATCH for bare per-membership re-registration rate questions: Korean '회원권별 재등록률 보여줘/알려줘/확인해줘', '재등록률 통계', '재등록률 추이' (NO explicit ranking keyword). For RANKING/TOP variant (Korean: '재등록률 순위/랭킹/상위/높은 회원권/Top') use CHECK_MEMBERSHIP+TOP+RE+REGISTRATION_STATISTICS_BY+PERIOD instead — that returns pre-sorted ranking with rank column but drops newCount/totalRevenue. For member-level re-registration rate (per-individual, not per-product) use MEMBER category CHECK_MEMBER+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD.",
      "endpoint": "GET /api2/membership/re-registration-rate",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "default_sort": { "field": "reRegistrationRate", "direction": "desc", "secondary": "reRegistrationCount" },
      "available_fields": {
        "memberShipName": "회원권명",
        "purchaseCount": "구매 건수",
        "newCount": "신규 건수",
        "reRegistrationCount": "재등록 건수",
        "reRegistrationRate": "재등록률(%)",
        "totalRevenue": "총 매출",
        "averageRevenuePerMember": "회원당 평균 매출",
        "startDate": "조회 시작일",
        "endDate": "조회 종료일",
        "totalMemberShipCount": "총 회원권 수",
        "totalPurchaseMembers": "총 구매 회원수",
        "totalNewMembers": "신규 회원수",
        "totalReRegistrationMembers": "재등록 회원수",
        "totalReRegistrationRate": "재등록률(%)",
        "totalAverageRevenuePerMember": "회원당 평균 매출"
      },
      "call_api_guide": "[NEXT STEP] Per-membership re-registration rate. summary holds the overall re-registration rate (totalReRegistrationRate). Summarize the overall in text."
    },
    {
      "id": 28,
      "intention_key": "CHECK_MEMBERSHIP+TERM+RATIO_STATISTICS_BY+PERIOD",
      "category": "MEMBERSHIP",
      "description": "Retrieve long-term vs short-term membership enrollment ratio statistics by period. Analyzes enrollment counts and ratios by duration type (1/3/6/12 months).",
      "endpoint": "GET /api2/membership/term-ratio",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "memberShipName": "회원권명",
        "validity": "유효기간(일)",
        "durationType": "유형(장기/단기)",
        "purchaseCount": "구매 건수",
        "purchaseRate": "구매 비율(%)",
        "totalRevenue": "총 매출",
        "startDate": "조회 시작일",
        "endDate": "조회 종료일",
        "termStandardDays": "기간 기준일(일)",
        "totalPurchaseCount": "총 구매 건수",
        "longTermCount": "장기 건수",
        "shortTermCount": "단기 건수",
        "longTermRate": "장기 비율(%)",
        "shortTermRate": "단기 비율(%)",
        "longTermRevenue": "장기 매출",
        "shortTermRevenue": "단기 매출"
      },
      "call_api_guide": "[NEXT STEP] Long-term vs short-term membership signup ratio. summary holds the overall long/short ratio (longTermRate, shortTermRate) and revenue. Summarize in text."
    },
    {
      "id": 29,
      "intention_key": "CHECK_MEMBERSHIP+DISCOUNT+RETENTION_STATISTICS",
      "category": "MEMBERSHIP",
      "description": "Retrieve re-registration rate (retention) of discounted members. Analyzes ratio of members who re-registered after expiry among those who received discounts. Used for evaluating discount policy effectiveness.",
      "endpoint": "GET /api2/membership/discount-retention",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "memberShipName": "회원권명",
        "discountPurchaseCount": "할인 구매 건수",
        "discountReRegistrationCount": "할인 재등록 건수",
        "discountReRegistrationRate": "할인 재등록률(%)",
        "averageDiscountAmount": "평균 할인 금액",
        "nonDiscountPurchaseCount": "비할인 구매 건수",
        "nonDiscountReRegistrationCount": "비할인 재등록 건수",
        "nonDiscountReRegistrationRate": "비할인 재등록률(%)",
        "totalPurchaseCount": "총 구매 건수",
        "totalReRegistrationCount": "총 재등록 건수",
        "totalReRegistrationRate": "전체 재등록률(%)",
        "discountTotalAmount": "총 할인 금액",
        "rateDifference": "재등록률 차이(%p)"
      },
      "call_api_guide": "[NEXT STEP] Comparison of re-registration rate between discount-applied vs non-discount members. summary holds the comparison scalars (discountReRegistrationRate, nonDiscountReRegistrationRate, rateDifference). [INTRO RULE] The user asked specifically about 할인 회원 재등록률 — intro/outro headline MUST lead with the comparison numbers ({{SUMMARY:discountReRegistrationRate}}% vs {{SUMMARY:nonDiscountReRegistrationRate}}%). FORBIDDEN: leading with {{SUMMARY:totalReRegistrationRate}} as the headline — that aggregates discount+non-discount and answers a different question. totalReRegistrationRate is optional supplementary context only."
    },
    {
      "id": 30,
      "intention_key": "CHECK_MEMBERSHIP+TOP+RE+REGISTRATION_STATISTICS_BY+PERIOD",
      "category": "MEMBERSHIP",
      "description": "Retrieve top membership products RANKED by re-registration rate within period (TOP intention — pre-sorted ranking with rank column). Use ONLY when the user explicitly asks for ranking/top/highest re-registration memberships — Korean ranking keyword required: '재등록률 순위/랭킹/상위/높은 회원권/재등록 잘 되는 회원권/Top/탑/제일/가장 높은/1위'. Returns rank + memberShipName + reRegistrationRate + reRegistrationCount + purchaseCount per row (NO newCount, NO totalRevenue). For bare per-membership re-registration rate description WITHOUT ranking framing (Korean: '회원권별 재등록률 보여줘/알려줘/확인해줘', '재등록률 통계' — no 순위/상위/높은 등) use CHECK_MEMBERSHIP+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD (BASE intention, id=27) — it returns the richer column set the user typically wants for a rate breakdown.",
      "endpoint": "GET /api2/membership/top-re-registration",
      "service": "zone",
      "creative": false,
      "autoFallback": true,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "default_sort": { "field": "reRegistrationRate", "direction": "desc", "secondary": "reRegistrationCount" },
      "available_fields": {
        "rank": "순위",
        "memberShipName": "회원권명",
        "purchaseCount": "구매 건수",
        "reRegistrationCount": "재등록 건수",
        "reRegistrationRate": "재등록률(%)",
        "startDate": "조회 시작일",
        "endDate": "조회 종료일",
        "totalPurchaseMembers": "총 구매 회원수",
        "newMembers": "신규 회원수",
        "reRegistrationMembers": "재등록 회원수"
      },
      "call_api_guide": "[NEXT STEP] Membership ranking by re-registration rate. Ranking is already pre-computed."
    },
    {
      "id": 31,
      "intention_key": "CHECK_MEMBERSHIP+TOP+ATTENDANCE_STATISTICS_BY+PERIOD",
      "category": "MEMBERSHIP",
      "description": "Retrieve top membership products ranked by attendance rate within period. Analyzes which membership products' subscribers have highest attendance. For overall attendance KPI use SCHEDULE category CHECK_MEMBER+ATTENDANCE+RATE_STATISTICS_BY+PERIOD. Keywords: 출석률 높은 회원권, 출석 좋은 회원권 순위",
      "endpoint": "GET /api2/membership/top-attendance",
      "service": "zone",
      "creative": false,
      "autoFallback": true,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "default_sort": { "field": "attendanceRate", "direction": "desc", "secondary": "attendedCount" },
      "available_fields": {
        "rank": "순위",
        "memberShipName": "회원권명",
        "reservationCount": "예약 건수",
        "attendedCount": "출석 건수",
        "absentCount": "결석 건수",
        "attendanceRate": "예약 출석률(%)",
        "startDate": "조회 시작일",
        "endDate": "조회 종료일",
        "totalMembers": "전체 회원수",
        "attendedMembers": "출석 회원수",
        "noAttendanceMembers": "무출석 회원수",
        "activeRate": "전체 출석률(%)",
        "totalReservations": "총 예약 건수",
        "totalAttendedCount": "출석 건수",
        "totalLateCount": "지각 건수",
        "totalLateRate": "지각률(%)",
        "totalAbsentCount": "결석 건수",
        "avgAttendanceRate": "예약 출석률 평균(%)"
      },
      "call_api_guide": "[NEXT STEP] Membership ranking by attendance rate. Ranking is already pre-computed.\n[METRIC DISAMBIGUATION] Per-row metric is `attendanceRate` (라벨 \"예약 출석률(%)\") — each membership's reserved-to-attended ratio. If user asks for an overall \"전체 출석률\" KPI, use summary.activeRate (\"전체 출석률(%)\") = members who attended out of total members."
    },
    {
      "id": 32,
      "intention_key": "CHECK_SCHEDULE+LESSON_LIST_BY+PERIOD",
      "category": "SCHEDULE",
      "description": "Retrieve per-session schedule entries for a specified period (each row = one scheduled session with classDate/classTime/lessonName/teacherNames/maxBookedPeople). Use ONLY when user explicitly requests scheduled sessions for a period (Korean: 이번 주 수업 일정/오늘 수업 스케줄/N월 수업 시간표/이번주 수업 시간/수업 일정 보여줘). For bare '수업 종류/어떤 수업/수업 목록' WITHOUT a period or schedule qualifier, use CHECK_SCHEDULE+CATALOG_LIST (period-independent class type catalog). For reservation/attendance details use CHECK_SCHEDULE+RESERVATION+STATUS_LIST_BY+PERIOD.",
      "endpoint": "GET /api2/schedule/lesson-list/simple",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "this_week_start", "end": "this_week_end" },
      "available_fields": {
        "lessonName": "수업명",
        "classDate": "수업 날짜",
        "classTime": "수업 시간",
        "maxBookedPeople": "최대 예약 인원",
        "teacherNames": "담당 강사",
        "isCanceled": "취소 여부"
      },
      "call_api_guide": null
    },
    {
      "id": 33,
      "intention_key": "CHECK_SCHEDULE+RESERVATION+STATUS_LIST_BY+PERIOD",
      "category": "SCHEDULE",
      "description": "Retrieve all classes' reservation/attendance overview for a period. Returns per-session individual records (raw slot data). NOT for capacity utilization / 정원 대비 / 이용률 / efficiency queries — use CHECK_RESERVE+ATTENDANCE_STATISTICS(groupBy:lesson) for those (it has pre-computed utilizationRate). NOT for aggregate per-member attendance count (Korean: 총/평균/합계/최대/최소 + 출석 횟수) — use CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD (returns per-member attendedCount). This intention's rows are per-reservation events, lacking per-member attendedCount field.",
      "endpoint": "GET /api2/schedule/lesson-list",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "lessonName": "수업명",
        "classDate": "수업 날짜",
        "classTime": "수업 시간",
        "maxBookedPeople": "최대 예약 인원",
        "reserveCount": "예약 인원수",
        "reserves.name": "회원명",
        "reserves.phone": "전화번호",
        "reserves.statusName": "예약 상태"
      },
      "call_api_guide": "[FIELD SEMANTICS] reserveCount = per-session slot capacity (how many people CAN book that session), NOT per-member attendance count. For aggregate per-class participation count (수업별 참여 횟수), use CHECK_RESERVE+ATTENDANCE_STATISTICS(groupBy:\"lesson\") instead — it provides totalSessions/attendedCount/totalReservations per lesson. Do NOT filter reserveCount as if it were attendance count.\n[FORBIDDEN ROUTE — AGGREGATE ATTENDANCE COUNT] NEVER use this intention to answer aggregate per-member attendance count queries (Korean: 총/평균/합계/최대/최소 + 출석 횟수). The correct intention is CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD which returns per-member rows with attendedCount field."
    },
    {
      "id": 34,
      "intention_key": "CHECK_SCHEDULE+RESERVATION+STATUS_DETAIL_BY+PERIOD",
      "category": "SCHEDULE",
      "description": "Retrieve one specific class's detailed reservation status. Use when class name or time IS specified. Returns individual reservee names and attendance status.",
      "endpoint": "GET /api2/schedule/reserve-status",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": true
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": true
        },
        {
          "name": "classTime",
          "type": "string",
          "format": "HH:mm",
          "required": false
        },
        {
          "name": "lessonName",
          "type": "string",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "lessonName": "수업명",
        "classDate": "수업 날짜",
        "classTime": "수업 시간",
        "attendanceRate": "출석률(%)",
        "totalReserveCount": "총 예약 인원",
        "attendedCount": "출석 인원",
        "reserves.name": "회원명",
        "reserves.phone": "전화번호",
        "reserves.statusName": "예약/출석 상태"
      },
      "call_api_guide": null
    },
    {
      "id": 35,
      "intention_key": "CHECK_SCHEDULE+SETTING_DETAIL_BY+PERIOD",
      "category": "SCHEDULE",
      "description": "Retrieve specific class configuration details. Returns class time, capacity, coach, visibility, booking window settings. For system (store) settings use INFRA category CHECK_SYSTEM+SETTING_DETAIL.",
      "endpoint": "GET /api2/schedule/lesson-detail",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": true
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": true
        },
        {
          "name": "classTime",
          "type": "string",
          "format": "HH:mm",
          "required": false
        },
        {
          "name": "lessonName",
          "type": "string",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "lessonName": "수업명",
        "classDate": "수업 날짜",
        "classTime": "수업 시간",
        "teacherNames": "담당 강사",
        "maxBookedPeople": "최대 예약 인원",
        "type": "수업 유형",
        "size": "수업 규모",
        "time": "수업 시간(분)",
        "isCanceled": "취소 여부",
        "isHide": "숨김 여부"
      },
      "call_api_guide": "[NEXT STEP] Lesson operation settings detail. Summarize the settings info in text."
    },
    {
      "id": 36,
      "intention_key": "CHECK_SCHEDULE+CATALOG_LIST",
      "category": "SCHEDULE",
      "description": "Retrieve all class types (catalog) registered at the center. Returns class names, types, capacity, coaches. PRIMARY USE: bare '수업 종류/수업 종류 알려줘/어떤 수업/무슨 수업/수업 목록/수업 카탈로그/운영 수업/등록된 수업/클래스 종류/레슨 종류' WITHOUT period qualifier — this is the deterministic catalog query (period-independent, each row = one distinct class type). For per-session schedule entries with date/time use CHECK_SCHEDULE+LESSON_LIST_BY+PERIOD. Catalog is also used as existence-check for a specific named lesson (필라테스/요가/HIIT etc.) before drilling into RESERVATION_DETAIL.",
      "endpoint": "GET /api2/lesson/catalog",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "includeDeleted",
          "type": "boolean",
          "required": false,
          "default": false
        }
      ],
      "prior_endpoint": null,
      "available_fields": {
        "lessonName": "수업명",
        "type": "수업 유형",
        "size": "수업 규모",
        "maxBookedPeople": "최대 예약 인원",
        "teacherNames": "담당 강사",
        "isHide": "숨김 여부",
        "isDeleted": "삭제 여부"
      },
      "call_api_guide": "[NEXT STEP] Center lesson catalog (type list). For bare '수업 종류/어떤 수업 있어/수업 목록' (no specific lesson name in input, no period qualifier), MUST call show_display with columns [lessonName, type, maxBookedPeople, teacherNames]. Korean reference intro: \"센터에서 운영 중인 수업 종류예요. 총 {{COUNT}}종류예요\". NEVER finish with text-only when items[] has rows — the catalog list is a tabular response. EXCEPTION: if user named a specific lesson (필라테스/요가/HIIT etc.) and catalog is being used as existence-check only, items.lessonName has no match → finish(\"해당 수업은 등록되어 있지 않아요\") instead of show_display."
    },
    {
      "id": 37,
      "intention_key": "CHECK_MEMBER+ATTENDANCE+RATE_STATISTICS_BY+PERIOD",
      "category": "SCHEDULE",
      "primaryRateKey": "activeRate",
      "description": "Retrieve overall attendance rate KPI. Returns total attendance rate %, no-show rate, active member ratio. Use for '출석률 어때?', '출석률 통계'. Also matches '이탈 위험/이탈 가능성/출석 저조/안나오는 회원' (members at churn risk are identified via low attendance — use this intention, NOT CHURN_RETENTION_RATE). NOT individual member list.",
      "endpoint": "GET /api2/reserve/attendance-rate",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "userId": "링코 ID(linkCoachId)",
        "name": "이름",
        "phone": "전화번호",
        "gender": "성별",
        "attendedCount": "출석 횟수",
        "lateCount": "지각 횟수",
        "absentCount": "결석 횟수",
        "totalCount": "전체 건수",
        "attendanceRate": "예약 출석률(%)",
        "totalMembers": "전체 회원수",
        "activeMembers": "활성 회원수",
        "activeRate": "전체 출석률(%)",
        "avgAttendanceRate": "예약 출석률 평균(%)",
        "avgWeeklyFrequency": "평균 주당 출석 빈도",
        "noAttendanceMembers": "무출석 회원수"
      },
      "call_api_guide": "[NEXT STEP] summary holds overall attendance KPIs.\n[CRITICAL — DEFAULT METRIC] \"출석률\" without \"예약\" qualifier = summary.activeRate (\"전체 출석률(%)\") = ratio of members who attended out of ALL members. ALWAYS use {{SUMMARY:activeRate}} for \"출석률\" KPI. NEVER use avgAttendanceRate as the headline — avgAttendanceRate is \"예약 출석률 평균\" (reserved-to-attended ratio, often 100% when no absences, misleading as headline).\n[THREE METRICS]\n  1. summary.activeRate (\"전체 출석률(%)\") — DEFAULT for \"출석률\" KPI. e.g. 34 means 34% of all members attended.\n  2. summary.avgAttendanceRate (\"예약 출석률 평균(%)\") — only when user explicitly says \"예약 출석률\".\n  3. members[].attendanceRate — per-member filtering/ranking only.\n[DO NOT COMPUTE] Cite the appropriate summary scalar as-is. Do NOT synthesize from totalAttendedCount/totalReservations.\n[KPI FINISH] For KPI intent (Korean: \"출석률 알려줘/보여줘\"), finish with {{SUMMARY:activeRate}} (e.g. Korean: \"이번 달 전체 출석률은 {{SUMMARY:activeRate}}%야\"). show_display only when user requests member list."
    },
    {
      "id": 38,
      "intention_key": "CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD",
      "category": "SCHEDULE",
      "description": "Retrieve per-member attendance list. Returns individual member names, attendance count, last visit date. Use for '안 나오는 회원', '출석 현황 목록', sorting by visit frequency.",
      "endpoint": "GET /api2/reserve/attendance-stats",
      "service": "zone",
      "creative": false,
      "autoFallback": true,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "sortBy",
          "type": "string",
          "required": false
        },
        {
          "name": "sortOrder",
          "type": "string",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "userId": "링코 ID(linkCoachId)",
        "name": "이름",
        "phone": "전화번호",
        "gender": "성별",
        "reservationCount": "예약 횟수",
        "attendedCount": "출석 횟수",
        "absentCount": "결석 횟수",
        "attendanceRate": "출석률(%)",
        "weeklyFrequency": "주당 출석 빈도",
        "lastVisit": "마지막 방문일"
      },
      "call_api_guide": "[RANKING REDIRECT — WRONG INTENTION FOR PLAIN RANKING] If the user asked for plain per-member attendance ranking (Korean: 출석 순위/출석 랭킹/제일 많이 한 회원/출석 많은 회원/상위 회원/높은 순) WITHOUT an attribute pre-filter (성별 등), this is the WRONG intention — do NOT sort+show_display here as a ranking substitute. Re-route to CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD + CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST then show_attendance_ranking (dedicated ranking renderer). This non-MONTHLY variant + sort + show_display is only correct for attribute pre-filter ranking (e.g. 여성 회원 + 출석률 상위) or bare 출석 현황 list — NOT for a plain ranking request.\\n[ATTENDANCE FILTER RULE]\nALWAYS use attendedCount for filtering attendance. NEVER use weeklyFrequency for filtering.\nWhen user says \"주 N회 이하/이상\", convert to total: N × numberOfWeeks, where numberOfWeeks = round((endDate - startDate) / 7).\n[SINGLE CATEGORY — NO CROSS-MERGE] For \"주 N회 + 나온/출석한 + 회원\" pattern, this intention alone is sufficient. DO NOT call ALIVE_LIST/MEMBERSHIP_LIST or merge_slots — single call_api + filter + show_display is the correct path.\n[BARE STATUS REQUEST — MANDATORY DEFAULT COLUMNS] For bare \"출석 현황/출석 목록/출석 명단/회원별 출석/출석 알려줘/출석 보여줘/출석 확인\" (no week-frequency, no zero-attendance qualifier), show_display columns MUST be EXACTLY: [\"name\", \"gender\", \"phone\", \"attendedCount\", \"reservationCount\", \"attendanceRate\"]. This is MANDATORY — DO NOT substitute or omit columns. Omitting attendanceRate / reservationCount / gender returns a degenerate member directory and FAILS the user's intent. weeklyFrequency / lastVisit are SECONDARY — include only if user explicitly mentions frequency/visit pattern.\n[BARE STATUS REQUEST — INTRO/OUTRO PHRASING] response items[] contains ALL members in period (both ATTENDED and NON-ATTENDED — attendedCount=0 rows are members with zero attendance in period). summary fields hold the split: summary.activeMembers (출석한 회원수), summary.totalMembers (전체 회원수), summary.noAttendanceMembers (무출석 회원수). intro/outro MUST cite this split — e.g. Korean: \"이번 달 출석 회원 {{SUMMARY:activeMembers}}명 (전체 {{SUMMARY:totalMembers}}명, 미출석 {{SUMMARY:noAttendanceMembers}}명)이야\". NEVER say only \"총 N명\" with items.length — that conflates attended and non-attended and is misleading. summary scalars are precomputed by backend — cite as-is, DO NOT recompute.\n[REQUIRED COLUMNS for \"주 N회\" queries] show_display MUST include weeklyFrequency column so user can verify the per-week criterion. Default: [name, phone, attendedCount, weeklyFrequency, lastVisit].\n[NO SECONDARY HISTORY_LIST CALL] DO NOT additionally call CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST when this intention is the primary source for show_display — HISTORY_LIST is only paired with the MONTHLY variant for show_attendance_chart/show_attendance_ranking sequences, and would overwrite buffer.items with member-meta rows lacking attendedCount.\n[FILTER BY INTENT]\n- User wants members who ATTENDED (\"나온/출석한/다닌\"):\n  Use range with min:1 to exclude zero-attendance.\n- User wants members who DID NOT attend (\"안 나온/안 한/결석/미출석\"):\n  Use exact:0 for zero attendance.\nweeklyFrequency is for DISPLAY ONLY."
    },
    {
      "id": 41,
      "intention_key": "CHECK_SALES+YEAR_STATISTICS",
      "category": "SALES",
      "description": "Retrieve annual individual transaction list for specified year (annual total = sum of transaction totalPrice; summary.totalMaxPrice is the all-time record-high MONTHLY revenue, NOT the annual total). Returns each transaction: member name, item, amount, date. Use for \"올해 총매출\", \"연간 매출 합계\", \"2025년 매출\", \"연도별 매출 통계\", \"올해 매출 내역\" (Korean: 연도별/연간/올해 총/내역). If user asks for \"월별\" or monthly breakdown, use CHECK_SALES+MONTHLY+TREND instead.",
      "endpoint": "GET /api2/sales/annual-revenue",
      "service": "zone",
      "creative": false,
      "defaultDisplay": "show_revenue_chart",
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "searchYear",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": true
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "year_start", "end": "today" },
      "available_fields": {
        "totalMaxPrice": "역대 최고 월 매출(연간 총매출 아님, 차트 기준선)",
        "name": "회원명",
        "itemName": "상품/회원권명",
        "cardPrice": "카드 결제액",
        "cashPrice": "현금 결제액",
        "discountPrice": "할인액",
        "totalPrice": "결제 금액",
        "approvalDate": "결제일",
        "isRefund": "환불 여부"
      },
      "call_api_guide": "[NEXT STEP] Annual sales statistics. items[] = individual transactions of the year — annual TOTAL revenue = {{SUM:totalPrice}} (refunds are negative rows). summary.totalMaxPrice is the ALL-TIME RECORD-HIGH MONTHLY revenue (chart crown baseline), NOT the annual total — NEVER announce it as 연간/올해 총매출. Default → show_revenue_chart (monthly bar chart). Announce annual total via {{SUM:totalPrice}} in intro/outro. Use show_display only if user explicitly asks for 표/리스트/목록/list/table — columns use flat field names (name, itemName, totalPrice, approvalDate), NOT sales.* prefix."
    },
    {
      "id": 42,
      "intention_key": "CHECK_SALES_STATISTICS_BY+PERIOD",
      "category": "SALES",
      "description": "Retrieve individual sales transaction records for a period. Returns each transaction: member, item, amount, date. Use ONLY when user explicitly requests a transaction list/detail (Korean: 매출 내역/매출 목록/매출 리스트/매출 표/거래 내역/결제 내역). For bare '매출 보여줘' / '이번 달 매출' (no 내역/목록/리스트/list/table qualifier), use CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD (scalar summary). NOT month-over-month comparison.",
      "endpoint": "GET /api2/sales/period-history",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": true
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": true
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "name": "회원명",
        "itemName": "상품/회원권명",
        "totalPrice": "결제 금액",
        "cardPrice": "카드 결제액",
        "cashPrice": "현금 결제액",
        "discountPrice": "할인액",
        "approvalDate": "결제일",
        "isRefund": "환불 여부"
      },
      "call_api_guide": "[NEXT STEP] Per-period sales transaction records (one row per transaction, NOT per member). Default to show_display — per-transaction detail table (name, itemName, totalPrice, approvalDate). Branch: \"캘린더/달력/일별/주간/주별/이번주/지난주 + 매출\" → show_sales_view (pass startDate/endDate); \"추이/차트/그래프/시각화 + 매출\" → show_revenue_chart.\n[NOT A PER-MEMBER SUM SOURCE] Each row = one transaction; the same member may appear multiple times (refunds, multi-purchase). aggregate(field:\"name\") DOES NOT sum totalPrice — it only copies the representative row's totalPrice. Do NOT use this intention for queries that need per-member revenue totals with filter/sort (Korean: 1인당 매출/회원별 매출/회원당 매출 + 평균 이상/이하/상위 N명/하위 N명/이상만/이하만) — that combination has no correct execution path in the current toolchain. For such queries route to CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD with the capability-disclosure response shape (see [PER-MEMBER REVENUE — CAPABILITY DISCLOSURE — STRICT ROUTING] in the keyword routing section). Showing the raw transaction list as if it answered \"회원별 평균 이상\" is FORBIDDEN — it mislabels transaction-level rows as member-level results.\n[PER-PRODUCT COLUMN SELECTION — REVENUE vs COUNT — STRICT INTENT MAP] After aggregate(field:\"itemName\") on CHECK_SALES_STATISTICS_BY+PERIOD, the buffer exposes BOTH _count (transaction frequency per product) AND _sum_totalPrice (groupwise revenue sum per product — plus _sum_cardPrice/_sum_cashPrice/_sum_discountPrice when present). REQUIRED show_display columns by user intent:\n  (a) \"상품별 매출/상품별 매출 통계/상품 매출/상품별 매출 합계\" (Korean: 매출/매출액/매출 통계 + per-product) → columns=[\"itemName\", \"_sum_totalPrice\"]. intro Korean reference: \"상품별 매출 합계예요. 총 {{COUNT}}종류이고, 합계는 {{SUM:_sum_totalPrice}}원이에요\". DO NOT use _count as the primary numeric column here — that surfaces transaction frequency (횟수) but the user asked for 매출 (revenue amount). Naming the table \"매출 내역/매출 통계\" while the column is _count(횟수) creates a deterministic label-mismatch the user immediately catches.\n  (b) \"상품별 판매 건수/상품별 판매 횟수/상품별 거래 건수\" (Korean: 판매 건수/판매 횟수/거래 건수 + per-product) → columns=[\"itemName\", \"_count\"]. intro Korean reference: \"상품별 판매 건수예요. 총 {{COUNT}}종류이고, 거래 합계는 {{SUM:_count}}건이에요\".\n  (c) Ambiguous \"상품별 판매 통계/상품별 통계\" without explicit 매출/건수 axis → DEFAULT to (a) revenue view (columns=[\"itemName\", \"_sum_totalPrice\"]) since \"판매 통계\" typically implies revenue at fitness centers. If user follow-ups with 건수/횟수 narrowing, switch to (b).\n  NEVER mix label terms across the intro/table: if intro says \"매출 내역\" the table column MUST be _sum_totalPrice (label: \"총 금액 합계\"), not _count (label: \"횟수\"). Conversely if column is _count, intro MUST say \"판매 건수/거래 횟수\", NOT \"매출 내역/매출 통계\"."
    },
    {
      "id": 43,
      "intention_key": "CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD",
      "category": "SALES",
      "description": "Retrieve monthly sales trend over multiple months. Returns month-by-month revenue comparison and change rate. Use for \"올해 월별 매출\", \"매출 추이\", \"월별 비교\", \"트렌드\". NOT single-period transaction list.",
      "endpoint": "GET /api2/sales/monthly-trend",
      "service": "zone",
      "creative": false,
      "defaultDisplay": "show_revenue_chart",
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startMonth",
          "type": "string",
          "format": "YYYY-MM",
          "required": false
        },
        {
          "name": "endMonth",
          "type": "string",
          "format": "YYYY-MM",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "month": "월",
        "cardPrice": "카드 결제액",
        "cashPrice": "현금 결제액",
        "totalSales": "총 매출",
        "netSales": "순 매출",
        "refundPrice": "환불액",
        "changeRate": "전월 대비 변동률(%)"
      },
      "call_api_guide": "[NEXT STEP] Monthly sales trend. summary holds total revenue (totalSales) and average monthly sales (averageMonthlySales). Default → show_revenue_chart (monthly bar chart). Announce {{SUMMARY:totalSales}} / {{SUMMARY:averageMonthlySales}} in intro/outro. Use show_display only if user explicitly asks for 표/리스트/목록/list/table. changeRate captures month-over-month movement."
    },
    {
      "id": 44,
      "intention_key": "CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD",
      "category": "SALES",
      "description": "Retrieve average revenue per transaction by period. Analyzes average amount per payment and per member.",
      "endpoint": "GET /api2/sales/average-revenue",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "grossRevenue": {
          "label": "총 매출액",
          "dimension": "currency"
        },
        "totalRefundAmount": {
          "label": "총 환불액",
          "dimension": "currency"
        },
        "netRevenue": {
          "label": "순 매출액",
          "dimension": "currency"
        },
        "customerCount": {
          "label": "결제 고객수",
          "dimension": "people"
        },
        "averageRevenue": {
          "label": "평균 객단가",
          "dimension": "currency"
        },
        "totalPointUsage": {
          "label": "총 포인트 사용액",
          "dimension": "currency"
        },
        "averagePointUsage": {
          "label": "평균 포인트 사용액",
          "dimension": "currency"
        },
        "startDate": {
          "label": "조회 시작일",
          "dimension": "none"
        },
        "endDate": {
          "label": "조회 종료일",
          "dimension": "none"
        }
      },
      "call_api_guide": "[NEXT STEP] Average per-member revenue statistics (no per-member list). Summarize the numbers in text. No need to call show_display."
    },
    {
      "id": 45,
      "intention_key": "CHECK_SALES+PAYMENT+RATIO_STATISTICS_BY+PERIOD",
      "category": "SALES",
      "description": "Retrieve payment method ratio statistics by period (card, cash, bank transfer). Analyzes transaction count and amount ratio per payment method.",
      "endpoint": "GET /api2/sales/payment-method-ratio",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "paymentOptionName": "결제수단",
        "transactionCount": "거래 건수",
        "totalAmount": "거래 금액",
        "amountRate": "금액 비율(%)",
        "startDate": "조회 시작일",
        "endDate": "조회 종료일",
        "totalTransactionCount": "총 거래 건수"
      },
      "call_api_guide": "[NEXT STEP] Per-payment-method ratio statistics. Each row = one payment method (e.g. 카드/현금/링코페이) with its aggregated transactionCount + totalAmount + amountRate.\n[FORMULA DEFINITION — CRITICAL] The 총 거래 건수 (total transaction count) shown to the user MUST equal the visible sum of per-method transactionCount in the table (row-sum), NOT the backend summary.totalTransactionCount. Backend summary.totalTransactionCount can include refunded/canceled transactions and may differ from the row-sum (실제 결제 완료 합계). Citing summary 43 while the table sums to 38 produces a visible contradiction the user immediately catches.\n[INTRO PLACEHOLDER — STRICT] For 총 거래 건수/전체 거래 건수/총 결제 건수: MUST use {{SUM:transactionCount}} — this computes the row-sum of items[].transactionCount, deterministically matching the visible table. NEVER use {{SUMMARY:totalTransactionCount}} (backend metric, can mismatch row-sum) and NEVER use {{COUNT}} (row count = payment method type count, e.g. 3 — contradicts table's transactionCount column). For 총 거래 금액/전체 결제 금액: use {{SUM:totalAmount}} (row-sum of totalAmount), NOT {{SUMMARY:totalAmount}}.\n[OUTRO PER-METHOD CITATION — DETERMINISTIC] When citing each payment method in outro analysis (Korean: \"카드 N건/현금 N건/링코페이 N건\"), MUST cite the EXACT items[i].transactionCount value from the displayed table — NEVER use {{COUNT}} (row count), NEVER invent a number. Same for per-method 금액: cite items[i].totalAmount as displayed. Per-method 비율: cite items[i].amountRate from the table.\n[SINGLE METHOD FILTER — Korean: \"카드 비율/카드만/현금 비율\"] If user asks for one payment method, prefer plain text answer using items[i].amountRate (e.g. \"카드 비율은 56.1%야\") — NO show_display needed for single-value answer. If show_display IS used with filter(paymentOptionName), the intro/outro 거래 건수 MUST cite items[0].transactionCount (the filtered row's actual count, e.g. 20 for 카드) — NEVER \"총 거래 1건\" (1 = row count after filter, not the method's transaction count, produces self-contradiction).\n[ANTI-CONTRADICTION] Forbidden patterns: (a) \"<method> N건\" where N differs from items[i].transactionCount; (b) \"<method 금액>원 중 <total 금액>원 비율\" mixing method amount with grand total; (c) summary totalTransactionCount applied (43 vs row-sum 38 = visible contradiction); (d) \"총 거래 1건\" after single-method filter (collides with row's transactionCount=20). If unsure of a value, OMIT the number; never approximate.\nProvide ratio analysis in text."
    },
    {
      "id": 46,
      "intention_key": "CHECK_PRODUCT+CATALOG_LIST",
      "category": "PRODUCT",
      "description": "Retrieve all product types (catalog) sold at the center. Returns product names, prices, stock quantities, images. Covers physical products (fitness gear, beverages). PRIMARY USE: bare '판매 상품 목록/판매 상품/상품 목록/판매 가능 상품/취급 상품/판매 중인 상품' WITHOUT period qualifier — this is the deterministic catalog query. For memberships use MEMBERSHIP category CHECK_MEMBERSHIP+CATALOG_LIST. Do NOT use CHECK_SALES_STATISTICS_BY+PERIOD as a substitute for catalog — sales transactions are NOT the catalog.",
      "endpoint": "GET /api2/product/ai_catalog",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "productName": "상품명",
        "price": "판매가",
        "inventory": "재고 수량"
      },
      "call_api_guide": "[NEXT STEP] Product catalog (list). MUST call show_display with columns [productName, price, inventory]. Korean reference intro: \"판매 상품 목록이에요. 총 {{COUNT}}개예요\". NEVER finish with text-only when items[] has rows — the catalog list is a tabular response."
    },
    {
      "id": 47,
      "intention_key": "CHECK_PRODUCT+SALES_LIST_BY+PERIOD",
      "category": "PRODUCT",
      "description": "Retrieve PHYSICAL product (merchandise) sales history for specified period. Returns sold product names, quantities, amounts, dates, buyer members. Scope: physical merchandise only (supplements, gear, towels, accessories). For overall store sales including memberships (typical use case for gym/crossfit centers — '상품 판매' often means total store sales rather than physical goods), use CHECK_SALES_STATISTICS_BY+PERIOD or CHECK_SALES_LIST_BY+PERIOD (SALES category) instead.",
      "endpoint": "GET /api2/product/sales-history",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "productName": "상품명",
        "buyerName": "구매자명",
        "phone": "전화번호",
        "count": "수량",
        "cardPrice": "카드 결제액",
        "cashPrice": "현금 결제액",
        "discountPrice": "할인액",
        "approvalDate": "구매일"
      },
      "call_api_guide": null
    },
    {
      "id": 48,
      "intention_key": "CHECK_PRODUCT+SALES_STATISTICS",
      "category": "PRODUCT",
      "description": "Retrieve aggregated sales statistics per PHYSICAL product (merchandise). Returns total sold quantity, revenue, popular product rankings. Scope: physical merchandise only. For overall store sales statistics including memberships, use CHECK_SALES_STATISTICS_BY+PERIOD (SALES category) instead.",
      "endpoint": "GET /api2/product/sales-statistics",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "rank": "순위",
        "productName": "상품명",
        "unitPrice": "단가",
        "totalQuantity": "총 판매 수량",
        "salesCount": "거래 건수",
        "totalRevenue": "총 매출"
      },
      "call_api_guide": "[NEXT STEP] Per-product sales ranking statistics. summary holds totalQuantity + totalRevenue. MUST call show_display with ranking table — NEVER list individual products as text. Use {{COUNT}} for product count, {{SUMMARY:totalRevenue}} for total revenue in intro/outro."
    },
    {
      "id": 49,
      "intention_key": "CHECK_NOTICE_LIST_BY+PERIOD",
      "category": "COMMUNICATION",
      "description": "Retrieve notice/announcement list for specified period. Returns notice titles, dates, view counts, comment counts.",
      "endpoint": "GET /api2/notice/history",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "title": "공지 제목",
        "content": "내용",
        "createdDate": "작성일"
      },
      "call_api_guide": null
    },
    {
      "id": 50,
      "intention_key": "CHECK_NOTICE+TOP+COMMENTED_STATISTICS",
      "category": "COMMUNICATION",
      "description": "Retrieve most popular notices ranked by comment count. Returns notice titles, comment counts, dates in descending comment order.",
      "endpoint": "GET /api2/notice/top-commented",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "limit",
          "type": "integer",
          "required": false,
          "default": 5
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "default_sort": { "field": "totalCommentCount", "direction": "desc", "secondary": "commentCount" },
      "available_fields": {
        "title": "공지 제목",
        "content": "내용",
        "commentCount": "댓글수",
        "replyCount": "대댓글수",
        "totalCommentCount": "총 댓글수",
        "createdDate": "작성일"
      },
      "call_api_guide": "[NEXT STEP] Popular notices ranked by comment count."
    },
    {
      "id": 51,
      "intention_key": "CHECK_MESSAGE_LIST_BY+PERIOD",
      "category": "COMMUNICATION",
      "description": "Retrieve SMS/LMS send history for specified period. Returns send dates, recipients, content, delivery status (success/failure). Default channel when ambiguous — '메시지 보낸 거/메시지 이력/문자' without 카카오/알림톡/비즈 keyword maps HERE (NOT CHECK_BIZ_LIST_BY+PERIOD).",
      "endpoint": "GET /api2/message/send-history",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "sendType": "발송 유형",
        "title": "제목",
        "content": "내용",
        "createdDate": "발송일",
        "successCount": "성공 건수",
        "failedCount": "실패 건수",
        "totalCount": "총 발송 건수",
        "members.name": "수신자",
        "members.phone": "전화번호"
      },
      "call_api_guide": null
    },
    {
      "id": 52,
      "intention_key": "CHECK_BIZ_LIST_BY+PERIOD",
      "category": "COMMUNICATION",
      "description": "Retrieve Kakao Biz notification send history for specified period. Returns send dates, recipients, template names, delivery status.",
      "endpoint": "GET /api2/biz/send-history",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "sendType": "발송 유형",
        "senderName": "발송자",
        "title": "제목",
        "createdDate": "발송일",
        "successCount": "성공 건수",
        "failedCount": "실패 건수",
        "totalCount": "총 발송 건수",
        "members.name": "수신자",
        "members.phone": "전화번호"
      },
      "call_api_guide": null
    },
    {
      "id": 53,
      "intention_key": "CHECK_LOCKER_LIST",
      "category": "INFRA",
      "description": "Retrieve all lockers status overview. Returns total/in-use/available locker counts, assigned members per locker. Facility asset perspective. For specific member's locker use MEMBER_DETAIL CHECK_MEMBER+LOCKER_DETAIL.",
      "endpoint": "GET /api2/locker/summary",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "divisionName": {
          "label": "구분",
          "dimension": "none"
        },
        "lockerName": {
          "label": "락커명",
          "dimension": "none"
        },
        "state": {
          "label": "상태",
          "enums": { "Empty": "비어있음", "Using": "사용 중" }
        },
        "userName": {
          "label": "사용자",
          "dimension": "none"
        },
        "startDate": {
          "label": "시작일",
          "dimension": "none"
        },
        "scheduledEndDate": {
          "label": "종료 예정일",
          "dimension": "none"
        }
      },
      "call_api_guide": "[NEXT STEP] This returns ALL lockers (both empty and in-use). Check user's original request for implied subset:\n- Korean: 사용 중인 락커 → auto-filtered to state=Using. If 0 results, tell user no lockers are currently in use (Korean: 사용 중인 락커가 없어요).\n- Korean: 비어있는/빈 락커 → auto-filtered to state=Empty.\nIf auto-filter already applied (see message), proceed with filtered data. Do NOT present full list as if it matches a subset request.\n\n[INTRO PHRASING] User may have said '락커 상세'/'락커 정보' but you called LIST (since no locker number was specified). In show_display.intro, use overview phrasing — '락커 현황'/'락커 목록' — instead of echoing '상세' verbatim. Echoing '상세' on LIST data misleads the user into thinking they got per-locker drilldown. Example: WRONG '락커 상세 정보 50건이에요' / RIGHT '락커 현황 50건이에요' or '전체 락커 50건 중 사용 중 0건, 비어있음 50건이에요'."
    },
    {
      "id": 54,
      "intention_key": "CHECK_LOCKER_DETAIL",
      "category": "INFRA",
      "description": "Retrieve specific locker's usage history by locker number/name. Use ONLY when locker number/name is explicitly specified in user input (e.g. '22번 락커', 'A-01 락커'). NOT by member name. CRITICAL: If user says '락커 상세'/'락커 정보' WITHOUT a number, use CHECK_LOCKER_LIST instead — '상세' alone is ambiguous and CHECK_LOCKER_LIST is callable as overview. NEVER refuse asking for locker number when LIST overview is available.",
      "endpoint": "GET /api2/locker/history",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "lockerName",
          "type": "string",
          "required": true
        }
      ],
      "prior_endpoint": null,
      "available_fields": {
        "lockerName": "락커",
        "currentState": "현재 상태",
        "totalUsageCount": "사용 횟수",
        "userName": "사용자",
        "phone": "전화번호",
        "startDate": "시작일",
        "endDate": "종료일",
        "scheduledEndDate": "종료 예정일",
        "status": "상태",
        "isExit": "퇴장 여부"
      },
      "call_api_guide": "[LOCKER STATE INTERPRETATION] Frontend normalizes response: items always contain at least 1 row with { lockerName, currentState (한국어: 비어있음/사용중), totalUsageCount }. Past usage history rows extend with userName/phone/startDate/endDate. Empty histories → only the 1-row state entry (NOT a missing locker).\n\n<rule>NEVER say '정보가 없어요' / '확인되지 않아요' / '조회된 내역이 없어요' when items has the lockerName row. Such phrasing implies the locker does not exist, which is a false-negative hallucination.</rule>\n\n<rule>The '사용 횟수' (history count) figure MUST come from the totalUsageCount field, NOT from items.length. items.length=1 with empty histories means '락커 메타 1행 + 사용 이력 0건' (the row IS the locker itself). Never say '사용 내역 1건' when totalUsageCount=0.</rule>\n\n<example type=\"RIGHT\">currentState=비어있음 + totalUsageCount=0 → '22번 락커는 현재 비어있어요. 사용 기록은 없어요.'</example>\n<example type=\"RIGHT\">currentState=사용중 → '22번 락커는 현재 사용중이에요. (사용자명/시작일 등 history 정보 포함)'</example>\n<example type=\"WRONG\">'22번 락커 정보가 없어요' / '22번 락커는 확인되지 않아요' — items 있는데 미존재로 오해 유발</example>\n<example type=\"WRONG\">items=1행 totalUsageCount=0 → '사용 내역은 총 1건이에요' (items.length를 history 건수로 잘못 인용)</example>"
    },
    {
      "id": 55,
      "intention_key": "CHECK_SYSTEM+SETTING_DETAIL",
      "category": "INFRA",
      "description": "Retrieve center system configuration. Returns business hours, policies, center name, contact info. For class settings use SCHEDULE category CHECK_SCHEDULE+SETTING_DETAIL_BY+PERIOD.",
      "endpoint": "GET /api/system/",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {},
      "call_api_guide": "[NEXT STEP] Center system settings. Answer in text only for the setting items the user asked about. No need to call show_display."
    },
    {
      "id": 56,
      "intention_key": "CHECK_PARTNER+GYM_LIST",
      "category": "INFRA",
      "description": "Retrieve partner (affiliated) gym list. Returns partner gym names, locations, contact info.",
      "endpoint": "GET /api2/partner_gym/",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "storeName": "센터명",
        "storeTel": "전화번호",
        "storeAddress": "주소",
        "openingHour": "운영시간",
        "partnerType": "제휴 유형"
      },
      "call_api_guide": "[NEXT STEP] Partner gym list. Data is pre-cleaned: only storeName, storeTel, storeAddress, openingHour, partnerType fields remain. ID fields and image URLs are removed by frontend."
    },
    {
      "id": 57,
      "intention_key": "CHECK_WORKOUT+WOD_LIST_BY+PERIOD",
      "category": "WORKOUT",
      "description": "Retrieve WOD (Workout of the Day) content for specified period. Returns daily workout details, exercise types, sets/reps.",
      "endpoint": "GET /api2/workout/content",
      "service": "exercise",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "this_week_start", "end": "this_week_end" },
      "available_fields": {
        "title": "WOD 제목",
        "lessonName": "수업명",
        "classDate": "날짜",
        "wodContent": "운동 내용"
      },
      "call_api_guide": "[NEXT STEP] WOD (workout-of-the-day) content for the period. The wodContent field contains the full workout program (section titles + exercises). Always include wodContent in show_display columns so the user sees the actual workout details."
    },
    {
      "id": 58,
      "intention_key": "CHECK_WORKOUT+PARTICIPANT_LIST_BY+PERIOD",
      "category": "WORKOUT",
      "description": "Retrieve workout schedule for specified period with attended participants. Each row = one participant (userId, name, profile) for a workout (lessonName, title, classDate, participantCount). Workouts with 0 participants excluded.",
      "endpoint": "GET /api2/workout/participants",
      "service": "exercise",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "this_week_start", "end": "this_week_end" },
      "available_fields": {
        "title": "WOD 제목",
        "lessonName": "수업명",
        "classDate": "날짜",
        "participantCount": "참가 인원",
        "name": "참가자명",
        "userId": "회원 ID",
        "profile": "프로필 이미지"
      },
      "call_api_guide": "[NEXT STEP] Workout participant list."
    },
    {
      "id": 59,
      "intention_key": "CHECK_WORKOUT+PARTNER+GYM_LIST_BY+PERIOD",
      "category": "WORKOUT",
      "description": "Retrieve partner gym list and shared WOD status for specified period. Returns shared WOD counts and participant counts per partner gym.",
      "endpoint": "GET /api2/workout/share-gyms",
      "service": "exercise",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "this_week_start", "end": "this_week_end" },
      "available_fields": {
        "storeName": "체육관명",
        "wodCount": "WOD 수"
      },
      "call_api_guide": "[NEXT STEP] Per-partner-gym WOD sharing status."
    },
    {
      "id": 60,
      "intention_key": "CHECK_WORKOUT+PARTNER+GYM+WOD_LIST",
      "category": "WORKOUT",
      "description": "Retrieve partner gym's WOD content. Returns WOD details and exercise types shared by affiliated gyms.",
      "endpoint": "GET /api2/workout/share-gym-wods",
      "service": "exercise",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "storeName": "소속 지점",
        "title": "WOD 제목",
        "lessonName": "수업명",
        "classDate": "날짜"
      },
      "call_api_guide": "[NEXT STEP] Partner gym shared WOD list. Each row = one WOD from an external partner gym (storeName field identifies the source gym). show_display columns MUST include \"storeName\" (소속 지점) alongside [\"title\",\"lessonName\",\"classDate\"] — user asked about PARTNER gym WOD (제휴점/파트너/공유), so source gym identity is primary disambiguator. Recommended columns=[\"storeName\",\"title\",\"lessonName\",\"classDate\"]."
    },
    {
      "id": 61,
      "intention_key": "CHECK_COACH_LIST",
      "category": "INFRA",
      "description": "Retrieve coach list. Returns coach name, phone, profile",
      "endpoint": "GET /api2/teacher/",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "teacherName": "코치명",
        "phone": "전화번호",
        "profile": "프로필 이미지"
      },
      "call_api_guide": "[NEXT STEP] Center coach (instructor) list."
    },
    {
      "id": 62,
      "intention_key": "CHECK_MEMBER+ALL_LIST",
      "category": "MEMBER",
      "description": "Retrieve ALL members (active + inactive/expired). Includes name, gender, phone, birthDay, isActive flag, expiry date. Called without parameters. Use this for '전체 회원' / '모든 회원' queries — covers active and expired alike, and for aggregations across the full member base (e.g. age average via compute='age', gender ratio via compute='group'). For expired-only members use CHECK_MEMBER+EXPIRED_LIST. For active-only (currently valid membership) use CHECK_MEMBER+ALIVE_LIST. For birthday-person filter (이번 달 생일자 = active members whose birthday falls in current month) use CHECK_MEMBER+ALIVE_LIST + filter(birthDay).",
      "endpoint": "GET /api2/member/list",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": null,
      "prior_endpoint": null,
      "available_fields": {
        "name": "이름",
        "gender": "성별",
        "birthDay": "생년월일",
        "phone": "전화번호",
        "isActive": "유효 여부",
        "viewMembershipName": "대표 회원권명",
        "viewStartDate": "회원권 시작일",
        "viewEndDate": "회원권 만료일",
        "viewCount": "회원권 잔여 이용 횟수",
        "createdDate": "등록일"
      },
      "call_api_guide": null
    },
    {
      "id": 63,
      "intention_key": "CHECK_SALES+LINKOPAY_LIST_BY+PERIOD",
      "category": "SALES",
      "description": "Retrieve LinkoPay (online card payment) transaction history, split into regular vs subscription payments. Summarizes total revenue, refunds, net revenue, and per-channel (regular/subscription) revenue + count for the period. Returns per-transaction detail: payment type, member name, item name, amount, refund flag. For overall revenue use CHECK_SALES_STATISTICS_BY+PERIOD; for payment method ratio use CHECK_SALES+PAYMENT+RATIO_STATISTICS_BY+PERIOD.",
      "endpoint": "GET /api2/sales/linkopay-history",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "available_fields": {
        "paymentType": "결제 유형",
        "memberName": "회원명",
        "itemName": "상품/회원권명",
        "price": "결제 금액",
        "approvalDate": "승인일시",
        "isRefund": "환불 여부"
      },
      "call_api_guide": "[NEXT STEP] LinkoPay payment records. If total revenue aggregation is needed, use compute(operation:\"sum\", field:\"price\").\n[REFUND DATA] isRefund is a boolean field (true=refunded, false=normal). To filter refunded items: filter(field:\"isRefund\", condition:{type:\"exact\", value:true}). For total refund amount, use {{SUMMARY:totalRefundAmount}} (backend-provided scalar). For refund count, use {{SUMMARY:refundCount}}. If filter yields 0 items (no refunds exist), tell the user there are no refund records and report the total refund amount as {{SUMMARY:totalRefundAmount}}. Do NOT re-call the API or merge_slots to answer refund questions — the current buffer already has all the data."
    },
    {
      "id": 64,
      "intention_key": "CHECK_MEMBER+ATTENDANCE_DETAIL",
      "category": "MEMBER_DETAIL",
      "description": "Retrieve one specific member's daily attendance/absence records. Returns per-record: date, lesson, attendance flag, reserved membership name. For class-level aggregation use CHECK_MEMBER+LESSON+PARTICIPATION_DETAIL.",
      "endpoint": "GET /api2/member/{memberId}/attendance-daily",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "memberId",
          "type": "long",
          "required": true
        },
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": "CHECK_MEMBER_SEARCH",
      "available_fields": {
        "classDate": "수업 날짜",
        "classTime": "수업 시간",
        "lessonName": "수업명",
        "status": "출석 상태",
        "membershipName": "예약 회원권명"
      },
      "call_api_guide": "[NEXT STEP] Member's per-date attendance records. Show via show_display. status: attended=출석, absent=결석. For per-lesson aggregation/statistics, use CHECK_MEMBER+LESSON+PARTICIPATION_DETAIL."
    },
    {
      "id": 67,
      "intention_key": "CHECK_MEMBERSHIP+TOP+SALES_STATISTICS_BY+PERIOD",
      "category": "MEMBERSHIP",
      "description": "Retrieve membership-product sales ranking statistics for a period. Returns per-membership: sales count, total revenue, average price, sales share. Sorted by sales count descending; zero-sale memberships excluded. Default match for '인기 회원권/회원권 판매 순위/회원권별 매출/많이 팔린 회원권/가장 많이 팔린 회원권' (인기 default = 판매 ranking, NOT re-registration NOT attendance). For re-registration ranking use CHECK_MEMBERSHIP+TOP+RE+REGISTRATION_STATISTICS_BY+PERIOD; for attendance ranking use CHECK_MEMBERSHIP+TOP+ATTENDANCE_STATISTICS_BY+PERIOD; for overall sales trend use CHECK_SALES+MONTHLY+TREND_STATISTICS_BY+PERIOD.",
      "endpoint": "GET /api2/membership/top-sales",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "default_sort": { "field": "salesCount", "direction": "desc", "secondary": "salesRevenue" },
      "available_fields": {
        "rank": "순위",
        "memberShipName": "회원권명",
        "salesCount": "판매 건수",
        "salesRevenue": "총 매출",
        "averagePrice": "평균 결제가",
        "salesRate": "판매 점유율(%)"
      },
      "call_api_guide": "[NEXT STEP] Membership ranking by sales count. summary holds total sales count (totalSalesCount) and total revenue (totalSalesRevenue). rank=1 is the best-selling membership. For \"얼마야\" questions, answer with averagePrice (average paid price)."
    },
    {
      "id": 68,
      "intention_key": "CHECK_RESERVE+ATTENDANCE_STATISTICS",
      "category": "SCHEDULE",
      "description": "Retrieve reservation/attendance statistics grouped by lesson/coach/day-of-week/time-slot. USE THIS for 정원 대비/이용률/capacity utilization/수업 효율 queries (groupBy=lesson provides utilizationRate %). Korean keyword → groupBy mapping: '수업별/수업별 수강률/수업별 출석/정원 대비/이용률/수업 효율/비효율 수업' → groupBy=lesson, '코치별/강사별/수업 많이 한 코치' → groupBy=coach (omit coachName for all-coach comparison, set coachName for single-coach detail), '요일별/요일별 수강률/무슨 요일 출석 많아' → groupBy=day, '시간대별/시간별/오전·오후·저녁 참여율/몇 시 출석' → groupBy=time. Defaults to groupBy=day when no grouping keyword is given. For overall attendance rate KPI use CHECK_MEMBER+ATTENDANCE+RATE_STATISTICS_BY+PERIOD; for per-member attendance list use CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD.",
      "endpoint": "GET /api2/reserve/attendance-statistics",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "groupBy",
          "type": "string",
          "enum": [
            "lesson",
            "coach",
            "day",
            "time"
          ],
          "required": false,
          "default": "day"
        },
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "coachName",
          "type": "string",
          "required": false
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-30d", "end": "today" },
      "tie_break_field": "attendedCount",
      "available_fields": {
        "_groupBy=lesson": {
          "lessonName": {
            "label": "수업명",
            "dimension": "none"
          },
          "teacherNames": {
            "label": "담당 코치명",
            "dimension": "none"
          },
          "totalSessions": {
            "label": "총 수업 횟수",
            "dimension": "session"
          },
          "totalReservations": {
            "label": "총 예약 건수",
            "dimension": "transaction"
          },
          "attendedCount": {
            "label": "출석 건수",
            "dimension": "session"
          },
          "lateCount": {
            "label": "지각 건수",
            "dimension": "session"
          },
          "absentCount": {
            "label": "결석 건수",
            "dimension": "session"
          },
          "attendanceRate": {
            "label": "예약 출석률(%)",
            "dimension": "rate"
          },
          "avgReservationsPerSession": {
            "label": "회당 평균 예약 인원",
            "dimension": "people"
          },
          "avgAttendedPerSession": {
            "label": "회당 평균 출석 인원",
            "dimension": "people"
          },
          "defaultMaxBookedPeople": {
            "label": "수업 기본 정원",
            "dimension": "people"
          },
          "totalCapacity": {
            "label": "총 정원(회차별 effective 합)",
            "dimension": "people"
          },
          "utilizationRate": {
            "label": "정원 대비 출석률(%)",
            "dimension": "rate"
          },
          "timeSlotCount": {
            "label": "시간대 수",
            "dimension": "none"
          },
          "classTime": {
            "label": "수업 시간대",
            "dimension": "none"
          }
        },
        "_groupBy=coach": {
          "teacherId": {
            "label": "코치 ID",
            "dimension": "none"
          },
          "coachName": {
            "label": "코치명",
            "dimension": "none"
          },
          "coachProfile": {
            "label": "프로필 이미지",
            "dimension": "none"
          },
          "phone": {
            "label": "전화번호",
            "dimension": "none"
          },
          "totalLessons": {
            "label": "담당 수업 종류 수",
            "dimension": "none"
          },
          "totalSessions": {
            "label": "총 수업 횟수",
            "dimension": "session"
          },
          "attendedSessions": {
            "label": "출석 확인 수업 수",
            "dimension": "session"
          },
          "totalReservations": {
            "label": "총 예약 건수",
            "dimension": "transaction"
          },
          "totalAttendedCount": {
            "label": "출석 건수",
            "dimension": "session"
          },
          "totalLateCount": {
            "label": "지각 건수",
            "dimension": "session"
          },
          "totalAbsentCount": {
            "label": "결석 건수",
            "dimension": "session"
          },
          "attendedReservationRatio": {
            "label": "예약 출석률(%)",
            "dimension": "rate"
          },
          "avgAttendedPerSession": {
            "label": "회당 평균 출석",
            "dimension": "people"
          }
        },
        "_groupBy=day": {
          "dayOfWeek": {
            "label": "요일(MON~SUN)",
            "dimension": "none"
          },
          "totalSessions": {
            "label": "총 수업 횟수",
            "dimension": "session"
          },
          "totalReservations": {
            "label": "총 예약 건수",
            "dimension": "transaction"
          },
          "attendedCount": {
            "label": "출석 건수",
            "dimension": "session"
          },
          "lateCount": {
            "label": "지각 건수",
            "dimension": "session"
          },
          "absentCount": {
            "label": "결석 건수",
            "dimension": "session"
          },
          "attendanceRate": {
            "label": "예약 출석률(%)",
            "dimension": "rate"
          },
          "avgReservationsPerSession": {
            "label": "회당 평균 예약 인원",
            "dimension": "people"
          },
          "avgAttendedPerSession": {
            "label": "회당 평균 출석 인원",
            "dimension": "people"
          }
        },
        "_groupBy=time": {
          "classTime": {
            "label": "수업 시간대",
            "dimension": "none"
          },
          "totalSessions": {
            "label": "총 수업 횟수",
            "dimension": "session"
          },
          "totalReservations": {
            "label": "총 예약 건수",
            "dimension": "transaction"
          },
          "attendedCount": {
            "label": "출석 건수",
            "dimension": "session"
          },
          "lateCount": {
            "label": "지각 건수",
            "dimension": "session"
          },
          "absentCount": {
            "label": "결석 건수",
            "dimension": "session"
          },
          "attendanceRate": {
            "label": "예약 출석률(%)",
            "dimension": "rate"
          },
          "avgReservationsPerSession": {
            "label": "회당 평균 예약 인원",
            "dimension": "people"
          },
          "avgAttendedPerSession": {
            "label": "회당 평균 출석 인원",
            "dimension": "people"
          }
        }
      },
      "call_api_guide": "[REQUIRED PARAM — DO NOT OMIT] groupBy: \"coach\" | \"lesson\" | \"day\" | \"time\". Always set explicitly based on user's question; omitting causes incorrect grouping.\n[KEYWORD → groupBy] \"코치별/강사별/코치 성과/누가 가장 많이/어떤 코치/코치 인기/인기 코치\" → \"coach\". \"수업별/레슨별/클래스별/어떤 수업/인기 수업\" → \"lesson\". \"요일별/일별/무슨 요일\" → \"day\". \"시간대별/시간별/오전·오후·저녁 참여율/몇 시 출석/어떤 시간대/인기 시간대\" → \"time\".\n[POPULARITY/RANKING DIMENSION RULE — CRITICAL]\n  • Sentence-INITIAL axis word governs groupBy when followed by ranking/popularity intent. Pattern: \"어떤 X (수식어) (이/가) 가장 인기/많이/높아/낮아\" → groupBy = X's axis. The token after \"어떤\" is the primary dimension.\n  • \"어떤 코치 ~ 가장 인기/코치별 인기/인기 코치/누구 코치/코치 누가\" → groupBy=\"coach\" (NOT lesson). Even when surface tokens include \"수업\" after 코치 (e.g., \"어떤 코치 수업이 가장 인기\"), the primary axis is 코치 — output rows must be 코치 dimension (coachName/teacherNames per row).\n  • \"어떤 시간대가 가장 인기/시간대별 참여/인기 시간대\" → groupBy=\"time\". Output rows must be 시간대 dimension (classTime per row).\n  • \"어떤 수업이 가장 인기/수업별 인기/인기 수업\" → groupBy=\"lesson\". Output rows are 수업 dimension.\n  • Compound BOTH-direction (\"코치와 해당 수업/N명 코치의 인기 수업\" — explicit conjunction \"와/의\") → see [COMPOUND COACH+LESSON ROUTING] in global guide → groupBy=lesson (rows carry both lessonName + teacherNames). \"어떤 코치 수업\" WITHOUT \"와/의\" is NOT compound; it is 코치 dimension query.\n[SELF-CHECK BEFORE call_api] (1) Does the user's question mention a grouping axis (코치/수업/요일/시간대)? (2) If yes, set groupBy accordingly. (3) If no axis is mentioned, default to \"day\". Never call call_api without groupBy.\n\n[SORT FIELD INTENT MAPPING — MANDATORY when user implies ranking/superlative]\n  When user input contains ranking/superlative keywords (가장/최다/제일/높은/낮은/많은/적은/인기/상위/하위/TOP), you MUST call sort tool after call_api with field+direction matching user's metric. NEVER omit sort for ranking queries — buffer's raw row order is NOT guaranteed.\n  Keyword → sort field + direction (groupBy=lesson/time/day; groupBy=coach uses *coach variants):\n  • \"참여 인원 가장 많은/참여자 많은/예약 많은/수강 많은/인원 많은\" → field=\"totalReservations\" direction=\"desc\" (coach: \"totalReservations\")\n  • \"참여 인원 가장 적은/참여 적은/예약 적은/인원 적은\" → field=\"totalReservations\" direction=\"asc\"\n  • \"출석 많은/출석 높은/출석 가장 많이/출석 1위\" → field=\"attendedCount\" direction=\"desc\" (coach: \"totalAttendedCount\")\n  • \"출석 낮은/출석 적은/출석 안 나오는/출석 가장 적은\" → field=\"attendedCount\" direction=\"asc\" (coach: \"totalAttendedCount\")\n  • \"참여율 높은/출석률 높은/예약 출석률 1위\" → field=\"attendanceRate\" direction=\"desc\" (coach: \"attendedReservationRatio\")\n  • \"참여율 낮은/출석률 낮은\" → field=\"attendanceRate\" direction=\"asc\"\n  • \"인기 있는/인기 수업/인기 코치/인기 시간대 (alone, no metric qualifier)\" → field=\"totalReservations\" direction=\"desc\" (popularity defaults to 참여 인원). NOT utilizationRate.\n  • \"수업 효율/이용률/정원 대비 출석/없애야 될 수업/폐강\" → field=\"utilizationRate\" direction=\"asc\" (low-efficiency) or \"desc\" (high-efficiency).\n  • \"회당 평균 출석 많은/회당 평균 높은\" → field=\"avgAttendedPerSession\" direction=\"desc\"\n  • \"수업 횟수 많은/세션 많은\" → field=\"totalSessions\" direction=\"desc\"\n  ANTI-PATTERN: Q \"참여 인원 많은\" + sort(\"utilizationRate\") = WRONG. Q \"출석 낮은\" + sort(\"utilizationRate\", asc) = WRONG — that ranks by capacity ratio, not by attendance count. Pick the field that matches the user's noun, not your default metric.\n\n[METRIC SELECTION — CRITICAL]\n  • Row `attendanceRate` (라벨 \"예약 출석률(%)\") = per-group ratio of (attended / reserved). Per-row only. **DO NOT** aggregate (산술평균 등) across rows and report as \"전체 출석률 X%\".\n  • Row `attendedReservationRatio` (groupBy=coach only, 라벨 \"예약 출석률(%)\") = per-coach ratio. Same per-row rule applies.\n  • Row `utilizationRate` (라벨 \"정원 대비 출석률(%)\") = per-group ratio of (attended / capacity). Use for \"없애야 될 수업\", \"수업 효율\", \"정원 대비 X\" intents.\n  • **Root scalar `attendedReservationRatio` (라벨 \"예약 출석률(%)\") = corpus-level weighted average (가중평균, 판정완료 기준).** This is the ONLY field that can be cited as \"전체 예약 출석률\" / \"기간 예약 출석률\" KPI. Use when user asks for corpus aggregate WITHOUT a per-group axis.\n  • For \"전체 출석률\" with mental model = 전체 회원 중 출석한 회원 비율 — this intention does NOT have it. Switch to CHECK_MEMBER+ATTENDANCE+RATE_STATISTICS_BY+PERIOD and use summary.activeRate (라벨 \"전체 출석률(%)\").\n\n[GRANULARITY 분기 — DECISION TREE]\n  1. User input mentions group axis (수업별/코치별/요일별/시간대별)? → row attendanceRate (or attendedReservationRatio when groupBy=coach). Per-row citation only.\n  2. User input = corpus KPI without group axis (e.g., \"이번 달 예약 출석률\", \"기간 예약 출석률\")? → root scalar attendedReservationRatio. Cite as-is (corpus weighted avg).\n  3. User input = \"전체 출석률\" with 전체 회원 mental model? → wrong intention. Re-route to CHECK_MEMBER+ATTENDANCE+RATE summary.activeRate.\n\n[INTRO/OUTRO NUMERIC RULE] Do NOT write \"전체 출석률은 X%\" by aggregating per-row attendanceRate. For corpus expression, ONLY cite root scalar attendedReservationRatio. For per-row expression, include row context (수업명/코치명/요일/시간대).\n\n[COHORT REDIRECT] If the user uses member-lifecycle keywords (\"꾸준히/라이프사이클/가입 후 N개월/이탈 시점/신규 회원 출석 변화\"), use MEMBER category's CHECK_MEMBER+ATTENDANCE+TENURE+TREND_STATISTICS_BY+PERIOD instead of this intention."
    },
    {
      "id": 69,
      "intention_key": "CHECK_MEMBER+ATTENDANCE+TENURE+TREND_STATISTICS_BY+PERIOD",
      "category": "MEMBER",
      "description": "Retrieve average attendance rate trend by months-since-signup, using each member's first membership start date as signup. Use ONLY for cohort / member-lifecycle aggregate questions like \"회원들이 평균적으로 몇 개월 꾸준히 나오다가 출석률 떨어져?\". Cohort keyword triggers: '꾸준히/라이프사이클/가입 후 N개월/이탈 시점/신규 회원 출석 변화/가입 후 몇 개월부터' — these go HERE, NOT SCHEDULE attendance intentions. NOT for individual member lists — rows are tenureMonth aggregates (no name/phone), so merge_slots with member lists will FAIL. For per-member attendance data use CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD instead. Members without any membership purchase are auto-excluded (no signup date). Summary includes peakRetentionMonth (sustained attendance window), dropOffStartMonthRelative (first month dropping >15%p below store average) / dropOffStartMonthAbsolute (first month below 70%). Time axis is \"months since signup\", which differs from SCHEDULE category attendance intentions (calendar time axis).",
      "endpoint": "GET /api2/member/attendance-tenure-trend",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        {
          "name": "startDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false,
          "default": "6개월 전"
        },
        {
          "name": "endDate",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false,
          "default": "오늘"
        },
        {
          "name": "cohortJoinFrom",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "cohortJoinTo",
          "type": "string",
          "format": "YYYY-MM-DD",
          "required": false
        },
        {
          "name": "maxMonths",
          "type": "number",
          "required": false,
          "default": 12,
          "min": 1,
          "max": 24
        }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "-180d", "end": "today" },
      "available_fields": {
        "tenureMonth": "가입 후 경과 월수",
        "tenureLabel": "구분(가입 첫 달/N개월 차)",
        "avgAttendanceRate": "평균 출석률(%)",
        "memberCount": "대상 회원 수",
        "totalAttended": "총 출석 횟수",
        "totalReserved": "총 예약 횟수",
        "startDate": "조회 시작일",
        "endDate": "조회 종료일",
        "cohortJoinFrom": "Cohort 가입일 시작",
        "cohortJoinTo": "Cohort 가입일 종료",
        "maxMonths": "분석 최대 경과월수"
      },
      "call_api_guide": "[NEXT STEP] Attendance trend by months-since-signup. From summary, announce peakRetentionMonth (the most sustained attendance window) and the drop-off onset in PLAIN everyday Korean — NEVER expose internal jargon (FORBIDDEN: \"절대 70% 기준\", \"매장 평균 -15%p\", \"relative/absolute\"). Plain mapping: dropOffStartMonthAbsolute → \"N개월 차부터 출석률이 70% 아래로 떨어져\"; dropOffStartMonthRelative → \"N개월 차부터 매장 평균보다 눈에 띄게 낮아져\". If both point to the SAME month, state it ONCE (\"N개월 차부터 출석률이 떨어지기 시작해\") — no redundant dual basis. If they differ, mention both with ONLY those plain phrasings. Show per-month detail via show_display. Use items.tenureLabel (\"가입 첫 달\", \"N개월 차\") as the table label.\n[DROPOFF FACT-CHECK — MANDATORY] Before mentioning a drop-off month, verify the value in summary:\n  - dropOffStartMonthRelative === null (or key absent) → say \"출석률이 매장 평균보다 크게 낮아지는 구간은 없었어요\" (plain, no jargon) — DO NOT invent a month.\n  - dropOffStartMonthAbsolute === null (or key absent) → say \"출석률이 70% 아래로 떨어지는 구간은 없었어요\" (plain, no jargon) — DO NOT invent a month.\n  - dropOffStartMonthRelative or dropOffStartMonthAbsolute === peakRetentionMonth → IMPOSSIBLE pattern, treat as null (drop-off must occur AFTER peak).\n  - If the value is a number > peakRetentionMonth → cite that month explicitly (\"X개월 차부터 떨어지기 시작해요\").\n[FORBIDDEN] Citing the peak month as drop-off month (e.g., \"8개월 차에 꾸준한데 8개월 차부터 떨어져요\") — self-contradiction. Citing 0개월 / 0달 when peak is later — self-contradiction. Citing only threshold percentage without a month when key is non-null — incomplete."
    },
    {
      "id": 66,
      "intention_key": "CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST",
      "category": "MEMBER",
      "description": "전체 회원 목록 + 회원권 이용 이력 (Korean: 회원권 이력/회원권 히스토리/회원권 변경 내역). 회원별 usages 배열(시작일/종료일)을 포함하여 산정기간 overlap 계산, 이탈/유효회원 판정, 연령대 분류 등에 활용. No params required — returns ALL members. Do NOT ask for member name. show_member_status (신규/재등록/이탈 분류) / show_attendance_chart (유효회원 산정) / show_attendance_ranking (회원 메타 보강) / show_engagement_chart (연령대 분류) / show_ltv (회원 모수) visualization 도구의 buffer source. [BUFFER-SOURCE INTENTION] visualizationOnly:true — items[]의 usages 필드는 nested array라 표 셀 렌더링 불가. show_display 호출 시 visualization-only guard로 차단됨.",
      "endpoint": "GET /api2/member/all/statistics",
      "service": "zone",
      "creative": false,
      "autoFallback": true,
      "forceParams": true,
      "visualizationOnly": true,
      "fallback_intention": "CHECK_MEMBER+ALIVE_LIST",
      "next_flow": "FLOW2",
      "params": [],
      "prior_endpoint": null,
      "available_fields": {
        "memberId": "회원 PK",
        "name": "이름",
        "gender": "성별",
        "birthDay": "생년월일",
        "phone": "전화번호",
        "countryCode": "국가코드",
        "usages": "회원권 이용 이력 배열 ([{startDate, endDate}])"
      },
      "call_api_guide": "[NEXT STEP] 회원별 전체 회원권 이력입니다. usages 배열로 산정기간 overlap 계산, 이탈/유효회원 판정, 연령별 분석에 사용. 본 intention은 visualizationOnly buffer source이므로 show_display로 직접 표시 불가 (visualization-only guard 차단). 다음 두 분기 중 사용자 의도에 맞게 선택:\n[A] 시각화 분석 의도 (특정 지표/추이/분류): show_member_status(신규/재등록/이탈 분류) / show_engagement_chart(연령대 × 회원권 분석) / show_attendance_chart(출석률 추이) / show_attendance_ranking(출석 순위) / show_ltv(고객 생애 가치) 중 사용자 질의에 맞는 도구 호출.\n[B] 회원별 회원권 정보를 표로 보고 싶은 일반 요청 (Korean: '회원권 이력', '회원권 히스토리', '회원권 변경 내역' bare list): 본 intention 대신 CHECK_MEMBER+ALIVE_LIST(유효 회원) 또는 CHECK_MEMBER+EXPIRED_LIST(만료 회원) 호출 — viewMembershipName/viewStartDate/viewEndDate가 flat field로 제공되어 show_display 표 셀에 정상 노출됨. 사용자 의도 명시 없으면 ALIVE_LIST 기본."
    },
    {
      "id": 67,
      "intention_key": "CHECK_RESERVE+CLASS+SLOT_LIST_BY+PERIOD",
      "category": "SCHEDULE",
      "description": "기간 내 시간대별 수업 예약 건수. classTime/lessonName/teacherNames와 reserveCount 반환. 인기 수업·시간대 분석에 사용. show_class_attendance visualization 도구의 buffer source.",
      "endpoint": "POST /api2/reserve/check/frequency",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        { "name": "startDate", "type": "string", "format": "YYYY-MM-DDTHH:mm:ss", "required": true },
        { "name": "endDate", "type": "string", "format": "YYYY-MM-DDTHH:mm:ss", "required": true }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "month-start", "end": "month-end" },
      "available_fields": {
        "classTime": "수업 시간 (HH:MM-HH:MM)",
        "lessonName": "수업명",
        "teacherNames": "강사명",
        "color": "테마 색상",
        "reserveCount": "예약 건수"
      },
      "call_api_guide": "[NEXT STEP] 시간대/수업별 예약 건수입니다 (show_class_attendance 전용 buffer source). REQUIRED: 반드시 show_class_attendance({ intro }) 호출 — 시간대 뱃지/강사명/수강 인원 렌더링 + reserveCount 내림차순 정렬을 도구가 내부에서 자동 처리하므로 별도 sort 불필요. show_display + sort 경로는 사용 금지 — 전용 렌더러를 우회해 일반 표로 격하되어 '수업별 수강 인원' 시각화 의도를 충족하지 못함. 예외: 사용자가 입력에서 명시적으로 '표/리스트/목록'을 요청한 경우에만 show_display 허용(soft default)."
    },
    {
      "id": 68,
      "intention_key": "CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD",
      "category": "SCHEDULE",
      "description": "기간 내 회원별 일자 단위 출석 기록. 회원별 reserveAttendances 배열(classDate)을 포함하여 일별 출석률, 회원 랭킹, 출석 빈도 분석에 사용. CHECK_MEMBER+ATTENDANCE+RATE_STATISTICS_BY+PERIOD와 응답 구조가 다름 — 본 인텐션은 회원별 reserveAttendances 배열을 제공하여 일별/월별 집계에 더 유연. show_attendance_chart / show_attendance_ranking visualization 도구의 buffer source.",
      "endpoint": "POST /api2/sales/monthly/attendance",
      "service": "zone",
      "creative": false,
      "autoFallback": true,
      "visualizationOnly": true,
      "next_flow": "FLOW2",
      "params": [
        { "name": "startDate", "type": "string", "format": "YYYY-MM-DDTHH:mm:ss", "required": true },
        { "name": "endDate", "type": "string", "format": "YYYY-MM-DDTHH:mm:ss", "required": true }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "month-start", "end": "month-end" },
      "available_fields": {
        "memberId": "회원 PK",
        "name": "이름",
        "gender": "성별",
        "birthDay": "생년월일",
        "phone": "전화번호",
        "countryCode": "국가코드",
        "endDate": "회원권 만료일",
        "reserveAttendances": "출석 기록 배열 ([{classDate}])"
      },
      "call_api_guide": "[NEXT STEP] 회원별 일자 단위 출석 기록입니다. 시각화 도구 호출 전 call_api(CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST)도 함께 호출 필수 (회원 메타 보강 + 0회 출석 회원 포함). 그 후 일별 출석률 추이(show_attendance_chart) 또는 회원별 출석 랭킹(show_attendance_ranking)으로 시각화 권장.\n[METRIC DEFINITION — STRICT] items[] 필드는 [memberId, name, gender, birthDay, phone, countryCode, endDate, reserveAttendances]만 존재. attendanceRate / attendedCount / lastVisit / reservationCount 필드는 items[]에 없음 (sort/filter target 불가, undefined 처리됨). 회원별 attendCount 는 show_attendance_ranking가 reserveAttendances 배열 기준으로 산출. \"출석률 상위/높은/랭킹\" 의도면 sort(attendanceRate) + show_display 금지 — show_attendance_ranking 호출.\n[GENDER PRE-FILTER] 성별 등 회원 메타로 사전 filter 필요 시 옵션: (a) show_attendance_ranking 후 결과를 후처리, 또는 (b) 비-MONTHLY 변형(CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD)을 사용 — 후자는 per-row attendedCount/attendanceRate 필드를 제공해 filter+sort+show_display 가능. show_display 직접 호출 시 reserveAttendances 배열은 raw 노출되니 의도된 시각화 도구를 사용하는 것을 권장."
    },
    {
      "id": 69,
      "intention_key": "CHECK_SALES_LIST_BY+YEAR",
      "category": "SALES",
      "description": "Retrieve individual sales transaction list for specified year. Returns each transaction row: member name, item, amount, date. Use for \"올해 매출 내역\", \"연도별 매출 목록\", \"올해 결제 내역\", \"연간 매출 리스트\" (Korean: 내역/목록/리스트/결제 내역). standardYear param for year-scope query. Also used as show_ltv buffer source. If user asks for aggregate TOTAL only (Korean: 총매출/합계/통계/얼마), use CHECK_SALES+YEAR_STATISTICS instead.",
      "endpoint": "POST /api2/sales/revenue/year",
      "service": "zone",
      "creative": false,
      "autoFallback": true,
      "forceParams": true,
      "next_flow": "FLOW2",
      "params": [
        { "name": "standardYear", "type": "string", "format": "YYYY-01-01THH:mm:ss", "required": true }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "year-start", "end": "year-end" },
      "available_fields": {
        "name": "회원명",
        "itemName": "상품/회원권명",
        "totalPrice": "결제 금액",
        "cardPrice": "카드 결제액",
        "cashPrice": "현금 결제액",
        "discountPrice": "할인액",
        "approvalDate": "결제일",
        "isRefund": "환불 여부"
      },
      "call_api_guide": "[NEXT STEP] Yearly sales transaction list. show_display with transaction details (columns: name, itemName, totalPrice, approvalDate). Also usable as show_ltv / show_revenue_chart buffer source."
    },
    {
      "id": 70,
      "intention_key": "CHECK_MEMBERSHIP+DURATION_LIST_BY+PERIOD",
      "category": "MEMBERSHIP",
      "description": "기간 내 회원별 회원권 지속 기간(개월). memberId/duration 배열 반환. LTV 산정용 평균 지속 기간 계산에 사용 (duration<1은 1로 보정). show_ltv visualization 도구의 buffer source.",
      "endpoint": "POST /api2/membership/duration/avg",
      "service": "zone",
      "creative": false,
      "autoFallback": true,
      "forceParams": true,
      "visualizationOnly": true,
      "next_flow": "FLOW2",
      "params": [
        { "name": "startDate", "type": "string", "format": "YYYY-MM-DDTHH:mm:ss", "required": true },
        { "name": "endDate", "type": "string", "format": "YYYY-MM-DDTHH:mm:ss", "required": true }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "year-start", "end": "today" },
      "available_fields": {
        "memberId": "회원 PK",
        "duration": "회원권 지속 기간(개월)"
      },
      "call_api_guide": "[NEXT STEP] 회원별 평균 회원권 지속 기간입니다. LTV 산정용 durationAvg 계산에 사용 (duration<1은 1로 보정). show_ltv visualization 도구의 입력."
    },
    {
      "id": 71,
      "intention_key": "CHECK_MEMBER+ENGAGEMENT_LIST_BY+PERIOD",
      "category": "MEMBER",
      "description": "기간 내 회원별 이용권/예약 활동 기록. userId/birthDay/membershipId/membershipName/reserveCount 반환. 연령대 × 회원권 분석에 사용 (birthDay로 연령대 분류 + membershipId 집계 + uniqueMemberCount 산정). show_engagement_chart visualization 도구의 buffer source.",
      "endpoint": "POST /api2/member/engagement",
      "service": "zone",
      "creative": false,
      "autoFallback": true,
      "visualizationOnly": true,
      "next_flow": "FLOW2",
      "params": [
        { "name": "startDate", "type": "string", "format": "YYYY-MM-DDTHH:mm:ss", "required": true },
        { "name": "endDate", "type": "string", "format": "YYYY-MM-DDTHH:mm:ss", "required": true }
      ],
      "prior_endpoint": null,
      "defaultPeriod": { "start": "month-start", "end": "month-end" },
      "available_fields": {
        "userId": "사용자 ID",
        "birthDay": "생년월일",
        "membershipId": "회원권 PK",
        "membershipName": "회원권명",
        "reserveCount": "예약 횟수"
      },
      "call_api_guide": "[NEXT STEP] 회원별 이용권/예약 활동 기록입니다. 연령대 × 회원권 분석(show_engagement_chart)에 사용. birthDay로 연령대 분류 + membershipId 집계 + uniqueMemberCount 산정."
    },
    {
      // DB id70 미러 (2026-07-06 rid204 백엔드 완료분 정합) — 라이브 DB와 verbatim 동기. 수정 시 DB 동시 반영 필수.
      "id": 72,
      "intention_key": "CHECK_MEMBERSHIP+PURCHASE_LIST_BY+PERIOD",
      "category": "MEMBERSHIP",
      "description": "Retrieve membership purchase/usage history rows for ALL members within a period. One row per membership registration (a member with 3 memberships = 3 rows; dedupe by member on the frontend, count unique members by memberId). Returns member name, phone, membership name, registration/payment date, membership start/end date, price (points excluded, free trial = 0), and first-membership flag. Period filter is on the membership start date; refunded (is_deleted) rows are excluded. For a single-member history use CHECK_MEMBER+MEMBERSHIP_DETAIL; for current-membership-only lists use CHECK_MEMBER+ALIVE_LIST; for per-payment records use CHECK_SALES_STATISTICS_BY+PERIOD; for product sales ranking use CHECK_MEMBERSHIP+TOP+SALES_STATISTICS_BY+PERIOD; for membership types (catalog) use CHECK_MEMBERSHIP+CATALOG_LIST. Keywords: 이용권 쓴 사람, 체험권 사용자 명단, 회원권 구매 이력, 이용권 산 사람, 추가 구매, 재구매, 체험권 전환, 정규권 구매자",
      "endpoint": "GET /api2/membership/purchase-history",
      "service": "zone",
      "creative": false,
      "next_flow": "FLOW2",
      "params": [
        { "name": "startDate", "type": "string", "format": "YYYY-MM-DD", "required": false },
        { "name": "endDate", "type": "string", "format": "YYYY-MM-DD", "required": false },
        { "name": "membershipName", "type": "string", "required": false }
      ],
      "prior_endpoint": null,
      "available_fields": {
        "name": "이름",
        "phone": "전화번호",
        "memberShipName": "회원권명",
        "registrationDate": "등록일(결제일)",
        "startDate": "회원권 시작일",
        "endDate": "회원권 만료일",
        "price": "결제 금액",
        "isFirstMembership": "첫 회원권 여부"
      },
      "call_api_guide": "[NEXT STEP] 회원권 구매/사용 이력 목록입니다(회원권 1건=1행, 동일 회원 중복 가능). (a) 특정 회원권 사용자 명단 -> filter(memberShipName 정확히 일치) 후 show_display. (b) 전환/추가구매 추적(예: 체험권 쓴 사람 중 정규권 구매) -> 두 슬롯에 서로 다른 필터로 적재 후 merge_slots(mode:intersect, matchKey:userId). 이때 정규권 슬롯은 체험 기간이 아니라 그 이후까지 넓은 기간으로 조회할 것(전환은 체험 이후 발생). (c) 인원수 질문(\"몇 명\")은 같은 회원 중복 행에 유의하여 고유 회원 수(memberId 기준)로 답할 것."
    }
  ]
};
